import {
  HOST_MCP_TASK_CONTEXT_CAPABILITY,
  AGENT_EGRESS_FRESH_SESSION_CAPABILITY,
  InputPreparationModelSchema,
  InputPreparationBindingSchema,
  InputPreparationOfferBindingSchema,
  AGENT_INPUT_PREPARATION_CAPABILITY,
  AGENT_EGRESS_POLICY_CAPABILITY,
  AGENT_EGRESS_RELIABLE_ACK_CAPABILITY,
  AGENT_MESSAGE_EGRESS_CAPABILITY,
  AgentEgressPolicySchema,
  AgentMessagePublishPayloadSchema,
  AgentMessageDispositionPayloadSchema,
  AgentMessageServerContextSchema,
  AgentRefSchema,
  DispatchSelectionSchema,
  ProviderProfileBindingSchema,
  RuntimeIdSchema,
  TERMINAL_PROJECTION_SELECTION_CAPABILITY,
  type AgentEgressPolicy
} from "@byok-sdk/protocol";
import { z } from "zod";

export const LocaleSchema = z.enum(["zh-CN", "zh-TW", "en"]);
export const DefaultSaleskoLocale = "en" as const;

export const TenantIdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[a-z0-9][a-z0-9_-]*$/);

export const SaleskoOAuthProviders = ["google", "apple", "linkedin"] as const;
export const SaleskoSupabaseOAuthProviders = ["google", "apple", "linkedin_oidc"] as const;
export const SaleskoSupabaseOAuthProviderByProvider = {
  google: "google",
  apple: "apple",
  linkedin: "linkedin_oidc"
} as const satisfies Record<(typeof SaleskoOAuthProviders)[number], (typeof SaleskoSupabaseOAuthProviders)[number]>;

export const SaleskoOAuthProviderSchema = z.enum(SaleskoOAuthProviders);
export const SaleskoSupabaseOAuthProviderSchema = z.enum(SaleskoSupabaseOAuthProviders);
export const SaleskoSessionGrantTokenPrefix = "salesko-session-v1" as const;
export const SaleskoSessionGrantAudience = "salesko-api-session" as const;
export const SaleskoSessionMcpConsentScope = "mcp:consent" as const;
export const SaleskoSessionGraphProposeScope = "graph:propose" as const;
export const SaleskoTenantRoleSchema = z.enum(["viewer", "editor", "admin"]);
export const DefaultSaleskoTenantRole = "admin" as const;
export const ProviderAuthGrantTokenPrefix = "salesko-provider-v1" as const;
export const ProviderAuthGrantAudience = "salesko-agent-provider" as const;
export const ProviderAuthGrantScope = "agent:provider" as const;
export const GraphFrameShareTokenPrefix = "salesko-share-v1" as const;
export const GraphFrameShareAudience = "salesko-graph-frame-share" as const;
export const SaleskoSessionProviderGrantScope = ProviderAuthGrantScope;
export const SaleskoSessionGrantScopeSchema = z.enum([SaleskoSessionMcpConsentScope, SaleskoSessionProviderGrantScope, SaleskoSessionGraphProposeScope]);
export const McpGraphReadScope = "graph:read" as const;
export const McpGraphWriteScope = "graph:write" as const;
export const McpCalendarReadScope = "calendar:read" as const;
export const McpConsentGrantTokenPrefix = "salesko-mcp-consent-v1" as const;
export const McpConsentGrantAudience = "salesko-mcp-consent" as const;
// Query param the MCP worker's `/authorize` reads an inbound consent token
// from, and that the web `/mcp-consent` portal appends before redirecting
// back. Shared here so the MCP worker and web app never drift on the wire
// name.
export const McpConsentQueryParam = "salesko_consent" as const;
export const McpConsentGrantScopeSchema = z.enum([McpGraphReadScope, McpGraphWriteScope, McpCalendarReadScope, ProviderAuthGrantScope]);

// --- Pricing tier (shared by billing entitlement, Generic Agent grants, Private Agent access) ---
export const SaleskoBillingTierSchema = z.enum(["free", "pro_byok", "business_managed"]);
// How Deep Research is paid for: `byok` runs on the owner's own computer with
// their own runtime sign-in, `metered` debits SalesKo credits, `none` has no
// Deep Research access at all.
export const SaleskoPrivateAgentAccessModeSchema = z.enum(["none", "byok", "metered"]);

export const SaleskoSessionGrantSchema = z.object({
  iss: z.string().min(1).default("salesko"),
  aud: z.literal(SaleskoSessionGrantAudience),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  name: z.string().min(1).max(128).optional(),
  role: SaleskoTenantRoleSchema.default(DefaultSaleskoTenantRole),
  scopes: z.array(SaleskoSessionGrantScopeSchema).min(1),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export const SaleskoSessionGrantIssueRequestSchema = z.object({
  userId: z.string().min(1).max(128),
  name: z.string().min(1).max(128).optional(),
  role: SaleskoTenantRoleSchema.default(DefaultSaleskoTenantRole),
  scopes: z.array(SaleskoSessionGrantScopeSchema).min(1).default([SaleskoSessionMcpConsentScope]),
  ttlSeconds: z.number().int().min(60).max(86_400).default(3600)
});

export const SaleskoSessionGrantIssueResponseSchema = z.object({
  tokenType: z.literal("Bearer"),
  token: z.string().min(1),
  expiresAt: z.string().datetime(),
  grant: SaleskoSessionGrantSchema
});

const MobileAuthBase64UrlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/);

export const MobileAuthProviderSchema = z.enum(["google", "linkedin"]);

export const MobileAuthExchangeRequestSchema = z.object({
  code: MobileAuthBase64UrlSchema.min(32).max(128),
  codeVerifier: z.string().min(43).max(128).regex(/^[A-Za-z0-9._~-]+$/),
  state: MobileAuthBase64UrlSchema.min(32).max(128)
});

export const MobileAuthRefreshRequestSchema = z.object({
  refreshToken: MobileAuthBase64UrlSchema.min(32).max(128)
});

export const MobileAuthRevokeRequestSchema = MobileAuthRefreshRequestSchema;

export const MobileAuthProfileSchema = z.object({
  email: z.string().email().max(320),
  name: z.string().min(1).max(128),
  userId: z.string().min(1).max(128)
});

export const MobileAuthSessionResponseSchema = z.object({
  profile: MobileAuthProfileSchema,
  refreshToken: MobileAuthBase64UrlSchema.min(32).max(128),
  refreshTokenExpiresAt: z.string().datetime(),
  session: SaleskoSessionGrantIssueResponseSchema
});

export const ProviderAuthGrantSchema = z.object({
  iss: z.string().min(1).default("salesko"),
  aud: z.literal(ProviderAuthGrantAudience),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128).optional(),
  role: SaleskoTenantRoleSchema.default(DefaultSaleskoTenantRole),
  // Optional so already-issued grants (max TTL 3600s) don't fail validation
  // during rollout; absent tier is treated as "free" by consumers.
  tier: SaleskoBillingTierSchema.optional(),
  scopes: z.array(z.string().min(1)).refine((scopes) => scopes.includes(ProviderAuthGrantScope), {
    message: `Provider auth grant must include ${ProviderAuthGrantScope}.`
  }),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export const ProviderAuthGrantIssueRequestSchema = z.object({
  userId: z.string().min(1).max(128).optional(),
  ttlSeconds: z.number().int().min(60).max(3600).default(300)
});

export const ProviderAuthGrantIssueResponseSchema = z.object({
  tokenType: z.literal("Bearer"),
  token: z.string().min(1),
  expiresAt: z.string().datetime(),
  grant: ProviderAuthGrantSchema
});

export const McpConsentGrantSchema = z.object({
  iss: z.string().min(1).default("salesko"),
  aud: z.literal(McpConsentGrantAudience),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  name: z.string().min(1).max(128).optional(),
  role: SaleskoTenantRoleSchema.default(DefaultSaleskoTenantRole),
  scopes: z.array(McpConsentGrantScopeSchema).min(1),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export const McpConsentGrantIssueRequestSchema = z.object({
  userId: z.string().min(1).max(128).optional(),
  name: z.string().min(1).max(128).optional(),
  scopes: z.array(McpConsentGrantScopeSchema).min(1).default([McpGraphReadScope]),
  ttlSeconds: z.number().int().min(60).max(3600).default(600)
});

export const McpConsentGrantIssueResponseSchema = z.object({
  tokenType: z.literal("Bearer"),
  token: z.string().min(1),
  expiresAt: z.string().datetime(),
  grant: McpConsentGrantSchema
});

export const GraphFrameShareGrantSchema = z.object({
  iss: z.string().min(1).default("salesko-api"),
  aud: z.literal(GraphFrameShareAudience),
  tenantId: TenantIdSchema,
  frameRecordId: z.string().min(1),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export const GraphFrameShareLinkIssueRequestSchema = z.object({
  ttlSeconds: z.number().int().min(60).max(2_592_000).default(604_800)
});

export const GraphFrameShareLinkIssueResponseSchema = z.object({
  tokenType: z.literal("Bearer"),
  token: z.string().min(1),
  expiresAt: z.string().datetime(),
  shareUrl: z.string().url(),
  grant: GraphFrameShareGrantSchema
});

export const GraphEntityTypeSchema = z.enum([
  "person",
  "organisation",
  "event",
  "opportunity",
  "note"
]);

export type GraphEntityType = z.infer<typeof GraphEntityTypeSchema>;

export function canonicalEntityIdForNode(entityType: GraphEntityType, label: string): string {
  const normalizedLabel = normalizeCanonicalEntityLabel(label);
  const slug = normalizedLabel
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
  return `canonical:${entityType}:${slug || `u-${stableLabelHash(normalizedLabel)}`}`;
}

/**
 * Resolve which node in a frame is the ego-center ("you"), given the tenant's
 * authoritative self canonical entity id. Matching reuses the same deterministic
 * canonical-id function the rest of the system keys entities by: the stored id is
 * the authority, this is not a label heuristic. Returns undefined when nothing is
 * designated or no node in this frame matches — fail-closed, so the renderer
 * simply highlights no self rather than guessing one.
 */
export function resolveSelfNodeId(
  frame: GraphVisualizationFrame,
  selfCanonicalEntityId: string | null | undefined
): string | undefined {
  if (!selfCanonicalEntityId) {
    return undefined;
  }
  for (const node of frame.graph.nodes) {
    if (canonicalEntityIdForNode(node.type, node.label) === selfCanonicalEntityId) {
      return node.id;
    }
  }
  return undefined;
}

// Authoritative self designation write: the canonical entity id of the node that is
// "you", or null to clear. Persisted per authenticated user; resolved to ui.selfNodeId
// per frame via resolveSelfNodeId.
export const SaleskoSelfNodeUpdateRequestSchema = z.object({
  selfCanonicalEntityId: z.string().trim().min(1).max(160).nullable()
});
export type SaleskoSelfNodeUpdateRequest = z.infer<typeof SaleskoSelfNodeUpdateRequestSchema>;

export function normalizeCanonicalEntityLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

function stableLabelHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

export const GraphRelationTypeSchema = z.enum([
  "met_at",
  "works_at",
  "attended",
  "introduced_by",
  "opportunity_at",
  "mentioned",
  "related_to"
]);

export const GraphPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number()
});

export const GraphVisualSchema = z.object({
  position: GraphPositionSchema,
  color: z.string().min(1),
  size: z.number().positive(),
  emphasis: z.enum(["normal", "focused", "review"]).default("normal")
});

export const GraphPersonAttributeKeys = [
  "role",
  "company",
  "gender",
  "birthday",
  "interests",
  "email",
  "phone",
  "location",
  "source",
  "stage"
] as const;

export const GraphPersonGenderValues = ["male", "female", "other", "undisclosed"] as const;

export const GraphPersonAttributesSchema = z
  .object({
    role: z.string().min(1).max(300).optional(),
    company: z.string().min(1).max(300).optional(),
    gender: z.enum(GraphPersonGenderValues).optional(),
    birthday: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    interests: z.string().min(1).max(1_000).optional(),
    email: z.string().min(1).max(320).optional(),
    phone: z.string().min(1).max(100).optional(),
    location: z.string().min(1).max(500).optional(),
    source: z.string().min(1).max(500).optional(),
    stage: z.string().min(1).max(500).optional()
  })
  .strict();

export type GraphPersonAttributes = z.infer<typeof GraphPersonAttributesSchema>;

/**
 * Strictly projects the product-owned person attribute vocabulary from the
 * extensible graph data record. Unknown provenance/runtime keys stay outside
 * the projection; malformed known keys fail closed instead of being coerced.
 */
export function projectGraphPersonAttributes(data: Record<string, unknown>): GraphPersonAttributes {
  const projected = Object.fromEntries(
    GraphPersonAttributeKeys.flatMap((key) =>
      Object.prototype.hasOwnProperty.call(data, key) ? [[key, data[key]]] : []
    )
  );
  return GraphPersonAttributesSchema.parse(projected);
}

export const GraphNodeSchema = z.object({
  id: z.string().min(1),
  type: GraphEntityTypeSchema,
  label: z.string().min(1),
  summary: z.string().optional(),
  confidence: z.number().min(0).max(1),
  evidence: z.string().min(1).optional(),
  // Event-only time anchor for the timeline projection. Set ONLY when the value is an
  // ISO-like, sortable timestamp; timeline derivation reads it only on `type === "event"`
  // nodes and never infers time from arbitrary `data`. Relative/fuzzy phrases like
  // "last week" belong in `data.timeText` (display-only, not sortable).
  occurredAt: z.string().optional(),
  visual: GraphVisualSchema,
  data: z.record(z.string(), z.unknown()).default({})
});

export const GraphEdgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  type: GraphRelationTypeSchema,
  label: z.string().min(1),
  confidence: z.number().min(0).max(1),
  evidence: z.string().min(1).optional(),
  strength: z.number().min(0).max(1).default(0.5),
  data: z.record(z.string(), z.unknown()).default({})
});

export const GraphCanonicalRecordIdSchema = z.string().min(1).max(160);
export const GraphDatasetScopeSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/);

export const EvidenceSpanSourceKindSchema = z.enum([
  "note_document",
  "frame_authority",
  "change_set",
  "import",
  "manual"
]);

export const EvidenceSpanSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    noteDocumentId: GraphCanonicalRecordIdSchema.optional(),
    sourceKind: EvidenceSpanSourceKindSchema,
    sourceId: z.string().min(1).max(300),
    quote: z.string().min(1).max(4_000).optional(),
    startOffset: z.number().int().nonnegative().optional(),
    endOffset: z.number().int().nonnegative().optional(),
    confidence: z.number().min(0).max(1).default(1),
    capturedAt: z.string().datetime(),
    data: z.record(z.string(), z.unknown()).default({})
  })
  .refine(
    (span) =>
      span.startOffset === undefined ||
      span.endOffset === undefined ||
      span.endOffset >= span.startOffset,
    {
      message: "Evidence span endOffset must be greater than or equal to startOffset."
    }
  );

export const CanonicalEntitySchema = z.object({
  id: GraphCanonicalRecordIdSchema,
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  entityType: GraphEntityTypeSchema,
  label: z.string().min(1).max(300),
  normalizedLabel: z.string().min(1).max(300),
  summary: z.string().max(4_000).nullable().optional(),
  confidence: z.number().min(0).max(1).default(1),
  firstSeenAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  data: z.record(z.string(), z.unknown()).default({})
});

export const EntityAliasSchema = z.object({
  id: GraphCanonicalRecordIdSchema,
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  entityId: GraphCanonicalRecordIdSchema,
  alias: z.string().min(1).max(300),
  normalizedAlias: z.string().min(1).max(300),
  confidence: z.number().min(0).max(1).default(1),
  evidenceSpanId: GraphCanonicalRecordIdSchema.optional(),
  firstSeenAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  data: z.record(z.string(), z.unknown()).default({})
});

export const RelationshipEdgeStatusSchema = z.enum(["active", "deleted"]);

export const RelationshipEdgeSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    sourceEntityId: GraphCanonicalRecordIdSchema,
    targetEntityId: GraphCanonicalRecordIdSchema,
    relationType: GraphRelationTypeSchema,
    label: z.string().min(1).max(300),
    confidence: z.number().min(0).max(1).default(1),
    strength: z.number().min(0).max(1).default(0.5),
    status: RelationshipEdgeStatusSchema.default("active"),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    evidenceSpanIds: z.array(GraphCanonicalRecordIdSchema).default([]),
    data: z.record(z.string(), z.unknown()).default({})
  })
  .refine((edge) => edge.sourceEntityId !== edge.targetEntityId, {
    message: "RelationshipEdge sourceEntityId and targetEntityId must differ."
  });

export const GraphTimelineItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  timestamp: z.string().optional(),
  nodeIds: z.array(z.string().min(1)).default([]),
  summary: z.string().optional()
});

export const GraphCameraSchema = z.object({
  // `sphere` remains parseable only for already-persisted v1 frames. New
  // producers emit `flat`; no product renderer branches on the legacy value.
  mode: z.enum(["flat", "focused", "timeline", "sphere"]),
  focusNodeId: z.string().min(1).optional(),
  position: GraphPositionSchema,
  orbit: z
    .object({
      radius: z.number().positive(),
      autoRotate: z.boolean()
    })
    .default({
      radius: 9,
      autoRotate: false
    })
});

export const GraphFilterSchema = z.object({
  entityType: GraphEntityTypeSchema,
  enabled: z.boolean(),
  color: z.string().min(1),
  count: z.number().int().nonnegative()
});

export const GraphStatsSchema = z.object({
  people: z.number().int().nonnegative(),
  events: z.number().int().nonnegative(),
  organisations: z.number().int().nonnegative(),
  opportunities: z.number().int().nonnegative()
});

export const GraphDetailPanelSchema = z.object({
  selectedNodeId: z.string().min(1).optional(),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  tags: z.array(z.string()).default([]),
  about: z.string().optional(),
  linkedNodeIds: z.array(z.string().min(1)).default([])
});

export const GraphActiveViewSchema = z.enum(["overview", "graph", "people", "companies", "events", "lists", "signals"]);

export const GraphVisualizationUiSchema = z.object({
  productName: z.string().min(1),
  activeView: GraphActiveViewSchema,
  searchPlaceholder: z.string().min(1),
  filters: z.array(GraphFilterSchema),
  relationshipStrength: z.number().min(0).max(1),
  // Ego-center: id of the node that represents the authenticated user ("you").
  // The renderer pins and hero-styles this node so it reads as the origin of the
  // relationship graph. Identity is authoritative here: the UI never infers self
  // from a label heuristic or a "user" entity type. Absent = no self declared.
  selfNodeId: z.string().min(1).optional(),
  detailPanel: GraphDetailPanelSchema,
  stats: GraphStatsSchema
});

export const UpsertNodeCommandSchema = z.object({
  type: z.literal("upsert_node"),
  node: GraphNodeSchema
});

export const UpsertEdgeCommandSchema = z.object({
  type: z.literal("upsert_edge"),
  edge: GraphEdgeSchema
});

export const DeleteEdgeCommandSchema = z.object({
  type: z.literal("delete_edge"),
  edgeId: z.string().min(1)
});

export const SetFrameViewCommandSchema = z
  .object({
    type: z.literal("set_frame_view"),
    camera: GraphCameraSchema.optional(),
    selectedNodeId: z.string().min(1).nullable().optional(),
    activeView: GraphActiveViewSchema.optional()
  })
  .refine(
    (command) => command.camera !== undefined || command.selectedNodeId !== undefined || command.activeView !== undefined,
    {
      message: "set_frame_view requires camera, selectedNodeId, or activeView."
    }
  );

export const GraphMutationCommandSchema = z.discriminatedUnion("type", [
  UpsertNodeCommandSchema,
  UpsertEdgeCommandSchema,
  DeleteEdgeCommandSchema,
  SetFrameViewCommandSchema
]);

export const GraphChangeSetOpSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("upsert_entity"),
    entity: CanonicalEntitySchema
  }),
  z.object({
    type: z.literal("upsert_entity_alias"),
    alias: EntityAliasSchema
  }),
  z.object({
    type: z.literal("upsert_relationship_edge"),
    edge: RelationshipEdgeSchema
  }),
  z.object({
    type: z.literal("attach_evidence"),
    targetType: z.enum(["entity", "edge", "insight"]),
    targetId: GraphCanonicalRecordIdSchema,
    evidenceSpanId: GraphCanonicalRecordIdSchema,
    confidence: z.number().min(0).max(1).default(1)
  }),
  z.object({
    type: z.literal("delete_entity"),
    entityId: GraphCanonicalRecordIdSchema,
    reason: z.string().min(1).max(500).optional()
  }),
  z.object({
    type: z.literal("delete_relationship_edge"),
    edgeId: GraphCanonicalRecordIdSchema,
    reason: z.string().min(1).max(500).optional()
  })
]);

export const InsightTypeSchema = z.enum([
  "opportunity",
  "warm_intro",
  "follow_up",
  "relationship_risk",
  "custom"
]);

export const InsightStatusSchema = z.enum(["candidate", "active", "dismissed", "archived"]);

export const InsightSchema = z.object({
  id: GraphCanonicalRecordIdSchema,
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  insightType: InsightTypeSchema,
  title: z.string().min(1).max(300),
  summary: z.string().min(1).max(4_000),
  score: z.number().min(0).max(1),
  status: InsightStatusSchema.default("candidate"),
  entityIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  edgeIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  evidenceSpanIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  generatedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  data: z.record(z.string(), z.unknown()).default({})
});

export const FocusNodeCommandSchema = z.object({
  type: z.literal("focus_node"),
  nodeId: z.string().min(1),
  reason: z.string().optional()
});

export const SetCameraCommandSchema = z.object({
  type: z.literal("set_camera"),
  camera: GraphCameraSchema
});

export const AgentGraphCommandSchema = z.discriminatedUnion("type", [
  UpsertNodeCommandSchema,
  UpsertEdgeCommandSchema,
  FocusNodeCommandSchema,
  SetCameraCommandSchema,
  z.object({
    type: z.literal("request_review"),
    reason: z.string().min(1),
    nodeIds: z.array(z.string().min(1)).default([])
  })
]);

export const AgentGraphRequestSchema = z.object({
  threadId: z.string().min(1).optional(),
  message: z.string().min(1),
  locale: LocaleSchema.default(DefaultSaleskoLocale),
  mode: z.enum(["visualize", "data-input"]).default("visualize")
});

export const GraphVisualizationFrameSchema = z.object({
  schemaVersion: z.literal("graph.visualization.v1"),
  frameId: z.string().min(1),
  threadId: z.string().min(1).optional(),
  graph: z.object({
    nodes: z.array(GraphNodeSchema),
    edges: z.array(GraphEdgeSchema)
  }),
  timeline: z.array(GraphTimelineItemSchema).default([]),
  camera: GraphCameraSchema,
  ui: GraphVisualizationUiSchema,
  commands: z.array(AgentGraphCommandSchema).default([]),
  facets: z.object({
    entityTypes: z.array(GraphEntityTypeSchema).default([]),
    relationTypes: z.array(GraphRelationTypeSchema).default([]),
    warnings: z.array(z.string()).default([])
  }),
  summary: z.string(),
  needsReview: z.boolean(),
  source: z.enum(["deterministic-rule", "model-generated", "mastra-tool"])
});

// --- Transaction + Activity Feed dormant foundation ---
//
// These contracts are deliberately additive. Every existing graph.visualization.v1
// schema above and every current write/import/share/model wrapper continues to use
// the v1 roots. graph.visualization.v2 is an opt-in server-output contract; model
// extraction uses the separate strict proposal contract below and cannot mint any
// of the server-owned identity, fingerprint, or source-fact fields.

export const TransactionKindSchema = z.enum(["contract", "order", "invoice", "payment"]);
export const TransactionStatusSchema = z.enum(["committed", "settled", "cancelled"]);

// ISO 4217 List One entries with numeric minor units, published by SIX on
// 2026-01-01. Entries whose CcyMnrUnts is N.A. are excluded because amountMinor
// requires an authoritative currency minor unit. Keep this finite enum
// fail-closed; a syntactically plausible code is not currency authority.
// Source: https://www.six-group.com/dam/download/financial-information/data-center/iso-currrency/lists/list-one.xml
export const TransactionCurrencyCodes = [
  "AED",
  "AFN",
  "ALL",
  "AMD",
  "AOA",
  "ARS",
  "AUD",
  "AWG",
  "AZN",
  "BAM",
  "BBD",
  "BDT",
  "BHD",
  "BIF",
  "BMD",
  "BND",
  "BOB",
  "BOV",
  "BRL",
  "BSD",
  "BTN",
  "BWP",
  "BYN",
  "BZD",
  "CAD",
  "CDF",
  "CHE",
  "CHF",
  "CHW",
  "CLF",
  "CLP",
  "CNY",
  "COP",
  "COU",
  "CRC",
  "CUP",
  "CVE",
  "CZK",
  "DJF",
  "DKK",
  "DOP",
  "DZD",
  "EGP",
  "ERN",
  "ETB",
  "EUR",
  "FJD",
  "FKP",
  "GBP",
  "GEL",
  "GHS",
  "GIP",
  "GMD",
  "GNF",
  "GTQ",
  "GYD",
  "HKD",
  "HNL",
  "HTG",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "IQD",
  "IRR",
  "ISK",
  "JMD",
  "JOD",
  "JPY",
  "KES",
  "KGS",
  "KHR",
  "KMF",
  "KPW",
  "KRW",
  "KWD",
  "KYD",
  "KZT",
  "LAK",
  "LBP",
  "LKR",
  "LRD",
  "LSL",
  "LYD",
  "MAD",
  "MDL",
  "MGA",
  "MKD",
  "MMK",
  "MNT",
  "MOP",
  "MRU",
  "MUR",
  "MVR",
  "MWK",
  "MXN",
  "MXV",
  "MYR",
  "MZN",
  "NAD",
  "NGN",
  "NIO",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PAB",
  "PEN",
  "PGK",
  "PHP",
  "PKR",
  "PLN",
  "PYG",
  "QAR",
  "RON",
  "RSD",
  "RUB",
  "RWF",
  "SAR",
  "SBD",
  "SCR",
  "SDG",
  "SEK",
  "SGD",
  "SHP",
  "SLE",
  "SOS",
  "SRD",
  "SSP",
  "STN",
  "SVC",
  "SYP",
  "SZL",
  "THB",
  "TJS",
  "TMT",
  "TND",
  "TOP",
  "TRY",
  "TTD",
  "TWD",
  "TZS",
  "UAH",
  "UGX",
  "USD",
  "USN",
  "UYI",
  "UYU",
  "UYW",
  "UZS",
  "VED",
  "VES",
  "VND",
  "VUV",
  "WST",
  "XAD",
  "XAF",
  "XCD",
  "XCG",
  "XOF",
  "XPF",
  "YER",
  "ZAR",
  "ZMW",
  "ZWG"
] as const;
export const TransactionCurrencyCodeSchema = z.enum(TransactionCurrencyCodes);

export const SaleskoOpaqueSha256Schema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
export const TransactionSemanticFingerprintSchema = SaleskoOpaqueSha256Schema;
export const TransactionSourceFactKeySchema = SaleskoOpaqueSha256Schema;

export const GraphEntityTypeV2Schema = z.enum([
  "person",
  "organisation",
  "event",
  "opportunity",
  "note",
  "transaction"
]);

export const GraphRelationTypeV2Schema = z.enum([
  "met_at",
  "works_at",
  "attended",
  "introduced_by",
  "opportunity_at",
  "mentioned",
  "related_to",
  "party_to",
  "evidence_for",
  "converted_to"
]);

export const GraphServerSourceFactMetadataSchema = z
  .object({
    sourceFactKey: TransactionSourceFactKeySchema,
    sourceFrameRecordIds: z
      .array(GraphCanonicalRecordIdSchema)
      .min(1)
      .max(100)
      .refine(allUnique, "Source frame record ids must be unique."),
    sourceReferenceHashes: z
      .array(SaleskoOpaqueSha256Schema)
      .min(1)
      .max(100)
      .refine(allUnique, "Source reference hashes must be unique."),
    evidenceSpanIds: z
      .array(GraphCanonicalRecordIdSchema)
      .max(100)
      .refine(allUnique, "Evidence span ids must be unique."),
    backfilled: z.boolean()
  })
  .strict();

export const GraphTransactionDataV2Schema = z
  .object({
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    authorityKey: GraphCanonicalRecordIdSchema,
    kind: TransactionKindSchema,
    externalId: z.string().trim().min(1).max(300),
    status: TransactionStatusSchema,
    amountMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    currency: TransactionCurrencyCodeSchema,
    semanticFingerprint: TransactionSemanticFingerprintSchema,
    partyCanonicalEntityIds: z
      .array(GraphCanonicalRecordIdSchema)
      .min(1)
      .max(100)
      .refine(allUnique, "Canonical Transaction party ids must be unique.")
  })
  .strict()
  .refine((transaction) => transaction.kind !== "payment" || transaction.status !== "committed", {
    message: "A payment Transaction cannot be in the committed state.",
    path: ["status"]
  });

const GraphPersonNodeV2Schema = GraphNodeSchema.extend({ type: z.literal("person") }).strict();
const GraphOrganisationNodeV2Schema = GraphNodeSchema.extend({ type: z.literal("organisation") }).strict();
const GraphEventNodeV2Schema = GraphNodeSchema.extend({
  type: z.literal("event"),
  sourceFact: GraphServerSourceFactMetadataSchema.optional()
}).strict();
const GraphOpportunityNodeV2Schema = GraphNodeSchema.extend({
  type: z.literal("opportunity"),
  sourceFact: GraphServerSourceFactMetadataSchema.optional()
}).strict();
const GraphNoteNodeV2Schema = GraphNodeSchema.extend({ type: z.literal("note") }).strict();

export const GraphTransactionNodeV2Schema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    type: z.literal("transaction"),
    label: z.string().trim().min(1).max(300),
    summary: z.string().trim().min(1).max(4_000).optional(),
    confidence: z.number().min(0).max(1),
    evidence: z.string().trim().min(1).max(4_000),
    occurredAt: z.string().datetime({ offset: true }),
    visual: GraphVisualSchema,
    data: GraphTransactionDataV2Schema,
    sourceFact: GraphServerSourceFactMetadataSchema
  })
  .strict();

export const GraphNodeV2Schema = z.discriminatedUnion("type", [
  GraphPersonNodeV2Schema,
  GraphOrganisationNodeV2Schema,
  GraphEventNodeV2Schema,
  GraphOpportunityNodeV2Schema,
  GraphNoteNodeV2Schema,
  GraphTransactionNodeV2Schema
]);

export const GraphEdgeV2Schema = GraphEdgeSchema.omit({ type: true })
  .extend({ type: GraphRelationTypeV2Schema })
  .strict();

export const GraphFilterV2Schema = GraphFilterSchema.omit({ entityType: true })
  .extend({ entityType: GraphEntityTypeV2Schema })
  .strict();

export const GraphStatsV2Schema = GraphStatsSchema.extend({
  transactions: z.number().int().nonnegative()
}).strict();

export const GraphVisualizationUiV2Schema = GraphVisualizationUiSchema.omit({ filters: true, stats: true })
  .extend({
    filters: z.array(GraphFilterV2Schema),
    stats: GraphStatsV2Schema
  })
  .strict();

export const GraphVisualizationFrameV2SourceSchema = z.enum(["server-transaction-command", "server-backfill"]);

function sameUniqueValues(actual: string[], expected: string[]): boolean {
  return actual.length === new Set(actual).size &&
    expected.length === new Set(expected).size &&
    actual.length === expected.length &&
    actual.every((value) => expected.includes(value));
}

function allUnique(values: string[]): boolean {
  return values.length === new Set(values).size;
}

export const GraphVisualizationFrameV2Schema = GraphVisualizationFrameSchema.omit({
  schemaVersion: true,
  graph: true,
  ui: true,
  facets: true,
  source: true
})
  .extend({
    schemaVersion: z.literal("graph.visualization.v2"),
    graph: z
      .object({
        nodes: z.array(GraphNodeV2Schema),
        edges: z.array(GraphEdgeV2Schema)
      })
      .strict(),
    ui: GraphVisualizationUiV2Schema,
    facets: z
      .object({
        entityTypes: z.array(GraphEntityTypeV2Schema).default([]),
        relationTypes: z.array(GraphRelationTypeV2Schema).default([]),
        warnings: z.array(z.string()).default([])
      })
      .strict(),
    source: GraphVisualizationFrameV2SourceSchema
  })
  .strict()
  .superRefine((frame, context) => {
    const nodeIds = frame.graph.nodes.map((node) => node.id);
    if (!allUnique(nodeIds)) {
      context.addIssue({
        code: "custom",
        message: "V2 graph node ids must be unique.",
        path: ["graph", "nodes"]
      });
    }
    const edgeIds = frame.graph.edges.map((edge) => edge.id);
    if (!allUnique(edgeIds)) {
      context.addIssue({
        code: "custom",
        message: "V2 graph edge ids must be unique.",
        path: ["graph", "edges"]
      });
    }
    const nodeById = new Map(frame.graph.nodes.map((node) => [node.id, node]));
    const transactionNodes = frame.graph.nodes.filter((node) => node.type === "transaction");

    frame.graph.edges.forEach((edge, edgeIndex) => {
      const source = nodeById.get(edge.source);
      const target = nodeById.get(edge.target);
      if (!source || !target) {
        context.addIssue({
          code: "custom",
          message: "Every v2 edge must reference nodes present in the same frame.",
          path: ["graph", "edges", edgeIndex]
        });
        return;
      }

      const transactionIsEndpoint = source.type === "transaction" || target.type === "transaction";
      if (edge.type === "party_to") {
        if ((source.type !== "person" && source.type !== "organisation") || target.type !== "transaction") {
          context.addIssue({
            code: "custom",
            message: "party_to must point from a person or organisation to a transaction.",
            path: ["graph", "edges", edgeIndex, "type"]
          });
        }
      } else if (edge.type === "evidence_for") {
        if (source.type !== "event" || target.type !== "transaction") {
          context.addIssue({
            code: "custom",
            message: "evidence_for must point from an event to a transaction.",
            path: ["graph", "edges", edgeIndex, "type"]
          });
        }
      } else if (edge.type === "converted_to") {
        if (source.type !== "opportunity" || target.type !== "transaction") {
          context.addIssue({
            code: "custom",
            message: "converted_to must point from an opportunity to a transaction.",
            path: ["graph", "edges", edgeIndex, "type"]
          });
        }
      } else if (transactionIsEndpoint) {
        context.addIssue({
          code: "custom",
          message: "Transactions may only use party_to, evidence_for, or converted_to relations.",
          path: ["graph", "edges", edgeIndex, "type"]
        });
      }
    });

    transactionNodes.forEach((transaction) => {
      if (transaction.id !== transaction.data.canonicalTransactionId) {
        context.addIssue({
          code: "custom",
          message: "A Transaction node id must equal its server-issued canonical Transaction id.",
          path: ["graph", "nodes", frame.graph.nodes.indexOf(transaction), "data", "canonicalTransactionId"]
        });
      }
      if (transaction.sourceFact.evidenceSpanIds.length === 0) {
        context.addIssue({
          code: "custom",
          message: "A committed transaction requires at least one evidence span.",
          path: ["graph", "nodes", frame.graph.nodes.indexOf(transaction), "sourceFact", "evidenceSpanIds"]
        });
      }
      const hasPartyEdge = frame.graph.edges.some((edge) => edge.type === "party_to" && edge.target === transaction.id);
      if (!hasPartyEdge) {
        context.addIssue({
          code: "custom",
          message: "A committed transaction requires at least one party_to edge.",
          path: ["graph", "nodes", frame.graph.nodes.indexOf(transaction)]
        });
      }
      const representedPartyCanonicalEntityIds = [
        ...new Set(
          frame.graph.edges.flatMap((edge) => {
            if (edge.type !== "party_to" || edge.target !== transaction.id) {
              return [];
            }
            const party = nodeById.get(edge.source);
            if (!party || (party.type !== "person" && party.type !== "organisation")) {
              return [];
            }
            return [canonicalEntityIdForNode(party.type, party.label)];
          })
        )
      ];
      if (!sameUniqueValues(transaction.data.partyCanonicalEntityIds, representedPartyCanonicalEntityIds)) {
        context.addIssue({
          code: "custom",
          message: "Canonical Transaction party ids must exactly match the parties represented by party_to edges.",
          path: ["graph", "nodes", frame.graph.nodes.indexOf(transaction), "data", "partyCanonicalEntityIds"]
        });
      }
    });

    const counts = {
      person: frame.graph.nodes.filter((node) => node.type === "person").length,
      organisation: frame.graph.nodes.filter((node) => node.type === "organisation").length,
      event: frame.graph.nodes.filter((node) => node.type === "event").length,
      opportunity: frame.graph.nodes.filter((node) => node.type === "opportunity").length,
      note: frame.graph.nodes.filter((node) => node.type === "note").length,
      transaction: transactionNodes.length
    };
    if (
      frame.ui.stats.people !== counts.person ||
      frame.ui.stats.organisations !== counts.organisation ||
      frame.ui.stats.events !== counts.event ||
      frame.ui.stats.opportunities !== counts.opportunity ||
      frame.ui.stats.transactions !== counts.transaction
    ) {
      context.addIssue({
        code: "custom",
        message: "V2 stats must equal the graph's entity counts.",
        path: ["ui", "stats"]
      });
    }

    const seenFilterTypes = new Set<string>();
    frame.ui.filters.forEach((filter, filterIndex) => {
      if (seenFilterTypes.has(filter.entityType)) {
        context.addIssue({
          code: "custom",
          message: "V2 filters must contain at most one filter per entity type.",
          path: ["ui", "filters", filterIndex, "entityType"]
        });
      }
      seenFilterTypes.add(filter.entityType);
      if (filter.count !== counts[filter.entityType]) {
        context.addIssue({
          code: "custom",
          message: "A v2 filter count must equal its graph entity count.",
          path: ["ui", "filters", filterIndex, "count"]
        });
      }
    });
    if (!seenFilterTypes.has("transaction")) {
      context.addIssue({
        code: "custom",
        message: "A v2 frame requires an explicit transaction filter.",
        path: ["ui", "filters"]
      });
    }

    const entityTypes = Array.from(new Set(frame.graph.nodes.map((node) => node.type)));
    if (!sameUniqueValues(frame.facets.entityTypes, entityTypes)) {
      context.addIssue({
        code: "custom",
        message: "V2 entity facets must exactly describe the frame's node types.",
        path: ["facets", "entityTypes"]
      });
    }
    const relationTypes = Array.from(new Set(frame.graph.edges.map((edge) => edge.type)));
    if (!sameUniqueValues(frame.facets.relationTypes, relationTypes)) {
      context.addIssue({
        code: "custom",
        message: "V2 relation facets must exactly describe the frame's edge types.",
        path: ["facets", "relationTypes"]
      });
    }
  });

export const SaleskoTransactionProposalEvidenceSchema = z
  .object({
    sourceId: z.string().trim().min(1).max(300),
    quote: z.string().trim().min(1).max(4_000)
  })
  .strict();

export const SaleskoTransactionProposalSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_proposal.v1"),
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    authorityNodeId: z.string().trim().min(1).max(160),
    kind: TransactionKindSchema,
    externalId: z.string().trim().min(1).max(300),
    amountMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    currency: TransactionCurrencyCodeSchema,
    status: TransactionStatusSchema,
    occurredAt: z.string().datetime({ offset: true }),
    partyNodeIds: z
      .array(z.string().trim().min(1).max(160))
      .min(1)
      .max(100)
      .refine(allUnique, "Proposed Transaction party ids must be unique."),
    evidence: z
      .array(SaleskoTransactionProposalEvidenceSchema)
      .min(1)
      .max(100)
      .refine((items) => allUnique(items.map((item) => item.sourceId)), "Proposal evidence source ids must be unique.")
  })
  .strict()
  .superRefine((proposal, context) => {
    if (proposal.status === "cancelled") {
      return;
    }
    const expectedStatus = proposal.kind === "payment" ? "settled" : "committed";
    if (proposal.status !== expectedStatus) {
      context.addIssue({
        code: "custom",
        message:
          proposal.kind === "payment"
            ? "A non-cancelled payment proposal must start settled."
            : "A non-cancelled contract, order, or invoice proposal must start committed.",
        path: ["status"]
      });
    }
  });

export const TransactionProposalExtractionNodeSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    type: z.enum(["person", "organisation"]),
    label: z.string().trim().min(1).max(300),
    summary: z.string().trim().min(1).max(4_000).optional()
  })
  .strict();

export const TransactionProposalExtractionRequestSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_proposal_extraction_request.v1"),
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    candidateNodes: z
      .array(TransactionProposalExtractionNodeSchema)
      .min(1)
      .max(200)
      .refine((nodes) => allUnique(nodes.map((node) => node.id)), "Proposal extraction node ids must be unique."),
    evidence: z
      .array(SaleskoTransactionProposalEvidenceSchema)
      .min(1)
      .max(100)
      .refine(
        (items) => allUnique(items.map((item) => item.sourceId)),
        "Proposal extraction evidence span ids must be unique."
      )
  })
  .strict()
  .refine((request) => request.candidateNodes.some((node) => node.type === "organisation"), {
    message: "Transaction proposal extraction requires an organisation authority candidate.",
    path: ["candidateNodes"]
  });

export const TransactionProposalExtractionResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_proposal_extraction_response.v1"),
    proposals: z.array(SaleskoTransactionProposalSchema).max(20)
  })
  .strict()
  .refine(
    (response) =>
      allUnique(
        response.proposals.map(
          (proposal) => `${proposal.authorityNodeId}\u0000${proposal.kind}\u0000${proposal.externalId}`
        )
      ),
    {
      message: "A proposal extraction response cannot repeat the same proposed Transaction identity.",
      path: ["proposals"]
    }
  );

export const TransactionProposalExtractionErrorCodeSchema = z.enum([
  "transaction_proposal_model_unavailable",
  "transaction_proposal_model_timeout",
  "transaction_proposal_invalid_model_output",
  "transaction_proposal_unsupported_source"
]);

export const TransactionProposalExtractionErrorResponseSchema = z
  .object({
    error: z
      .object({
        code: TransactionProposalExtractionErrorCodeSchema,
        message: z.string().trim().min(1)
      })
      .strict()
  })
  .strict();

export const TransactionClientMutationIdSchema = z.string().trim().min(1).max(128);

export const TransactionRecordCommandSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_record.v1"),
    clientMutationId: TransactionClientMutationIdSchema,
    proposal: SaleskoTransactionProposalSchema
  })
  .strict();

export const TransactionStatusChangeCommandSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_status_change.v1"),
    clientMutationId: TransactionClientMutationIdSchema,
    expectedSemanticFingerprint: TransactionSemanticFingerprintSchema,
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    status: z.enum(["settled", "cancelled"]),
    evidence: z
      .array(SaleskoTransactionProposalEvidenceSchema)
      .min(1)
      .max(100)
      .refine(
        (items) => allUnique(items.map((item) => item.sourceId)),
        "Status-change evidence span ids must be unique."
      )
  })
  .strict();

export const TransactionCorrectionSnapshotSchema = z
  .object({
    authorityNodeId: GraphCanonicalRecordIdSchema,
    kind: TransactionKindSchema,
    externalId: z.string().trim().min(1).max(300),
    status: TransactionStatusSchema,
    amountMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    currency: TransactionCurrencyCodeSchema,
    occurredAt: z.string().datetime({ offset: true }),
    partyNodeIds: z
      .array(GraphCanonicalRecordIdSchema)
      .min(1)
      .max(100)
      .refine(allUnique, "Corrected Transaction party ids must be unique.")
  })
  .strict()
  .refine((snapshot) => snapshot.kind !== "payment" || snapshot.status !== "committed", {
    message: "A corrected payment Transaction cannot be in the committed state.",
    path: ["status"]
  });

export const TransactionCorrectionCommandSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_correction.v1"),
    clientMutationId: TransactionClientMutationIdSchema,
    expectedSemanticFingerprint: TransactionSemanticFingerprintSchema,
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    replacement: TransactionCorrectionSnapshotSchema,
    evidence: z
      .array(SaleskoTransactionProposalEvidenceSchema)
      .min(1)
      .max(100)
      .refine(
        (items) => allUnique(items.map((item) => item.sourceId)),
        "Correction evidence span ids must be unique."
      )
  })
  .strict();

export const ActivityBackfillEvidenceSubjectSchema = z.discriminatedUnion("subjectType", [
  z
    .object({
      subjectType: z.literal("event"),
      nodeId: GraphCanonicalRecordIdSchema,
      evidenceSpanId: GraphCanonicalRecordIdSchema,
      quote: z.string().trim().min(1).max(4_000)
    })
    .strict(),
  z
    .object({
      subjectType: z.literal("opportunity"),
      nodeId: GraphCanonicalRecordIdSchema,
      evidenceSpanId: GraphCanonicalRecordIdSchema,
      quote: z.string().trim().min(1).max(4_000)
    })
    .strict(),
  z
    .object({
      subjectType: z.literal("transaction"),
      proposal: SaleskoTransactionProposalSchema
    })
    .strict()
]);

export const ActivityBackfillCommandSchema = z
  .object({
    schemaVersion: z.literal("salesko.activity_backfill.v1"),
    clientMutationId: TransactionClientMutationIdSchema,
    originalRecordedAt: z.string().datetime({ offset: true }),
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    subjects: z.array(ActivityBackfillEvidenceSubjectSchema).min(1).max(200)
  })
  .strict()
  .superRefine((command, context) => {
    const subjectKeys = command.subjects.map((subject) =>
      subject.subjectType === "transaction"
        ? `transaction\u0000${subject.proposal.authorityNodeId}\u0000${subject.proposal.kind}\u0000${subject.proposal.externalId}`
        : `${subject.subjectType}\u0000${subject.nodeId}\u0000${subject.evidenceSpanId}`
    );
    if (!allUnique(subjectKeys)) {
      context.addIssue({
        code: "custom",
        message: "An Activity backfill command cannot repeat the same evidence-backed subject.",
        path: ["subjects"]
      });
    }
    command.subjects.forEach((subject, subjectIndex) => {
      if (
        subject.subjectType === "transaction" &&
        subject.proposal.sourceFrameRecordId !== command.sourceFrameRecordId
      ) {
        context.addIssue({
          code: "custom",
          message: "A historical Transaction proposal must bind the command source frame.",
          path: ["subjects", subjectIndex, "proposal", "sourceFrameRecordId"]
        });
      }
    });
  });

export const TransactionAuthorityResolutionMethodSchema = z.enum(["trusted_connector", "user_reviewed_organisation"]);

export const TransactionAuthorityRegistryRecordSchema = z
  .object({
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    authorityKey: GraphCanonicalRecordIdSchema,
    organisationCanonicalEntityId: GraphCanonicalRecordIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true })
  })
  .strict();

export const TransactionAuthoritySourceRecordSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    authorityKey: GraphCanonicalRecordIdSchema,
    resolutionMethod: TransactionAuthorityResolutionMethodSchema,
    issuerReferenceHash: SaleskoOpaqueSha256Schema,
    sourceAccountHash: SaleskoOpaqueSha256Schema.optional(),
    verifiedAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true })
  })
  .strict();

export const TransactionRegistryRecordSchema = z
  .object({
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    authorityKey: GraphCanonicalRecordIdSchema,
    kind: TransactionKindSchema,
    externalIdHash: SaleskoOpaqueSha256Schema,
    currentSemanticFingerprint: TransactionSemanticFingerprintSchema,
    currentFrameRecordId: GraphCanonicalRecordIdSchema,
    currentJournalEntryId: GraphCanonicalRecordIdSchema,
    supersedesTransactionId: GraphCanonicalRecordIdSchema.optional(),
    supersededByTransactionId: GraphCanonicalRecordIdSchema.optional(),
    supersededAt: z.string().datetime({ offset: true }).optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true })
  })
  .strict()
  .refine(
    (record) =>
      (record.supersededByTransactionId === undefined) === (record.supersededAt === undefined),
    {
      message: "A superseded Transaction registry record requires both replacement id and superseded time.",
      path: ["supersededByTransactionId"]
    }
  )
  .refine(
    (record) =>
      record.supersedesTransactionId === undefined ||
      (record.supersedesTransactionId !== record.canonicalTransactionId &&
        record.supersedesTransactionId !== record.supersededByTransactionId),
    {
      message: "A replacement Transaction must point to a distinct predecessor.",
      path: ["supersedesTransactionId"]
    }
  );

export const ActivityEvidenceStateSchema = z.enum(["available", "source_unavailable", "redacted"]);

export const TransactionSourceRecordSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    authoritySourceId: GraphCanonicalRecordIdSchema,
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    committedFrameRecordId: GraphCanonicalRecordIdSchema,
    sourceFactKey: TransactionSourceFactKeySchema,
    sourceReferenceHash: SaleskoOpaqueSha256Schema,
    semanticFingerprint: TransactionSemanticFingerprintSchema,
    evidenceSpanId: GraphCanonicalRecordIdSchema.optional(),
    evidenceState: ActivityEvidenceStateSchema,
    qualifyingAuthority: z.boolean().default(true),
    observedAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true })
  })
  .strict()
  .refine((source) => (source.evidenceState === "available") === (source.evidenceSpanId !== undefined), {
    message: "Available Transaction sources require an evidence span; unavailable or redacted sources forbid one.",
    path: ["evidenceSpanId"]
  });

export const ActivityJournalEntryTypeSchema = z.enum([
  "event.occurred",
  "opportunity.surfaced",
  "transaction.recorded",
  "transaction.status_changed",
  "transaction.corrected"
]);
export const ActivitySubjectTypeSchema = z.enum(["event", "opportunity", "transaction"]);

type ActivityEntrySubject = {
  id: string;
  entryType: z.infer<typeof ActivityJournalEntryTypeSchema>;
  subjectType: z.infer<typeof ActivitySubjectTypeSchema>;
  supersedesEntryId?: string | undefined;
};

function addActivityEntryInvariantIssues(value: ActivityEntrySubject, context: z.RefinementCtx): void {
  const expectedSubjectType = value.entryType.startsWith("event.")
    ? "event"
    : value.entryType.startsWith("opportunity.")
      ? "opportunity"
      : "transaction";
  if (value.subjectType !== expectedSubjectType) {
    context.addIssue({
      code: "custom",
      message: "Activity entry type and subject type must agree.",
      path: ["subjectType"]
    });
  }

  const isCorrection = value.entryType === "transaction.corrected";
  if (isCorrection !== (value.supersedesEntryId !== undefined)) {
    context.addIssue({
      code: "custom",
      message: "Only transaction.corrected requires a superseded entry id.",
      path: ["supersedesEntryId"]
    });
  }
  if (value.supersedesEntryId === value.id) {
    context.addIssue({
      code: "custom",
      message: "An activity correction cannot supersede itself.",
      path: ["supersedesEntryId"]
    });
  }
}

function addActivityJournalInvariantIssues(
  value: ActivityEntrySubject & { semanticFingerprint?: string | undefined },
  context: z.RefinementCtx
): void {
  addActivityEntryInvariantIssues(value, context);
  const isTransactionEntry = value.subjectType === "transaction";
  if (isTransactionEntry !== (value.semanticFingerprint !== undefined)) {
    context.addIssue({
      code: "custom",
      message: "Only transaction journal entries require a semantic fingerprint.",
      path: ["semanticFingerprint"]
    });
  }
}

export const ActivityJournalEntrySchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    entryType: ActivityJournalEntryTypeSchema,
    subjectType: ActivitySubjectTypeSchema,
    subjectId: GraphCanonicalRecordIdSchema,
    sourceFactKey: TransactionSourceFactKeySchema,
    semanticFingerprint: TransactionSemanticFingerprintSchema.optional(),
    recordedAt: z.string().datetime({ offset: true }),
    occurredAt: z.string().datetime({ offset: true }).optional(),
    title: z.string().trim().min(1).max(300),
    summary: z.string().trim().min(1).max(4_000),
    supersedesEntryId: GraphCanonicalRecordIdSchema.optional(),
    backfilled: z.boolean()
  })
  .strict()
  .superRefine(addActivityJournalInvariantIssues);

export const ActivityJournalSourceRecordSchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    datasetScope: GraphDatasetScopeSchema.default("salesko"),
    tenantId: TenantIdSchema,
    journalEntryId: GraphCanonicalRecordIdSchema,
    sourceFrameRecordId: GraphCanonicalRecordIdSchema,
    transactionSourceId: GraphCanonicalRecordIdSchema.optional(),
    sourceReferenceHash: SaleskoOpaqueSha256Schema,
    evidenceSpanId: GraphCanonicalRecordIdSchema.optional(),
    evidenceState: ActivityEvidenceStateSchema,
    qualifyingAuthority: z.boolean().default(true),
    createdAt: z.string().datetime({ offset: true })
  })
  .strict()
  .refine((source) => (source.evidenceState === "available") === (source.evidenceSpanId !== undefined), {
    message: "Available Activity sources require an evidence span; unavailable or redacted sources forbid one.",
    path: ["evidenceSpanId"]
  });

export const ActivityFeedCursorSchema = z.string().min(16).max(512).regex(/^[A-Za-z0-9_-]+$/);

export const ActivityFeedQuerySchema = z
  .object({
    cursor: ActivityFeedCursorSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50)
  })
  .strict();

export const TransactionActivityCapabilityResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_activity_capability.v1"),
    enabled: z.boolean()
  })
  .strict();

export const ActivityFeedSourceReferenceSchema = z
  .object({
    sourceReferenceHash: SaleskoOpaqueSha256Schema,
    sourceFrameRecordId: GraphCanonicalRecordIdSchema.optional(),
    evidenceSpanId: GraphCanonicalRecordIdSchema.optional(),
    evidenceState: ActivityEvidenceStateSchema
  })
  .strict()
  .superRefine((source, context) => {
    const available = source.evidenceState === "available";
    if (available !== (source.evidenceSpanId !== undefined)) {
      context.addIssue({
        code: "custom",
        message: "Available Feed sources require an evidence span; unavailable or redacted sources forbid one.",
        path: ["evidenceSpanId"]
      });
    }
    if (available !== (source.sourceFrameRecordId !== undefined)) {
      context.addIssue({
        code: "custom",
        message: "Available Feed sources require an exact source frame; unavailable or redacted sources forbid one.",
        path: ["sourceFrameRecordId"]
      });
    }
  });

export const ActivityFeedEntrySchema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    entryType: ActivityJournalEntryTypeSchema,
    subjectType: ActivitySubjectTypeSchema,
    subjectId: GraphCanonicalRecordIdSchema,
    sourceFactKey: TransactionSourceFactKeySchema,
    recordedAt: z.string().datetime({ offset: true }),
    occurredAt: z.string().datetime({ offset: true }).optional(),
    title: z.string().trim().min(1).max(300),
    summary: z.string().trim().min(1).max(4_000),
    evidenceState: ActivityEvidenceStateSchema,
    sources: z
      .array(ActivityFeedSourceReferenceSchema)
      .min(1)
      .max(100)
      .refine(
        (sources) => allUnique(sources.map((source) => source.sourceReferenceHash)),
        "Activity Feed source reference hashes must be unique."
      ),
    supersedesEntryId: GraphCanonicalRecordIdSchema.optional(),
    backfilled: z.boolean()
  })
  .strict()
  .superRefine((entry, context) => {
    addActivityEntryInvariantIssues(entry, context);
    entry.sources.forEach((source, sourceIndex) => {
      if (source.evidenceState !== entry.evidenceState) {
        context.addIssue({
          code: "custom",
          message: "Mixed Activity Feed evidence states are not supported until an aggregation contract is approved.",
          path: ["sources", sourceIndex, "evidenceState"]
        });
      }
    });
  });

export const ActivityFeedResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.activity_feed.v1"),
    items: z.array(ActivityFeedEntrySchema),
    count: z.number().int().nonnegative(),
    nextCursor: ActivityFeedCursorSchema.nullable()
  })
  .strict()
  .refine((response) => response.count === response.items.length, {
    message: "Activity Feed count must equal items.length.",
    path: ["count"]
  })
  .refine((response) => allUnique(response.items.map((item) => item.id)), {
    message: "Activity Feed page item ids must be unique.",
    path: ["items"]
  })
  .superRefine((response, context) => {
    for (let itemIndex = 1; itemIndex < response.items.length; itemIndex += 1) {
      const previous = response.items[itemIndex - 1]!;
      const current = response.items[itemIndex]!;
      const previousRecordedAt = Date.parse(previous.recordedAt);
      const currentRecordedAt = Date.parse(current.recordedAt);
      if (
        previousRecordedAt < currentRecordedAt ||
        (previousRecordedAt === currentRecordedAt && previous.id <= current.id)
      ) {
        context.addIssue({
          code: "custom",
          message: "Activity Feed items must be ordered by recordedAt DESC, then id DESC.",
          path: ["items", itemIndex]
        });
      }
    }
  });

export type TransactionKind = z.infer<typeof TransactionKindSchema>;
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;
export type TransactionCurrencyCode = z.infer<typeof TransactionCurrencyCodeSchema>;
export type TransactionSemanticFingerprint = z.infer<typeof TransactionSemanticFingerprintSchema>;
export type TransactionSourceFactKey = z.infer<typeof TransactionSourceFactKeySchema>;
export type GraphEntityTypeV2 = z.infer<typeof GraphEntityTypeV2Schema>;
export type GraphRelationTypeV2 = z.infer<typeof GraphRelationTypeV2Schema>;
export type GraphServerSourceFactMetadata = z.infer<typeof GraphServerSourceFactMetadataSchema>;
export type GraphTransactionDataV2 = z.infer<typeof GraphTransactionDataV2Schema>;
export type GraphTransactionNodeV2 = z.infer<typeof GraphTransactionNodeV2Schema>;
export type GraphNodeV2 = z.infer<typeof GraphNodeV2Schema>;
export type GraphEdgeV2 = z.infer<typeof GraphEdgeV2Schema>;
export type GraphStatsV2 = z.infer<typeof GraphStatsV2Schema>;
export type GraphVisualizationFrameV2 = z.infer<typeof GraphVisualizationFrameV2Schema>;
export type SaleskoTransactionProposalEvidence = z.infer<typeof SaleskoTransactionProposalEvidenceSchema>;
export type SaleskoTransactionProposal = z.infer<typeof SaleskoTransactionProposalSchema>;
export type TransactionProposalExtractionNode = z.infer<typeof TransactionProposalExtractionNodeSchema>;
export type TransactionProposalExtractionRequest = z.infer<typeof TransactionProposalExtractionRequestSchema>;
export type TransactionProposalExtractionResponse = z.infer<typeof TransactionProposalExtractionResponseSchema>;
export type TransactionProposalExtractionErrorCode = z.infer<typeof TransactionProposalExtractionErrorCodeSchema>;
export type TransactionProposalExtractionErrorResponse = z.infer<
  typeof TransactionProposalExtractionErrorResponseSchema
>;
export type TransactionRecordCommand = z.infer<typeof TransactionRecordCommandSchema>;
export type TransactionStatusChangeCommand = z.infer<typeof TransactionStatusChangeCommandSchema>;
export type TransactionCorrectionSnapshot = z.infer<typeof TransactionCorrectionSnapshotSchema>;
export type TransactionCorrectionCommand = z.infer<typeof TransactionCorrectionCommandSchema>;
export type ActivityBackfillEvidenceSubject = z.infer<typeof ActivityBackfillEvidenceSubjectSchema>;
export type ActivityBackfillCommand = z.infer<typeof ActivityBackfillCommandSchema>;
export type TransactionAuthorityResolutionMethod = z.infer<typeof TransactionAuthorityResolutionMethodSchema>;
export type TransactionAuthorityRegistryRecord = z.infer<typeof TransactionAuthorityRegistryRecordSchema>;
export type TransactionAuthoritySourceRecord = z.infer<typeof TransactionAuthoritySourceRecordSchema>;
export type TransactionRegistryRecord = z.infer<typeof TransactionRegistryRecordSchema>;
export type ActivityEvidenceState = z.infer<typeof ActivityEvidenceStateSchema>;
export type TransactionSourceRecord = z.infer<typeof TransactionSourceRecordSchema>;
export type ActivityJournalEntryType = z.infer<typeof ActivityJournalEntryTypeSchema>;
export type ActivitySubjectType = z.infer<typeof ActivitySubjectTypeSchema>;
export type ActivityJournalEntry = z.infer<typeof ActivityJournalEntrySchema>;
export type ActivityJournalSourceRecord = z.infer<typeof ActivityJournalSourceRecordSchema>;
export type ActivityFeedCursor = z.infer<typeof ActivityFeedCursorSchema>;
export type ActivityFeedQuery = z.infer<typeof ActivityFeedQuerySchema>;
export type TransactionActivityCapabilityResponse = z.infer<
  typeof TransactionActivityCapabilityResponseSchema
>;
export type ActivityFeedSourceReference = z.infer<typeof ActivityFeedSourceReferenceSchema>;
export type ActivityFeedEntry = z.infer<typeof ActivityFeedEntrySchema>;
export type ActivityFeedResponse = z.infer<typeof ActivityFeedResponseSchema>;

export const GraphFrameProjectionSourceSchema = z.object({
  schemaVersion: z.literal("salesko.graph_frame_projection_source.v1"),
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  projectionSource: z.enum(["frame_authority", "canonical_graph", "shadow_canonical_graph"]),
  frameRecordId: z.string().min(1).optional(),
  frameId: z.string().min(1).optional(),
  generatedAt: z.string().datetime(),
  entityIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  edgeIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  evidenceSpanIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  changeSetIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  warnings: z.array(z.string()).default([]),
  data: z.record(z.string(), z.unknown()).default({})
});

export const AgentChatMentionRangeSchema = z
  .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative()
  })
  .refine((range) => range.end >= range.start, {
    message: "Mention range end must be greater than or equal to start."
  });

export const AgentChatMentionSchema = z.object({
  nodeId: z.string().min(1),
  label: z.string().min(1),
  entityType: GraphEntityTypeSchema,
  sourceFrameId: z.string().min(1),
  range: AgentChatMentionRangeSchema.optional()
});

export const AgentChatMessageSchema = z.object({
  id: z.string().min(1),
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1),
  createdAt: z.string().datetime().optional(),
  mentions: z.array(AgentChatMentionSchema).default([])
});

export const AgentAssistantChatMessageSchema = AgentChatMessageSchema.extend({
  role: z.literal("assistant")
});

export const AgentChatRequestSchema = z.object({
  threadId: z.string().min(1).optional(),
  messages: z.array(AgentChatMessageSchema).min(1),
  locale: LocaleSchema.default(DefaultSaleskoLocale),
  mode: z.enum(["graph-copilot", "seeker"]).default("graph-copilot"),
  currentFrameId: z.string().min(1).optional(),
  currentFrame: GraphVisualizationFrameSchema.optional(),
  activeNodeId: z.string().min(1).optional(),
  mentions: z.array(AgentChatMentionSchema).default([])
});

export const AgentChatErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1)
});

export const AgentChatResponseSchema = z.object({
  threadId: z.string().min(1).optional(),
  assistantMessage: AgentAssistantChatMessageSchema,
  frame: GraphVisualizationFrameSchema.optional(),
  commands: z.array(AgentGraphCommandSchema).default([]),
  warnings: z.array(z.string()).default([]),
  source: z.enum(["tanstack-ai-generate", "mastra-stream", "mastra-generate", "deterministic-rule"])
});

export const AgentChatStreamEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("chat.started"),
    threadId: z.string().min(1).optional(),
    assistantMessageId: z.string().min(1)
  }),
  z.object({
    type: z.literal("chat.text_delta"),
    text: z.string().min(1)
  }),
  z.object({
    type: z.literal("chat.status"),
    status: z.string().min(1)
  }),
  z.object({
    type: z.literal("graph.frame"),
    frame: GraphVisualizationFrameSchema
  }),
  z.object({
    type: z.literal("graph.commands"),
    commands: z.array(AgentGraphCommandSchema).default([])
  }),
  z.object({
    type: z.literal("chat.done"),
    response: AgentChatResponseSchema
  }),
  z.object({
    type: z.literal("chat.error"),
    error: AgentChatErrorSchema
  })
]);

/** Read-only Generic Agent quota status for the current daily window (UTC).
 * `limit`/`remaining` are null for unlimited tiers (business_managed). */
export const AgentQuotaStatusResponseSchema = z.object({
  tier: SaleskoBillingTierSchema,
  period: z.string().min(1),
  limit: z.number().int().nonnegative().nullable(),
  used: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative().nullable()
});

/** Lightweight row for the thread history list (no message bodies). */
export const AgentChatThreadSummarySchema = z.object({
  threadId: z.string().min(1),
  title: z.string(),
  messageCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  frameRecordId: z.string().min(1).optional()
});

/** A persisted Generic Agent chat thread with its full transcript. Stored
 * per-user in apps/api (PlanetScale); the agent worker itself stays stateless. */
export const AgentChatThreadSchema = AgentChatThreadSummarySchema.extend({
  messages: z.array(AgentChatMessageSchema)
});

/** Idempotent upsert of a whole thread — the client sends the full transcript it
 * holds in memory after chat.done. The server derives the title from the first
 * user message when the client omits it. */
export const AgentChatThreadUpsertRequestSchema = z.object({
  messages: z.array(AgentChatMessageSchema).min(1),
  title: z.string().min(1).max(200).optional()
});

export const AgentChatThreadListResponseSchema = z.object({
  threads: z.array(AgentChatThreadSummarySchema)
});

export const AgentChatThreadResponseSchema = z.object({
  thread: AgentChatThreadSchema
});

export const GraphFrameWriteSchema = z.object({
  sessionId: z.string().min(1).optional(),
  message: z.string().min(1),
  frame: GraphVisualizationFrameSchema,
  submittedAt: z.string().datetime().optional()
});

export const GraphNoteIntakeSourceChannelSchema = z.enum([
  "web_note",
  "agent_note",
  "cli",
  "api_note",
  "import",
  "smoke"
]);

export const GraphNoteIntakeProcessingModeSchema = z.enum(["sync", "async"]);

export const GraphNoteIntakeRequestSchema = z.object({
  threadId: z.string().min(1).optional(),
  sessionId: z.string().min(1).optional(),
  currentFrameId: z.string().min(1).optional(),
  currentFrame: GraphVisualizationFrameSchema.optional(),
  extractedFrame: GraphVisualizationFrameSchema,
  sourceChannel: GraphNoteIntakeSourceChannelSchema.default("api_note"),
  languageHint: z.union([LocaleSchema, z.literal("auto")]).default("auto"),
  processingMode: GraphNoteIntakeProcessingModeSchema.default("sync"),
  text: z.string().trim().min(1).max(20_000),
  capturedAt: z.string().datetime().optional(),
  options: z
    .object({
      previewOnly: z.literal(false).default(false),
      autoApplyThreshold: z.number().min(0).max(1).default(0.95)
    })
    .default({ previewOnly: false, autoApplyThreshold: 0.95 })
}).refine((request) => request.extractedFrame.source !== "deterministic-rule", {
  message: "Note intake requires an LLM-extracted graph frame.",
  path: ["extractedFrame", "source"]
});

export const GraphNoteIntakePreviewSchema = z.object({
  candidateEntities: z.number().int().nonnegative(),
  candidateRelationships: z.number().int().nonnegative(),
  candidateOpportunities: z.number().int().nonnegative(),
  needsReview: z.boolean()
});

export const GraphNoteEvidenceSummarySchema = z.object({
  schemaVersion: z.literal("salesko.note_evidence_summary.v1"),
  noteDocumentId: GraphCanonicalRecordIdSchema.optional(),
  intakeId: z.string().min(1).optional(),
  frameRecordId: z.string().min(1).optional(),
  sourceChannel: GraphNoteIntakeSourceChannelSchema,
  languageHint: z.union([LocaleSchema, z.literal("auto")]),
  capturedAt: z.string().datetime().optional(),
  entityEvidenceCount: z.number().int().nonnegative(),
  relationshipEvidenceCount: z.number().int().nonnegative(),
  evidenceSpanCount: z.number().int().nonnegative(),
  canonicalEvidenceReady: z.boolean()
});

export const GraphNoteIntakeJobStatusSchema = z.enum(["queued", "processing", "committed", "failed"]);

export const GraphNoteIntakeJobRecordSchema = z.object({
  schemaVersion: z.literal("salesko.note_intake.job.v1"),
  intakeId: z.string().min(1),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128).optional(),
  status: GraphNoteIntakeJobStatusSchema,
  sourceChannel: GraphNoteIntakeSourceChannelSchema,
  languageHint: z.union([LocaleSchema, z.literal("auto")]),
  transport: z.enum(["cloudflare_queue", "memory"]),
  queuedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  capturedAt: z.string().datetime().optional(),
  startedAt: z.string().datetime().optional(),
  committedAt: z.string().datetime().optional(),
  failedAt: z.string().datetime().optional(),
  frameRecordId: z.string().min(1).optional(),
  failureMessage: z.string().min(1).max(500).optional(),
  preview: GraphNoteIntakePreviewSchema,
  evidence: GraphNoteEvidenceSummarySchema.optional(),
  frame: GraphVisualizationFrameSchema
});

export const GraphNoteIntakeJobMessageSchema = z.object({
  schemaVersion: z.literal("salesko.note_intake.job_message.v1"),
  intakeId: z.string().min(1),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128).optional(),
  request: GraphNoteIntakeRequestSchema,
  frame: GraphVisualizationFrameSchema,
  evidence: GraphNoteEvidenceSummarySchema.optional(),
  queuedAt: z.string().datetime()
});

export const GraphFrameExportFormatSchema = z.enum(["json", "obsidian"]);

export const GraphFrameJsonExportArtifactSchema = z.object({
  schemaVersion: z.literal("salesko.graph_frame.export.v1"),
  exportedAt: z.string().datetime(),
  frameRecordId: z.string().min(1),
  frame: GraphVisualizationFrameSchema
});

export const GraphObsidianExportFileSchema = z.object({
  path: z.string().min(1).max(300),
  content: z.string().min(1).max(100_000)
});

export const GraphFrameObsidianExportArtifactSchema = z.object({
  schemaVersion: z.literal("salesko.graph_frame.obsidian_export.v1"),
  exportedAt: z.string().datetime(),
  frameRecordId: z.string().min(1),
  files: z.array(GraphObsidianExportFileSchema).min(1).max(200),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()).default([])
});

export const GraphFrameExportResponseSchema = z.discriminatedUnion("format", [
  z.object({
    format: z.literal("json"),
    contentType: z.literal("application/json"),
    filename: z.string().min(1),
    artifact: GraphFrameJsonExportArtifactSchema
  }),
  z.object({
    format: z.literal("obsidian"),
    contentType: z.literal("application/json"),
    filename: z.string().min(1),
    artifact: GraphFrameObsidianExportArtifactSchema
  })
]);

export const GraphFrameJsonImportRequestSchema = z
  .object({
    sessionId: z.string().min(1).optional(),
    message: z.string().min(1).max(500).optional(),
    submittedAt: z.string().datetime().optional(),
    frame: GraphVisualizationFrameSchema.optional(),
    artifact: GraphFrameJsonExportArtifactSchema.optional()
  })
  .refine((input) => Boolean(input.frame ?? input.artifact), {
    message: "JSON import requires frame or artifact."
  });

export const GraphFrameObsidianImportRequestSchema = z.object({
  sessionId: z.string().min(1).optional(),
  message: z.string().min(1).max(500).optional(),
  submittedAt: z.string().datetime().optional(),
  files: z.array(GraphObsidianExportFileSchema).min(1).max(200)
});

export const GraphProjectionStatusSchema = z.enum(["projected", "failed", "skipped"]);
export const GraphChangeSetStatusSchema = z.enum(["pending", "applied", "reverted"]);
export const GraphChangeSetAuditLogEventTypeSchema = z.enum([
  "change_set.created",
  "change_set.applied",
  "change_set.reverted"
]);

const GraphChangeSetWriteBaseSchema = z.object({
  reason: z.string().min(1).max(500).optional(),
  clientMutationId: z.string().min(1).max(128).optional(),
  // 200 keeps one CSV import (e.g. the 50-node sample with edges) inside a single
  // change set while still bounding manual change-set payloads.
  commands: z.array(GraphMutationCommandSchema).max(200).default([]),
  // Canonical ops are journaled proposal intent. They do not execute directly
  // while frame authority remains the write/read boundary.
  canonicalOps: z.array(GraphChangeSetOpSchema).max(200).optional()
});

export const GraphChangeSetWriteSchema = GraphChangeSetWriteBaseSchema.refine(
  (input) => input.commands.length > 0 || Boolean(input.canonicalOps?.length),
  {
  message: "Change set requires frame commands or canonical ops."
  }
);

export const GraphV1ChangeSetCreateRequestSchema = GraphChangeSetWriteBaseSchema.extend({
  frameRecordId: z.string().min(1)
}).refine((input) => input.commands.length > 0 || Boolean(input.canonicalOps?.length), {
  message: "Change set requires frame commands or canonical ops."
});

export function graphMutationCommandsFromCanonicalOps(
  canonicalOps: z.infer<typeof GraphChangeSetOpSchema>[]
): z.infer<typeof GraphMutationCommandSchema>[] {
  const commands: z.infer<typeof GraphMutationCommandSchema>[] = [];
  for (const [index, operation] of canonicalOps.entries()) {
    if (operation.type === "upsert_entity") {
      commands.push({
        type: "upsert_node",
        node: {
          id: operation.entity.id,
          type: operation.entity.entityType,
          label: operation.entity.label,
          ...(operation.entity.summary ? { summary: operation.entity.summary } : {}),
          confidence: operation.entity.confidence,
          visual: {
            position: { x: (index % 6) - 2.5, y: Math.floor(index / 6), z: 0 },
            color: canonicalEntityColor(operation.entity.entityType),
            size: 1,
            emphasis: "normal"
          },
          data: {
            ...operation.entity.data,
            canonicalEntityId: operation.entity.id,
            datasetScope: operation.entity.datasetScope
          }
        }
      });
      continue;
    }

    if (operation.type === "upsert_relationship_edge") {
      commands.push({
        type: "upsert_edge",
        edge: {
          id: operation.edge.id,
          source: operation.edge.sourceEntityId,
          target: operation.edge.targetEntityId,
          type: operation.edge.relationType,
          label: operation.edge.label,
          confidence: operation.edge.confidence,
          strength: operation.edge.strength,
          data: {
            ...operation.edge.data,
            canonicalEdgeId: operation.edge.id,
            evidenceSpanIds: operation.edge.evidenceSpanIds
          }
        }
      });
      continue;
    }

    if (operation.type === "delete_relationship_edge") {
      commands.push({
        type: "delete_edge",
        edgeId: operation.edgeId
      });
    }
  }

  return commands;
}

function canonicalEntityColor(entityType: z.infer<typeof GraphEntityTypeSchema>) {
  switch (entityType) {
    case "person":
      return "#4a8cb0";
    case "organisation":
      return "#6ba872";
    case "event":
      return "#d4a44f";
    case "opportunity":
      return "#c97b63";
    case "note":
      return "#8b7cc3";
  }
}

export const GraphCsvImportRequestSchema = z.object({
  reason: z.string().min(1).max(500).optional(),
  clientMutationId: z.string().min(1).max(128).optional(),
  csv: z.string().min(1).max(100_000)
});

export const GraphChangeSetRecordSchema = z.object({
  id: z.string().min(1),
  frameRecordId: z.string().min(1),
  status: GraphChangeSetStatusSchema,
  commandCount: z.number().int().positive(),
  baseFrameVersion: z.number().int().positive().optional(),
  appliedFrameVersion: z.number().int().positive().optional(),
  reason: z.string().optional(),
  clientMutationId: z.string().optional(),
  createdAt: z.string().datetime(),
  appliedAt: z.string().datetime().optional(),
  revertedAt: z.string().datetime().optional()
});

export const GraphChangeSetCreateResponseSchema = z.object({
  changeSet: GraphChangeSetRecordSchema,
  auditLogId: z.string().min(1)
});

export const GraphChangeSetListResponseSchema = z.object({
  items: z.array(GraphChangeSetRecordSchema),
  count: z.number().int().nonnegative()
});

export const GraphChangeSetAuditLogRecordSchema = z.object({
  id: z.string().min(1),
  frameRecordId: z.string().min(1),
  changeSetId: z.string().min(1),
  eventType: GraphChangeSetAuditLogEventTypeSchema,
  event: z.record(z.string(), z.unknown()),
  createdAt: z.string().datetime()
});

export const GraphChangeSetAuditLogListResponseSchema = z.object({
  items: z.array(GraphChangeSetAuditLogRecordSchema),
  count: z.number().int().nonnegative()
});

export const GraphChangeSetAuditExportArtifactSchema = z.object({
  schemaVersion: z.literal("salesko.change_set.audit_export.v1"),
  exportedAt: z.string().datetime(),
  frameRecordId: z.string().min(1),
  changeSet: GraphChangeSetRecordSchema,
  auditLog: GraphChangeSetAuditLogListResponseSchema
});

export const GraphChangeSetAuditExportResponseSchema = z.object({
  contentType: z.literal("application/json"),
  filename: z.string().min(1),
  artifact: GraphChangeSetAuditExportArtifactSchema
});

export const GraphCsvImportSummarySchema = z.object({
  rowCount: z.number().int().nonnegative(),
  commandCount: z.number().int().positive(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()).default([])
});

export const GraphCsvImportResponseSchema = GraphChangeSetCreateResponseSchema.extend({
  importSummary: GraphCsvImportSummarySchema
});

export const GraphFrameCommitResponseSchema = z.object({
  id: z.string(),
  status: z.enum(["accepted", "needs_review"]),
  acceptedAt: z.string().datetime(),
  nextAction: z.enum(["persist-graph", "review"]),
  version: z.number().int().positive().optional(),
  projectionStatus: GraphProjectionStatusSchema.default("skipped")
});

export const SaleskoGraphVisualizationToolName = "salesko.graph.visualization.create" as const;

export const SaleskoGraphVisualizationToolResultSchema = z.object({
  tool: z.literal(SaleskoGraphVisualizationToolName),
  result: z.object({
    frame: GraphVisualizationFrameSchema,
    commitCandidate: GraphFrameCommitResponseSchema.optional(),
    warnings: z.array(z.string()).default([])
  })
});

export const GraphNoteIntakeCommittedResponseSchema = z.object({
  schemaVersion: z.literal("salesko.note_intake.v1"),
  intakeId: z.string().min(1),
  status: z.enum(["accepted", "needs_review"]),
  queued: z.boolean(),
  frameRecordId: z.string().min(1),
  frameId: z.string().min(1),
  commit: GraphFrameCommitResponseSchema,
  preview: GraphNoteIntakePreviewSchema,
  evidence: GraphNoteEvidenceSummarySchema,
  frame: GraphVisualizationFrameSchema
});

export const GraphNoteIntakeQueuedResponseSchema = z.object({
  schemaVersion: z.literal("salesko.note_intake.v1"),
  intakeId: z.string().min(1),
  status: z.literal("queued"),
  queued: z.literal(true),
  job: GraphNoteIntakeJobRecordSchema,
  preview: GraphNoteIntakePreviewSchema,
  evidence: GraphNoteEvidenceSummarySchema,
  frame: GraphVisualizationFrameSchema
});

export const GraphNoteIntakeResponseSchema = z.union([
  GraphNoteIntakeCommittedResponseSchema,
  GraphNoteIntakeQueuedResponseSchema
]);

export const GraphFrameImportSummarySchema = z.object({
  format: GraphFrameExportFormatSchema,
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()).default([])
});

export const GraphFrameImportResponseSchema = z.object({
  commit: GraphFrameCommitResponseSchema,
  frame: GraphVisualizationFrameSchema,
  importSummary: GraphFrameImportSummarySchema
});

export const GraphFrameDeleteResponseSchema = z.object({
  id: z.string().min(1),
  deletedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative()
});

export const GraphCanonicalEntityEraseResponseSchema = z.object({
  canonicalEntityId: z.string().min(1),
  erasedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative()
});

export const GraphCanonicalEntityUpdateRequestSchema = z
  .object({
    label: z.string().trim().min(1).optional(),
    summary: z.string().trim().min(1).nullable().optional()
  })
  .refine((input) => input.label !== undefined || input.summary !== undefined, {
    message: "Canonical entity update requires label or summary."
  });

export const GraphCanonicalEntityUpdateResponseSchema = z.object({
  canonicalEntityId: z.string().min(1),
  updatedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative(),
  label: z.string().min(1).optional(),
  summary: z.string().nullable().optional()
});

export const GraphCanonicalEntityMergeRequestSchema = z.object({
  targetCanonicalEntityId: z.string().min(1),
  reason: z.string().trim().min(1).optional()
});

export const GraphCanonicalEntityMergeResponseSchema = z.object({
  sourceCanonicalEntityId: z.string().min(1),
  targetCanonicalEntityId: z.string().min(1),
  targetLabel: z.string().min(1),
  mergedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  mergedNodeCount: z.number().int().nonnegative(),
  projectedNodeCount: z.number().int().nonnegative(),
  projectedEdgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative()
});

export const GraphCanonicalEntityRekeyRequestSchema = z.object({
  label: z.string().trim().min(1),
  reason: z.string().trim().min(1).optional()
});

export const GraphCanonicalEntityRekeyResponseSchema = z.object({
  sourceCanonicalEntityId: z.string().min(1),
  targetCanonicalEntityId: z.string().min(1),
  targetLabel: z.string().min(1),
  rekeyedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  rekeyedNodeCount: z.number().int().nonnegative(),
  projectedNodeCount: z.number().int().nonnegative(),
  projectedEdgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative()
});

export const GraphCanonicalEntitySplitNodeRefSchema = z.object({
  frameRecordId: z.string().min(1),
  nodeId: z.string().min(1)
});

export const GraphCanonicalEntitySplitRequestSchema = z.object({
  label: z.string().trim().min(1),
  nodes: z.array(GraphCanonicalEntitySplitNodeRefSchema).min(1).max(200),
  reason: z.string().trim().min(1).optional()
});

export const GraphCanonicalEntitySplitResponseSchema = z.object({
  sourceCanonicalEntityId: z.string().min(1),
  targetCanonicalEntityId: z.string().min(1),
  targetLabel: z.string().min(1),
  splitAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  splitNodeCount: z.number().int().nonnegative(),
  remainingSourceNodeCount: z.number().int().nonnegative(),
  projectedNodeCount: z.number().int().nonnegative(),
  projectedEdgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative(),
  canonicalEntityCount: z.number().int().nonnegative()
});

export const GraphTenantErasureRequestSchema = z.object({
  confirmTenantId: TenantIdSchema,
  privacyExportAcknowledged: z.literal(true),
  erasurePlanAcknowledged: z.literal(true)
});

export const GraphTenantErasureResponseSchema = z.object({
  schemaVersion: z.literal("salesko.tenant_erasure.v1"),
  tenantId: TenantIdSchema,
  erasedAt: z.string().datetime(),
  destructiveActionExecuted: z.literal(true),
  summary: z.object({
    frameCount: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
    canonicalEntityCount: z.number().int().nonnegative(),
    canonicalGraphRowCount: z.number().int().nonnegative().default(0),
    changeSetCount: z.number().int().nonnegative(),
    auditLogCount: z.number().int().nonnegative(),
    agentResearchJobCount: z.number().int().nonnegative().default(0),
    agentResearchResultCount: z.number().int().nonnegative().default(0),
    agentResearchOutboxCount: z.number().int().nonnegative().default(0),
    agentResearchUsageEventCount: z.number().int().nonnegative().default(0),
    agentResearchEventCount: z.number().int().nonnegative().default(0),
    agentResearchErasureRequestCount: z.number().int().nonnegative().default(0)
  })
});

export const GraphRetentionPolicySchema = z.object({
  enabled: z.boolean(),
  retentionDays: z.number().int().positive().optional(),
  cutoffAcceptedBefore: z.string().datetime().optional()
});

export const GraphRetentionPlanFrameSchema = z.object({
  id: z.string().min(1),
  sessionId: z.string().min(1).optional(),
  version: z.number().int().positive().optional(),
  acceptedAt: z.string().datetime(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative()
});

export const GraphRetentionPlanResponseSchema = z.object({
  schemaVersion: z.literal("salesko.retention_plan.v1"),
  generatedAt: z.string().datetime(),
  tenantId: TenantIdSchema,
  mode: z.literal("dry_run"),
  destructiveActionExecuted: z.literal(false),
  policy: GraphRetentionPolicySchema,
  summary: z.object({
    frameCount: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
    canonicalEntityCount: z.number().int().nonnegative(),
    canonicalGraphRowCount: z.number().int().nonnegative().default(0),
    changeSetCount: z.number().int().nonnegative(),
    auditLogCount: z.number().int().nonnegative()
  }),
  frames: z.array(GraphRetentionPlanFrameSchema),
  canonicalEntityIds: z.array(z.string().min(1)),
  warnings: z.array(z.string()).default([])
});

export const GraphRetentionRunRequestSchema = z.object({
  confirmTenantId: TenantIdSchema,
  retentionPlanAcknowledged: z.literal(true)
});

export const GraphRetentionRunResponseSchema = z.object({
  schemaVersion: z.literal("salesko.retention_run.v1"),
  tenantId: TenantIdSchema,
  executedAt: z.string().datetime(),
  destructiveActionExecuted: z.literal(true),
  policy: GraphRetentionPolicySchema,
  summary: z.object({
    frameCount: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
    canonicalEntityCount: z.number().int().nonnegative(),
    canonicalGraphRowCount: z.number().int().nonnegative().default(0),
    changeSetCount: z.number().int().nonnegative(),
    auditLogCount: z.number().int().nonnegative()
  })
});

export const GraphFrameRecordSchema = z.object({
  id: z.string().min(1),
  sessionId: z.string().min(1).optional(),
  message: z.string().min(1),
  frame: GraphVisualizationFrameSchema,
  status: z.enum(["accepted", "needs_review"]),
  nextAction: z.enum(["persist-graph", "review"]),
  version: z.number().int().positive().optional(),
  submittedAt: z.string().datetime().optional(),
  acceptedAt: z.string().datetime()
});

export const GraphFrameRecordV2Schema = z
  .object({
    id: GraphCanonicalRecordIdSchema,
    sessionId: z.string().min(1).optional(),
    message: z.string().min(1),
    frame: GraphVisualizationFrameV2Schema,
    status: z.literal("accepted"),
    nextAction: z.literal("persist-graph"),
    version: z.number().int().positive().optional(),
    submittedAt: z.string().datetime().optional(),
    acceptedAt: z.string().datetime()
  })
  .strict()
  .refine((record) => record.id === record.frame.frameId, {
    message: "A server-owned v2 frame id must equal its authoritative frame record id.",
    path: ["frame", "frameId"]
  });

export const TransactionDetailResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_detail.v1"),
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    frameRecordId: GraphCanonicalRecordIdSchema,
    acceptedAt: z.string().datetime({ offset: true }),
    transaction: GraphTransactionNodeV2Schema
  })
  .strict()
  .superRefine((response, context) => {
    if (
      response.transaction.id !== response.canonicalTransactionId ||
      response.transaction.data.canonicalTransactionId !== response.canonicalTransactionId
    ) {
      context.addIssue({
        code: "custom",
        message: "Transaction detail must project the requested canonical Transaction.",
        path: ["transaction"]
      });
    }
  });

export const TransactionGraphResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_graph.v1"),
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    frameRecord: GraphFrameRecordV2Schema
  })
  .strict()
  .superRefine((response, context) => {
    const matching = response.frameRecord.frame.graph.nodes.filter(
      (node) =>
        node.type === "transaction" &&
        node.id === response.canonicalTransactionId &&
        node.data.canonicalTransactionId === response.canonicalTransactionId
    );
    if (matching.length !== 1) {
      context.addIssue({
        code: "custom",
        message: "A Transaction graph must contain exactly one requested canonical Transaction.",
        path: ["frameRecord", "frame", "graph", "nodes"]
      });
    }
  });

export const ActivitySourceDetailResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.activity_source_detail.v1"),
    entryId: GraphCanonicalRecordIdSchema,
    sourceReferenceHash: SaleskoOpaqueSha256Schema,
    frameRecord: GraphFrameRecordSchema,
    evidenceSpanId: GraphCanonicalRecordIdSchema,
    quote: z.string().min(1).max(4_000),
    nodeId: GraphCanonicalRecordIdSchema.optional()
  })
  .strict()
  .superRefine((response, context) => {
    if (response.nodeId === undefined) {
      return;
    }
    const matchingNodes = response.frameRecord.frame.graph.nodes.filter(
      (node) => node.id === response.nodeId && node.evidence === response.quote
    );
    if (matchingNodes.length !== 1) {
      context.addIssue({
        code: "custom",
        message: "An Activity source node must resolve exactly once with the exact evidence quote in its frame.",
        path: ["nodeId"]
      });
    }
  });

export const TransactionCommandOperationSchema = z.enum(["record", "status_change", "correction"]);
export const TransactionCommandOutcomeSchema = z.enum(["created", "idempotent", "provenance_attached"]);

export const TransactionCommandResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.transaction_command.v1"),
    operation: TransactionCommandOperationSchema,
    outcome: TransactionCommandOutcomeSchema,
    commandId: GraphCanonicalRecordIdSchema,
    canonicalTransactionId: GraphCanonicalRecordIdSchema,
    semanticFingerprint: TransactionSemanticFingerprintSchema,
    replacedCanonicalTransactionId: GraphCanonicalRecordIdSchema.optional(),
    frameRecord: GraphFrameRecordV2Schema,
    activity: ActivityFeedEntrySchema
  })
  .strict()
  .superRefine((response, context) => {
    if (response.activity.subjectType !== "transaction") {
      context.addIssue({
        code: "custom",
        message: "A Transaction command must return a Transaction Activity entry.",
        path: ["activity", "subjectType"]
      });
    }
    if (response.activity.subjectId !== response.canonicalTransactionId) {
      context.addIssue({
        code: "custom",
        message: "The returned Activity subject must equal the current canonical Transaction id.",
        path: ["activity", "subjectId"]
      });
    }
    const matchingTransactionNodes = response.frameRecord.frame.graph.nodes.filter(
      (node) =>
        node.type === "transaction" &&
        node.id === response.canonicalTransactionId &&
        node.data.canonicalTransactionId === response.canonicalTransactionId
    );
    if (matchingTransactionNodes.length !== 1) {
      context.addIssue({
        code: "custom",
        message: "A Transaction command response must resolve exactly one matching Transaction node in its frame.",
        path: ["frameRecord", "frame", "graph", "nodes"]
      });
    } else {
      const matchingTransactionNode = matchingTransactionNodes[0]!;
      if (matchingTransactionNode.type === "transaction") {
        if (matchingTransactionNode.data.semanticFingerprint !== response.semanticFingerprint) {
          context.addIssue({
            code: "custom",
            message: "The response semantic fingerprint must match its canonical Transaction node.",
            path: ["semanticFingerprint"]
          });
        }
        if (matchingTransactionNode.sourceFact.sourceFactKey !== response.activity.sourceFactKey) {
          context.addIssue({
            code: "custom",
            message: "The returned Activity source fact key must match its canonical Transaction node.",
            path: ["activity", "sourceFactKey"]
          });
        }
      }
    }
    const expectedEntryType =
      response.operation === "record"
        ? "transaction.recorded"
        : response.operation === "status_change"
          ? "transaction.status_changed"
          : "transaction.corrected";
    if (response.activity.entryType !== expectedEntryType) {
      context.addIssue({
        code: "custom",
        message: "The returned Activity entry type must match the Transaction command operation.",
        path: ["activity", "entryType"]
      });
    }
    if (
      response.replacedCanonicalTransactionId !== undefined &&
      (response.operation !== "correction" || response.replacedCanonicalTransactionId === response.canonicalTransactionId)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only an identity-changing correction may name a distinct replaced Transaction.",
        path: ["replacedCanonicalTransactionId"]
      });
    }
  });

export const ActivityBackfillResponseSchema = z
  .object({
    schemaVersion: z.literal("salesko.activity_backfill_result.v1"),
    outcome: TransactionCommandOutcomeSchema,
    commandId: GraphCanonicalRecordIdSchema,
    originalRecordedAt: z.string().datetime({ offset: true }),
    frameRecords: z.array(GraphFrameRecordV2Schema).max(200),
    items: z.array(ActivityFeedEntrySchema).min(1).max(200),
    count: z.number().int().positive()
  })
  .strict()
  .superRefine((response, context) => {
    if (response.count !== response.items.length) {
      context.addIssue({
        code: "custom",
        message: "Activity backfill count must equal items.length.",
        path: ["count"]
      });
    }
    if (!allUnique(response.items.map((item) => item.id))) {
      context.addIssue({
        code: "custom",
        message: "Activity backfill result ids must be unique.",
        path: ["items"]
      });
    }
    if (!allUnique(response.frameRecords.map((record) => record.id))) {
      context.addIssue({
        code: "custom",
        message: "Activity backfill frame record ids must be unique.",
        path: ["frameRecords"]
      });
    }
    if (response.items.some((item) => item.subjectType === "transaction") && response.frameRecords.length === 0) {
      context.addIssue({
        code: "custom",
        message: "A historical Transaction backfill must return its server-built v2 frame record.",
        path: ["frameRecords"]
      });
    }
    const transactionItems = response.items.filter((item) => item.subjectType === "transaction");
    transactionItems.forEach((item, itemIndex) => {
      const matchingNodes = response.frameRecords.flatMap((record, frameIndex) =>
        record.frame.graph.nodes.flatMap((node, nodeIndex) =>
          node.type === "transaction" &&
          node.id === item.subjectId &&
          node.data.canonicalTransactionId === item.subjectId &&
          node.sourceFact.sourceFactKey === item.sourceFactKey &&
          node.sourceFact.backfilled
            ? [{ frameIndex, nodeIndex }]
            : []
        )
      );
      if (matchingNodes.length !== 1) {
        context.addIssue({
          code: "custom",
          message:
            "Each historical Transaction Activity item must resolve exactly one matching Transaction node and source fact in the returned v2 frames.",
          path: ["items", itemIndex]
        });
      }
    });
    response.frameRecords.forEach((record, frameIndex) => {
      if (record.frame.source !== "server-backfill") {
        context.addIssue({
          code: "custom",
          message: "A historical Transaction backfill may return only server-backfill v2 frames.",
          path: ["frameRecords", frameIndex, "frame", "source"]
        });
      }
      const transactionNodes = record.frame.graph.nodes.filter((node) => node.type === "transaction");
      const itemReferenceCounts = transactionNodes.map(
        (node) =>
          transactionItems.filter(
          (item) =>
            item.subjectId === node.id &&
            item.subjectId === node.data.canonicalTransactionId &&
            item.sourceFactKey === node.sourceFact.sourceFactKey &&
            node.sourceFact.backfilled
          ).length
      );
      if (transactionNodes.length === 0 || itemReferenceCounts.some((count) => count !== 1)) {
        context.addIssue({
          code: "custom",
          message:
            "Every returned historical Transaction node must be bound to exactly one Transaction Activity item.",
          path: ["frameRecords", frameIndex]
        });
      }
    });
    response.items.forEach((item, itemIndex) => {
      if (!item.backfilled || item.recordedAt !== response.originalRecordedAt) {
        context.addIssue({
          code: "custom",
          message: "Backfilled Activity entries must retain the command's original recorded time.",
          path: ["items", itemIndex, "recordedAt"]
        });
      }
    });
  });

export const GraphFrameShareReadResponseSchema = z.object({
  record: GraphFrameRecordSchema,
  expiresAt: z.string().datetime()
});

export const GraphChangeSetApplyResponseSchema = z.object({
  changeSet: GraphChangeSetRecordSchema,
  auditLogId: z.string().min(1),
  projectionStatus: GraphProjectionStatusSchema,
  frame: GraphFrameRecordSchema
});

export const GraphChangeSetRevertResponseSchema = z.object({
  changeSet: GraphChangeSetRecordSchema,
  auditLogId: z.string().min(1),
  projectionStatus: GraphProjectionStatusSchema,
  frame: GraphFrameRecordSchema
});

export const GraphFrameListItemSchema = GraphFrameRecordSchema.pick({
  id: true,
  sessionId: true,
  status: true,
  nextAction: true,
  version: true,
  submittedAt: true,
  acceptedAt: true
}).extend({
  schemaVersion: z.literal("graph.visualization.v1"),
  summary: z.string(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  selectedNodeId: z.string().min(1).optional()
});

export const GraphFrameListResponseSchema = z.object({
  items: z.array(GraphFrameListItemSchema),
  count: z.number().int().nonnegative()
});

export const GraphReadNodeSchema = z.object({
  id: z.string().min(1),
  canonicalEntityId: z.string().min(1).optional(),
  frameRecordId: z.string().min(1),
  frameId: z.string().min(1),
  entityType: GraphEntityTypeSchema,
  label: z.string().min(1),
  summary: z.string().optional(),
  confidence: z.number().min(0).max(1),
  evidence: z.string().min(1).optional(),
  occurredAt: z.string().optional(),
  data: z.record(z.string(), z.unknown()).default({}),
  acceptedAt: z.string().datetime()
});

export const GraphReadEdgeSchema = z.object({
  id: z.string().min(1),
  frameRecordId: z.string().min(1),
  frameId: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  relationType: GraphRelationTypeSchema,
  label: z.string().min(1),
  confidence: z.number().min(0).max(1),
  evidence: z.string().min(1).optional(),
  strength: z.number().min(0).max(1),
  data: z.record(z.string(), z.unknown()).default({}),
  acceptedAt: z.string().datetime()
});

export const GraphReadNodeV2Schema = GraphReadNodeSchema.omit({ entityType: true })
  .extend({ entityType: GraphEntityTypeV2Schema })
  .strict();

export const GraphReadEdgeV2Schema = GraphReadEdgeSchema.omit({ relationType: true })
  .extend({ relationType: GraphRelationTypeV2Schema })
  .strict();

export const GraphProjectionResponseSchema = z.object({
  frameRecordId: z.string().min(1),
  projectionStatus: GraphProjectionStatusSchema,
  nodes: z.array(GraphReadNodeSchema),
  edges: z.array(GraphReadEdgeSchema)
});

export const GraphSearchResponseSchema = z.object({
  items: z.array(GraphReadNodeSchema),
  count: z.number().int().nonnegative()
});

export const GraphOpportunityStatusSchema = InsightStatusSchema;

export const GraphOpportunityFeedItemSchema = z.object({
  id: GraphCanonicalRecordIdSchema,
  title: z.string().min(1).max(300),
  summary: z.string().min(1).max(4_000),
  score: z.number().min(0).max(1),
  status: GraphOpportunityStatusSchema,
  primaryEntityId: GraphCanonicalRecordIdSchema.nullable().optional(),
  primaryEdgeId: GraphCanonicalRecordIdSchema.nullable().optional(),
  entityIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  edgeIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  evidenceSpanIds: z.array(GraphCanonicalRecordIdSchema).default([]),
  generatedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  data: z.record(z.string(), z.unknown()).default({})
});

export const GraphOpportunityFeedResponseSchema = z.object({
  schemaVersion: z.literal("salesko.opportunities.v1"),
  items: z.array(GraphOpportunityFeedItemSchema),
  count: z.number().int().nonnegative()
});

export const GraphOpportunityActionRequestSchema = z.object({
  action: z.enum(["dismiss", "save", "follow_up"])
});

export const GraphOpportunityActionResponseSchema = z.object({
  schemaVersion: z.literal("salesko.opportunity_action.v1"),
  action: GraphOpportunityActionRequestSchema.shape.action,
  opportunity: GraphOpportunityFeedItemSchema
});

export const GraphOpportunityActionProposalRequestSchema = GraphOpportunityActionRequestSchema.extend({
  reason: z.string().min(1).max(500).optional(),
  clientMutationId: z.string().min(1).max(128).optional()
});

export const GraphOpportunityActionProposalResponseSchema = z.object({
  schemaVersion: z.literal("salesko.opportunity_action_proposal.v1"),
  opportunityId: GraphCanonicalRecordIdSchema,
  action: GraphOpportunityActionRequestSchema.shape.action,
  frameRecordId: z.string().min(1),
  changeSet: GraphChangeSetRecordSchema,
  auditLogId: z.string().min(1),
  applied: z.literal(false),
  nextAction: z.string().min(1)
});

export const GraphNeighborsResponseSchema = z.object({
  node: GraphReadNodeSchema.optional(),
  neighbors: z.array(GraphReadNodeSchema),
  edges: z.array(GraphReadEdgeSchema),
  count: z.number().int().nonnegative()
});

export const GraphRelationshipPathKindSchema = z.enum(["direct", "common_neighbor"]);

export const GraphRelationshipPathSchema = z.object({
  kind: GraphRelationshipPathKindSchema,
  nodes: z.array(GraphReadNodeSchema).min(2).max(3),
  edges: z.array(GraphReadEdgeSchema).min(1).max(2),
  occurrenceCount: z.number().int().positive().optional(),
  score: z.number().min(0).max(1)
});

export const GraphRelationshipPathsResponseSchema = z.object({
  sourceNode: GraphReadNodeSchema.optional(),
  targetNode: GraphReadNodeSchema.optional(),
  commonNeighbors: z.array(GraphReadNodeSchema),
  paths: z.array(GraphRelationshipPathSchema),
  count: z.number().int().nonnegative()
});

export const GraphRelationshipReminderSchema = z.object({
  edge: GraphReadEdgeSchema,
  sourceNode: GraphReadNodeSchema.optional(),
  targetNode: GraphReadNodeSchema.optional(),
  lastSeenAt: z.string().datetime(),
  occurrenceCount: z.number().int().positive().optional(),
  staleAfterDays: z.number().int().positive(),
  staleByDays: z.number().int().nonnegative(),
  score: z.number().min(0).max(1)
});

export const GraphRelationshipRemindersResponseSchema = z.object({
  asOf: z.string().datetime(),
  staleAfterDays: z.number().int().positive(),
  items: z.array(GraphRelationshipReminderSchema),
  count: z.number().int().nonnegative()
});

export const GraphCanonicalEntitySchema = z.object({
  id: z.string().min(1),
  entityType: GraphEntityTypeSchema,
  label: z.string().min(1),
  summary: z.string().nullable().optional(),
  confidence: z.number().min(0).max(1),
  data: z.record(z.string(), z.unknown()).default({}),
  latestFrameRecordId: z.string().min(1),
  latestNodeId: z.string().min(1),
  lastSeenAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  nodeCount: z.number().int().nonnegative(),
  frameRecordCount: z.number().int().nonnegative()
});

export const GraphCanonicalEntityListResponseSchema = z.object({
  items: z.array(GraphCanonicalEntitySchema),
  count: z.number().int().nonnegative()
});

export const GraphCanonicalEntityDetailResponseSchema = z.object({
  entity: GraphCanonicalEntitySchema,
  nodes: z.array(GraphReadNodeSchema),
  count: z.number().int().nonnegative()
});

export const GraphPrivacyExportCanonicalEntitySchema = z.object({
  id: z.string().min(1),
  entityType: GraphEntityTypeSchema,
  label: z.string().min(1),
  nodeIds: z.array(z.string().min(1)),
  frameRecordIds: z.array(z.string().min(1)),
  lastSeenAt: z.string().datetime()
});

export const GraphPrivacyExportCanonicalEntityV2Schema = GraphPrivacyExportCanonicalEntitySchema.omit({
  entityType: true
})
  .extend({ entityType: GraphEntityTypeV2Schema })
  .strict();

export const GraphPrivacyExportChangeSetSchema = GraphChangeSetRecordSchema.extend({
  commands: z.array(GraphMutationCommandSchema),
  inverseOps: z.array(z.unknown()).default([])
});

export const GraphPrivacyExportCanonicalGraphRowsSchema = z.object({
  noteDocuments: z.array(z.record(z.string(), z.unknown())).default([]),
  evidenceSpans: z.array(z.record(z.string(), z.unknown())).default([]),
  entities: z.array(z.record(z.string(), z.unknown())).default([]),
  entityAliases: z.array(z.record(z.string(), z.unknown())).default([]),
  edges: z.array(z.record(z.string(), z.unknown())).default([]),
  edgeEvidence: z.array(z.record(z.string(), z.unknown())).default([]),
  graphJournal: z.array(z.record(z.string(), z.unknown())).default([]),
  insights: z.array(z.record(z.string(), z.unknown())).default([])
});

export const GraphPrivacyExportAgentResearchOutboxSchema = z.object({
  outboxId: z.string().min(1),
  jobId: z.string().min(1),
  eventType: z.enum(["dispatch", "cancel", "erase"]),
  dedupeKey: z.string().min(1),
  status: z.enum(["pending", "leased", "dispatched", "dead"]),
  availableAt: z.string().datetime(),
  leaseOwner: z.string().min(1).optional(),
  leaseExpiresAt: z.string().datetime().optional(),
  attemptCount: z.number().int().nonnegative(),
  lastErrorCode: z.string().min(1).optional(),
  createdAt: z.string().datetime(),
  dispatchedAt: z.string().datetime().optional()
});

export const GraphPrivacyExportAgentResearchEventSchema = z.object({
  eventId: z.string().min(1),
  jobId: z.string().min(1),
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  eventType: z.string().min(1).max(160),
  actorType: z.enum(["user", "runtime", "system"]),
  actorRefHash: z.string().min(1).optional(),
  idempotencyKey: z.string().min(1),
  metadata: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.string().datetime()
});

export const GraphPrivacyExportAgentResearchErasureRequestSchema = z.object({
  erasureId: z.string().min(1),
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  tenantId: TenantIdSchema,
  subjectType: z.enum(["tenant", "user", "job"]),
  subjectId: z.string().min(1),
  runtimeProvider: z.string().min(1),
  state: z.enum([
    "requested",
    "tombstoned",
    "runtime_cancel_requested",
    "runtime_stopped",
    "purging",
    "completed",
    "failed"
  ]),
  attemptCount: z.number().int().nonnegative(),
  nextAttemptAt: z.string().datetime().optional(),
  lastErrorCode: z.string().min(1).optional(),
  requestedAt: z.string().datetime(),
  completedAt: z.string().datetime().optional()
});

export const GraphPrivacyExportAgentResearchSchema = z
  .object({
    jobs: z.array(z.lazy((): z.ZodType<AgentResearchJob> => AgentResearchJobSchema)),
    results: z.array(z.lazy((): z.ZodType<AgentResearchResult> => AgentResearchResultSchema)),
    outbox: z.array(GraphPrivacyExportAgentResearchOutboxSchema),
    usageEvents: z.array(z.lazy((): z.ZodType<AgentResearchUsageEvent> => AgentResearchUsageEventSchema)),
    events: z.array(GraphPrivacyExportAgentResearchEventSchema),
    erasureRequests: z.array(GraphPrivacyExportAgentResearchErasureRequestSchema)
  })
  .default({
    jobs: [],
    results: [],
    outbox: [],
    usageEvents: [],
    events: [],
    erasureRequests: []
  });

export const GraphCanonicalShadowParityTableSchema = z.enum([
  "noteDocuments",
  "evidenceSpans",
  "entities",
  "entityAliases",
  "edges",
  "edgeEvidence",
  "graphJournal",
  "insights"
]);

export const GraphCanonicalShadowParityTableReportSchema = z.object({
  table: GraphCanonicalShadowParityTableSchema,
  status: z.enum(["pass", "expected_diff", "warning", "blocker"]).default("pass"),
  expectedRowCount: z.number().int().nonnegative(),
  actualRowCount: z.number().int().nonnegative(),
  missingRowCount: z.number().int().nonnegative(),
  extraRowCount: z.number().int().nonnegative(),
  expectedDiffCount: z.number().int().nonnegative().default(0),
  warningCount: z.number().int().nonnegative().default(0),
  blockerCount: z.number().int().nonnegative().default(0),
  sampleMissingKeys: z.array(z.string()).default([]),
  sampleExtraKeys: z.array(z.string()).default([])
});

export const GraphCanonicalShadowParityMismatchSchema = z.object({
  kind: z.string().min(1).max(120),
  table: GraphCanonicalShadowParityTableSchema,
  expectedKey: z.string().min(1).nullable().optional(),
  actualKey: z.string().min(1).nullable().optional(),
  reason: z.string().min(1).max(500),
  severity: z.enum(["expected_diff", "warning", "blocker"])
});

export const GraphCanonicalShadowParityMismatchGroupSchema = z.object({
  table: GraphCanonicalShadowParityTableSchema,
  kind: z.string().min(1).max(120),
  reason: z.string().min(1).max(500),
  severity: z.enum(["expected_diff", "warning", "blocker"]),
  count: z.number().int().nonnegative(),
  sample: z.array(GraphCanonicalShadowParityMismatchSchema).default([])
});

export const GraphCanonicalShadowParityResponseSchema = z.object({
  schemaVersion: z.literal("salesko.canonical_shadow_parity.v1"),
  tenantId: TenantIdSchema,
  datasetScope: GraphDatasetScopeSchema.default("salesko"),
  generatedAt: z.string().datetime(),
  checkedAt: z.string().datetime(),
  frameCount: z.number().int().nonnegative(),
  status: z.enum(["pass", "expected_diff", "warning", "blocker"]),
  summary: z.object({
    expectedCanonicalGraphRowCount: z.number().int().nonnegative(),
    actualCanonicalGraphRowCount: z.number().int().nonnegative(),
    missingRowCount: z.number().int().nonnegative(),
    extraRowCount: z.number().int().nonnegative(),
    expectedDiffCount: z.number().int().nonnegative().default(0),
    warningCount: z.number().int().nonnegative().default(0),
    blockerCount: z.number().int().nonnegative().default(0),
    mismatchedTableCount: z.number().int().nonnegative()
  }),
  tables: z.array(GraphCanonicalShadowParityTableReportSchema),
  tableReports: z.array(GraphCanonicalShadowParityTableReportSchema),
  mismatchGroups: z.array(GraphCanonicalShadowParityMismatchGroupSchema).default([]),
  blockingReasons: z.array(z.string().min(1).max(500)).default([]),
  warnings: z.array(z.string()).default([])
});

export const GraphPrivacyExportArtifactSchema = z.object({
  schemaVersion: z.literal("salesko.privacy_export.v1"),
  exportedAt: z.string().datetime(),
  tenantId: TenantIdSchema,
  summary: z.object({
    frameCount: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
    canonicalEntityCount: z.number().int().nonnegative(),
    canonicalGraphRowCount: z.number().int().nonnegative().default(0),
    changeSetCount: z.number().int().nonnegative(),
    auditLogCount: z.number().int().nonnegative(),
    agentResearchJobCount: z.number().int().nonnegative().default(0),
    agentResearchResultCount: z.number().int().nonnegative().default(0),
    agentResearchOutboxCount: z.number().int().nonnegative().default(0),
    agentResearchUsageEventCount: z.number().int().nonnegative().default(0),
    agentResearchEventCount: z.number().int().nonnegative().default(0),
    agentResearchErasureRequestCount: z.number().int().nonnegative().default(0)
  }),
  frames: z.array(GraphFrameRecordSchema),
  projectedNodes: z.array(GraphReadNodeSchema),
  projectedEdges: z.array(GraphReadEdgeSchema),
  canonicalEntities: z.array(GraphPrivacyExportCanonicalEntitySchema),
  canonicalGraph: GraphPrivacyExportCanonicalGraphRowsSchema.default({
    noteDocuments: [],
    evidenceSpans: [],
    entities: [],
    entityAliases: [],
    edges: [],
    edgeEvidence: [],
    graphJournal: [],
    insights: []
  }),
  changeSets: z.array(GraphPrivacyExportChangeSetSchema),
  auditLog: z.array(GraphChangeSetAuditLogRecordSchema),
  agentResearch: GraphPrivacyExportAgentResearchSchema
});

export const GraphPrivacyExportResponseSchema = z.object({
  contentType: z.literal("application/json"),
  filename: z.string().min(1),
  artifact: GraphPrivacyExportArtifactSchema
});

export const TransactionActivityPrivacySummarySchema = z
  .object({
    transactionFrameCount: z.number().int().nonnegative(),
    transactionAuthorityCount: z.number().int().nonnegative(),
    transactionAuthoritySourceCount: z.number().int().nonnegative(),
    transactionRegistryCount: z.number().int().nonnegative(),
    transactionSourceCount: z.number().int().nonnegative(),
    activityJournalEntryCount: z.number().int().nonnegative(),
    activitySourceCount: z.number().int().nonnegative()
  })
  .strict();

export const TransactionActivityPrivacyRowsSchema = z
  .object({
    authorities: z.array(TransactionAuthorityRegistryRecordSchema),
    authoritySources: z.array(TransactionAuthoritySourceRecordSchema),
    registry: z.array(TransactionRegistryRecordSchema),
    transactionSources: z.array(TransactionSourceRecordSchema),
    journalEntries: z.array(ActivityJournalEntrySchema),
    journalSources: z.array(ActivityJournalSourceRecordSchema)
  })
  .strict()
  .superRefine((rows, context) => {
    const ensureUnique = (values: string[], path: string, message: string) => {
      if (!allUnique(values)) {
        context.addIssue({ code: "custom", message, path: [path] });
      }
    };
    ensureUnique(
      rows.authorities.map((record) => record.authorityKey),
      "authorities",
      "Transaction Authority ids must be unique in a privacy artifact."
    );
    ensureUnique(
      rows.authorities.map((record) => `${record.tenantId}\u0000${record.organisationCanonicalEntityId}`),
      "authorities",
      "One reviewed organisation may resolve to only one Transaction Authority per tenant."
    );
    ensureUnique(
      rows.authoritySources.map((record) => record.id),
      "authoritySources",
      "Transaction Authority source ids must be unique in a privacy artifact."
    );
    ensureUnique(
      rows.registry.map((record) => record.canonicalTransactionId),
      "registry",
      "Transaction registry ids must be unique in a privacy artifact."
    );
    ensureUnique(
      rows.transactionSources.map((record) => record.id),
      "transactionSources",
      "Transaction source ids must be unique in a privacy artifact."
    );
    ensureUnique(
      rows.journalEntries.map((record) => record.id),
      "journalEntries",
      "Activity Journal ids must be unique in a privacy artifact."
    );
    ensureUnique(
      rows.journalSources.map((record) => record.id),
      "journalSources",
      "Activity source ids must be unique in a privacy artifact."
    );

    const authorityIds = new Set(rows.authorities.map((record) => record.authorityKey));
    const authoritySourceById = new Map(rows.authoritySources.map((record) => [record.id, record]));
    const registryById = new Map(rows.registry.map((record) => [record.canonicalTransactionId, record]));
    const transactionSourceById = new Map(rows.transactionSources.map((record) => [record.id, record]));
    const journalById = new Map(rows.journalEntries.map((record) => [record.id, record]));

    rows.authoritySources.forEach((record, recordIndex) => {
      if (!authorityIds.has(record.authorityKey)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction Authority source must resolve inside the privacy artifact.",
          path: ["authoritySources", recordIndex, "authorityKey"]
        });
      }
    });
    rows.registry.forEach((record, recordIndex) => {
      if (!authorityIds.has(record.authorityKey)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction registry record must resolve its Authority inside the privacy artifact.",
          path: ["registry", recordIndex, "authorityKey"]
        });
      }
      if (
        record.supersededByTransactionId !== undefined &&
        (!registryById.has(record.supersededByTransactionId) ||
          record.supersededByTransactionId === record.canonicalTransactionId)
      ) {
        context.addIssue({
          code: "custom",
          message: "A superseded Transaction must point to a distinct replacement in the same privacy unit.",
          path: ["registry", recordIndex, "supersededByTransactionId"]
        });
      }
      const currentJournal = journalById.get(record.currentJournalEntryId);
      if (
        !currentJournal ||
        currentJournal.subjectType !== "transaction" ||
        currentJournal.subjectId !== record.canonicalTransactionId ||
        currentJournal.semanticFingerprint !== record.currentSemanticFingerprint
      ) {
        context.addIssue({
          code: "custom",
          message: "A Transaction registry current Journal pointer must resolve its current semantic state.",
          path: ["registry", recordIndex, "currentJournalEntryId"]
        });
      }
      if (record.supersedesTransactionId !== undefined) {
        const predecessor = registryById.get(record.supersedesTransactionId);
        if (!predecessor || predecessor.supersededByTransactionId !== record.canonicalTransactionId) {
          context.addIssue({
            code: "custom",
            message: "A replacement Transaction must resolve a reciprocal predecessor inside the privacy unit.",
            path: ["registry", recordIndex, "supersedesTransactionId"]
          });
        }
      }
      if (record.supersededByTransactionId !== undefined) {
        const successor = registryById.get(record.supersededByTransactionId);
        if (!successor || successor.supersedesTransactionId !== record.canonicalTransactionId) {
          context.addIssue({
            code: "custom",
            message: "A superseded Transaction must resolve a reciprocal replacement inside the privacy unit.",
            path: ["registry", recordIndex, "supersededByTransactionId"]
          });
        }
      }
    });
    rows.transactionSources.forEach((record, recordIndex) => {
      if (!registryById.has(record.canonicalTransactionId)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction source must resolve its registry record inside the privacy artifact.",
          path: ["transactionSources", recordIndex, "canonicalTransactionId"]
        });
      }
      if (!authoritySourceById.has(record.authoritySourceId)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction source must resolve its Authority source inside the privacy artifact.",
          path: ["transactionSources", recordIndex, "authoritySourceId"]
        });
      } else {
        const registry = registryById.get(record.canonicalTransactionId);
        const authoritySource = authoritySourceById.get(record.authoritySourceId);
        if (registry && authoritySource?.authorityKey !== registry.authorityKey) {
          context.addIssue({
            code: "custom",
            message: "A Transaction source Authority must match the Transaction registry Authority.",
            path: ["transactionSources", recordIndex, "authoritySourceId"]
          });
        }
      }
    });
    rows.journalSources.forEach((record, recordIndex) => {
      const journal = journalById.get(record.journalEntryId);
      if (!journal) {
        context.addIssue({
          code: "custom",
          message: "Every Activity source must resolve its Journal entry inside the privacy artifact.",
          path: ["journalSources", recordIndex, "journalEntryId"]
        });
        return;
      }
      const isTransaction = journal.subjectType === "transaction";
      if (isTransaction !== (record.transactionSourceId !== undefined)) {
        context.addIssue({
          code: "custom",
          message: "Only Transaction Journal sources require an exact Transaction source link.",
          path: ["journalSources", recordIndex, "transactionSourceId"]
        });
      }
      if (record.transactionSourceId !== undefined) {
        const transactionSource = transactionSourceById.get(record.transactionSourceId);
        if (!transactionSource || transactionSource.canonicalTransactionId !== journal.subjectId) {
          context.addIssue({
            code: "custom",
            message: "A Transaction Journal source must resolve an exact source for its Transaction subject.",
            path: ["journalSources", recordIndex, "transactionSourceId"]
          });
        } else if (
          record.datasetScope !== transactionSource.datasetScope ||
          record.tenantId !== transactionSource.tenantId ||
          record.sourceFrameRecordId !== transactionSource.sourceFrameRecordId ||
          record.sourceReferenceHash !== transactionSource.sourceReferenceHash ||
          record.evidenceSpanId !== transactionSource.evidenceSpanId ||
          record.evidenceState !== transactionSource.evidenceState ||
          record.qualifyingAuthority !== transactionSource.qualifyingAuthority
        ) {
          context.addIssue({
            code: "custom",
            message: "A Transaction Journal source must exactly match its linked Transaction provenance tuple.",
            path: ["journalSources", recordIndex, "transactionSourceId"]
          });
        }
      }
    });
    rows.journalEntries.forEach((record, recordIndex) => {
      if (
        !rows.journalSources.some(
          (source) => source.journalEntryId === record.id && source.qualifyingAuthority
        )
      ) {
        context.addIssue({
          code: "custom",
          message: "Every exported Activity Journal entry requires at least one qualifying source row.",
          path: ["journalEntries", recordIndex]
        });
      }
      if (record.supersedesEntryId !== undefined && !journalById.has(record.supersedesEntryId)) {
        context.addIssue({
          code: "custom",
          message: "A correction privacy unit cannot contain a dangling superseded Activity entry.",
          path: ["journalEntries", recordIndex, "supersedesEntryId"]
        });
      }
      if (record.subjectType === "transaction" && !registryById.has(record.subjectId)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction Journal subject must resolve inside the privacy unit.",
          path: ["journalEntries", recordIndex, "subjectId"]
        });
      }
    });
  });

export const GraphPrivacyExportArtifactV2Schema = GraphPrivacyExportArtifactSchema.omit({
  schemaVersion: true,
  summary: true,
  frames: true,
  projectedNodes: true,
  projectedEdges: true,
  canonicalEntities: true
})
  .extend({
    schemaVersion: z.literal("salesko.privacy_export.v2"),
    summary: GraphPrivacyExportArtifactSchema.shape.summary
      .extend(TransactionActivityPrivacySummarySchema.shape)
      .strict(),
    frames: z.array(GraphFrameRecordSchema),
    transactionFrames: z.array(GraphFrameRecordV2Schema),
    projectedNodes: z.array(GraphReadNodeSchema),
    projectedEdges: z.array(GraphReadEdgeSchema),
    projectedNodesV2: z.array(GraphReadNodeV2Schema),
    projectedEdgesV2: z.array(GraphReadEdgeV2Schema),
    canonicalEntities: z.array(GraphPrivacyExportCanonicalEntityV2Schema),
    transactionActivity: TransactionActivityPrivacyRowsSchema
  })
  .strict()
  .superRefine((artifact, context) => {
    const expectedCounts: Array<[keyof typeof artifact.summary, number]> = [
      ["frameCount", artifact.frames.length + artifact.transactionFrames.length],
      ["transactionFrameCount", artifact.transactionFrames.length],
      ["nodeCount", artifact.projectedNodes.length + artifact.projectedNodesV2.length],
      ["edgeCount", artifact.projectedEdges.length + artifact.projectedEdgesV2.length],
      ["canonicalEntityCount", artifact.canonicalEntities.length],
      ["transactionAuthorityCount", artifact.transactionActivity.authorities.length],
      ["transactionAuthoritySourceCount", artifact.transactionActivity.authoritySources.length],
      ["transactionRegistryCount", artifact.transactionActivity.registry.length],
      ["transactionSourceCount", artifact.transactionActivity.transactionSources.length],
      ["activityJournalEntryCount", artifact.transactionActivity.journalEntries.length],
      ["activitySourceCount", artifact.transactionActivity.journalSources.length]
    ];
    expectedCounts.forEach(([field, expected]) => {
      if (artifact.summary[field] !== expected) {
        context.addIssue({
          code: "custom",
          message: `Privacy export ${field} must equal the exported row count.`,
          path: ["summary", field]
        });
      }
    });

    const allFrameIds = new Set([
      ...artifact.frames.map((record) => record.id),
      ...artifact.transactionFrames.map((record) => record.id)
    ]);
    const transactionFrameIds = new Set(artifact.transactionFrames.map((record) => record.id));
    const validateAvailableEvidenceSpan = (
      source: {
        datasetScope: string;
        tenantId: string;
        sourceFrameRecordId: string;
        evidenceSpanId?: string | undefined;
        evidenceState: string;
      },
      path: Array<string | number>
    ) => {
      if (source.evidenceState !== "available" || source.evidenceSpanId === undefined) {
        return;
      }
      const matchingEvidenceSpans = artifact.canonicalGraph.evidenceSpans.filter(
        (row) => row.id === source.evidenceSpanId
      );
      if (matchingEvidenceSpans.length !== 1) {
        context.addIssue({
          code: "custom",
          message: "An available Transaction Activity source must resolve exactly one canonical evidence span.",
          path
        });
        return;
      }
      const evidenceSpan = matchingEvidenceSpans[0]!;
      const evidenceData = evidenceSpan.data_json;
      const evidenceFrameRecordId =
        evidenceData && typeof evidenceData === "object" && !Array.isArray(evidenceData)
          ? (evidenceData as Record<string, unknown>).frameRecordId
          : undefined;
      if (
        evidenceSpan.dataset_scope !== source.datasetScope ||
        evidenceSpan.tenant_id !== source.tenantId ||
        evidenceSpan.source_id !== source.sourceFrameRecordId ||
        evidenceFrameRecordId !== source.sourceFrameRecordId
      ) {
        context.addIssue({
          code: "custom",
          message: "A canonical evidence span must belong to the source tenant, dataset, and exact source frame.",
          path
        });
      }
    };
    const allTenantRows = [
      ...artifact.transactionActivity.authorities,
      ...artifact.transactionActivity.authoritySources,
      ...artifact.transactionActivity.registry,
      ...artifact.transactionActivity.transactionSources,
      ...artifact.transactionActivity.journalEntries,
      ...artifact.transactionActivity.journalSources
    ];
    allTenantRows.forEach((record, recordIndex) => {
      if (record.tenantId !== artifact.tenantId) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction Activity privacy row must belong to the exported tenant.",
          path: ["transactionActivity", recordIndex, "tenantId"]
        });
      }
    });
    artifact.transactionActivity.registry.forEach((record, recordIndex) => {
      if (!transactionFrameIds.has(record.currentFrameRecordId)) {
        context.addIssue({
          code: "custom",
          message: "A current Transaction registry frame must be exported as a v2 Transaction frame.",
          path: ["transactionActivity", "registry", recordIndex, "currentFrameRecordId"]
        });
      }
    });
    artifact.transactionActivity.transactionSources.forEach((record, recordIndex) => {
      validateAvailableEvidenceSpan(record, [
        "transactionActivity",
        "transactionSources",
        recordIndex,
        "evidenceSpanId"
      ]);
      if (!allFrameIds.has(record.sourceFrameRecordId)) {
        context.addIssue({
          code: "custom",
          message: "Every Transaction source frame must be present in the privacy artifact.",
          path: ["transactionActivity", "transactionSources", recordIndex, "sourceFrameRecordId"]
        });
      }
      if (!transactionFrameIds.has(record.committedFrameRecordId)) {
        context.addIssue({
          code: "custom",
          message: "Every committed Transaction source frame must be exported as a v2 Transaction frame.",
          path: ["transactionActivity", "transactionSources", recordIndex, "committedFrameRecordId"]
        });
      }
    });
    artifact.transactionActivity.journalSources.forEach((record, recordIndex) => {
      validateAvailableEvidenceSpan(record, [
        "transactionActivity",
        "journalSources",
        recordIndex,
        "evidenceSpanId"
      ]);
      if (!allFrameIds.has(record.sourceFrameRecordId)) {
        context.addIssue({
          code: "custom",
          message: "Every Activity source frame must be present in the privacy artifact.",
          path: ["transactionActivity", "journalSources", recordIndex, "sourceFrameRecordId"]
        });
      }
    });
  });

export const GraphPrivacyExportResponseV2Schema = z
  .object({
    contentType: z.literal("application/json"),
    filename: z.string().min(1),
    artifact: GraphPrivacyExportArtifactV2Schema
  })
  .strict();

export const GraphPrivacyExportFormatSchema = z.enum(["json", "zip"]);

export const GraphTenantErasurePlanFrameSchema = z.object({
  id: z.string().min(1),
  sessionId: z.string().min(1).optional(),
  version: z.number().int().positive().optional(),
  acceptedAt: z.string().datetime(),
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  changeSetCount: z.number().int().nonnegative(),
  auditLogCount: z.number().int().nonnegative()
});

export const GraphTenantErasurePlanResponseSchema = z.object({
  schemaVersion: z.literal("salesko.tenant_erasure_plan.v1"),
  generatedAt: z.string().datetime(),
  tenantId: TenantIdSchema,
  mode: z.literal("dry_run"),
  destructiveActionExecuted: z.literal(false),
  privacyExport: z.object({
    route: z.literal("/api/graph/privacy/export"),
    filename: z.string().min(1),
    recommendedBeforeErasure: z.literal(true)
  }),
  summary: z.object({
    frameCount: z.number().int().nonnegative(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
    canonicalEntityCount: z.number().int().nonnegative(),
    canonicalGraphRowCount: z.number().int().nonnegative().default(0),
    changeSetCount: z.number().int().nonnegative(),
    auditLogCount: z.number().int().nonnegative(),
    agentResearchJobCount: z.number().int().nonnegative().default(0),
    agentResearchResultCount: z.number().int().nonnegative().default(0),
    agentResearchOutboxCount: z.number().int().nonnegative().default(0),
    agentResearchUsageEventCount: z.number().int().nonnegative().default(0),
    agentResearchEventCount: z.number().int().nonnegative().default(0),
    agentResearchErasureRequestCount: z.number().int().nonnegative().default(0)
  }),
  frames: z.array(GraphTenantErasurePlanFrameSchema),
  canonicalEntityIds: z.array(z.string().min(1)),
  warnings: z.array(z.string()).default([])
});

export const SaleskoTenantMembershipStatusSchema = z.enum(["active", "suspended", "revoked"]);
export const SaleskoTenantStatusSchema = z.enum(["active", "suspended", "archived"]);

export const SaleskoTenantMemberUserIdSchema = z.string().min(1).max(128).regex(/^[A-Za-z0-9_.:@-]+$/);

export const SaleskoTenantMemberSchema = z.object({
  tenantId: TenantIdSchema,
  userId: SaleskoTenantMemberUserIdSchema,
  email: z.string().email().max(320).optional(),
  name: z.string().trim().min(1).max(128).optional(),
  role: SaleskoTenantRoleSchema,
  status: SaleskoTenantMembershipStatusSchema,
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export const SaleskoTenantPolicySchema = z.object({
  tenantId: TenantIdSchema,
  name: z.string().min(1),
  status: SaleskoTenantStatusSchema,
  legalHold: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export const SaleskoTenantPolicyUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    status: SaleskoTenantStatusSchema.optional(),
    legalHold: z.boolean().optional()
  })
  .refine((value) => value.name !== undefined || value.status !== undefined || value.legalHold !== undefined, {
    message: "At least one tenant policy field is required."
  });

export const SaleskoTenantPolicyResponseSchema = z.object({
  tenantId: TenantIdSchema,
  policy: SaleskoTenantPolicySchema
});

export const SaleskoTenantProvisionRequestSchema = z.object({
  tenantId: TenantIdSchema,
  name: z.string().trim().min(1).max(120).optional()
});

export const ApiErrorResponseSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1)
  })
});

export const HealthResponseSchema = z.object({
  ok: z.boolean(),
  service: z.string(),
  runtime: z.string(),
  timestamp: z.string().datetime()
});

export type Locale = z.infer<typeof LocaleSchema>;
export type TenantId = z.infer<typeof TenantIdSchema>;
export type SaleskoTenantRole = z.infer<typeof SaleskoTenantRoleSchema>;
export type SaleskoOAuthProvider = z.infer<typeof SaleskoOAuthProviderSchema>;
export type SaleskoSupabaseOAuthProvider = z.infer<typeof SaleskoSupabaseOAuthProviderSchema>;
export type SaleskoSessionGrant = z.infer<typeof SaleskoSessionGrantSchema>;
export type SaleskoSessionGrantIssueRequest = z.input<typeof SaleskoSessionGrantIssueRequestSchema>;
export type SaleskoSessionGrantIssueResponse = z.infer<typeof SaleskoSessionGrantIssueResponseSchema>;
export type MobileAuthExchangeRequest = z.infer<typeof MobileAuthExchangeRequestSchema>;
export type MobileAuthProfile = z.infer<typeof MobileAuthProfileSchema>;
export type MobileAuthProvider = z.infer<typeof MobileAuthProviderSchema>;
export type MobileAuthRefreshRequest = z.infer<typeof MobileAuthRefreshRequestSchema>;
export type MobileAuthRevokeRequest = z.infer<typeof MobileAuthRevokeRequestSchema>;
export type MobileAuthSessionResponse = z.infer<typeof MobileAuthSessionResponseSchema>;
export type SaleskoTenantMember = z.infer<typeof SaleskoTenantMemberSchema>;
export type SaleskoTenantMembershipStatus = z.infer<typeof SaleskoTenantMembershipStatusSchema>;
export type SaleskoTenantPolicy = z.infer<typeof SaleskoTenantPolicySchema>;
export type SaleskoTenantPolicyResponse = z.infer<typeof SaleskoTenantPolicyResponseSchema>;
export type SaleskoTenantPolicyUpdateRequest = z.input<typeof SaleskoTenantPolicyUpdateRequestSchema>;
export type SaleskoTenantProvisionRequest = z.input<typeof SaleskoTenantProvisionRequestSchema>;
export type SaleskoTenantStatus = z.infer<typeof SaleskoTenantStatusSchema>;
export type ProviderAuthGrant = z.infer<typeof ProviderAuthGrantSchema>;
export type ProviderAuthGrantIssueRequest = z.input<typeof ProviderAuthGrantIssueRequestSchema>;
export type ProviderAuthGrantIssueResponse = z.infer<typeof ProviderAuthGrantIssueResponseSchema>;
export type GraphFrameShareGrant = z.infer<typeof GraphFrameShareGrantSchema>;
export type GraphFrameShareLinkIssueRequest = z.input<typeof GraphFrameShareLinkIssueRequestSchema>;
export type GraphFrameShareLinkIssueResponse = z.infer<typeof GraphFrameShareLinkIssueResponseSchema>;
export type McpConsentGrant = z.infer<typeof McpConsentGrantSchema>;
export type McpConsentGrantIssueRequest = z.input<typeof McpConsentGrantIssueRequestSchema>;
export type McpConsentGrantIssueResponse = z.infer<typeof McpConsentGrantIssueResponseSchema>;
export type GraphRelationType = z.infer<typeof GraphRelationTypeSchema>;
export type GraphPosition = z.infer<typeof GraphPositionSchema>;
export type GraphNode = z.infer<typeof GraphNodeSchema>;
export type GraphEdge = z.infer<typeof GraphEdgeSchema>;
export type GraphCanonicalRecordId = z.infer<typeof GraphCanonicalRecordIdSchema>;
export type GraphDatasetScope = z.infer<typeof GraphDatasetScopeSchema>;
export type EvidenceSpanSourceKind = z.infer<typeof EvidenceSpanSourceKindSchema>;
export type EvidenceSpan = z.infer<typeof EvidenceSpanSchema>;
export type CanonicalEntity = z.infer<typeof CanonicalEntitySchema>;
export type EntityAlias = z.infer<typeof EntityAliasSchema>;
export type RelationshipEdgeStatus = z.infer<typeof RelationshipEdgeStatusSchema>;
export type RelationshipEdge = z.infer<typeof RelationshipEdgeSchema>;
export type GraphTimelineItem = z.infer<typeof GraphTimelineItemSchema>;
export type GraphCamera = z.infer<typeof GraphCameraSchema>;
export type GraphMutationCommand = z.infer<typeof GraphMutationCommandSchema>;
export type GraphChangeSetOp = z.infer<typeof GraphChangeSetOpSchema>;
export type InsightType = z.infer<typeof InsightTypeSchema>;
export type InsightStatus = z.infer<typeof InsightStatusSchema>;
export type Insight = z.infer<typeof InsightSchema>;
export type AgentGraphCommand = z.infer<typeof AgentGraphCommandSchema>;
export type AgentGraphInput = z.input<typeof AgentGraphRequestSchema>;
export type AgentGraphRequest = z.output<typeof AgentGraphRequestSchema>;
export type GraphVisualizationFrame = z.infer<typeof GraphVisualizationFrameSchema>;
export type GraphFrameProjectionSource = z.infer<typeof GraphFrameProjectionSourceSchema>;
export type GraphCanonicalShadowParityTable = z.infer<typeof GraphCanonicalShadowParityTableSchema>;
export type GraphCanonicalShadowParityTableReport = z.infer<typeof GraphCanonicalShadowParityTableReportSchema>;
export type GraphCanonicalShadowParityMismatch = z.infer<typeof GraphCanonicalShadowParityMismatchSchema>;
export type GraphCanonicalShadowParityMismatchGroup = z.infer<typeof GraphCanonicalShadowParityMismatchGroupSchema>;
export type GraphCanonicalShadowParityResponse = z.infer<typeof GraphCanonicalShadowParityResponseSchema>;
export type AgentChatMentionRange = z.infer<typeof AgentChatMentionRangeSchema>;
export type AgentChatMention = z.infer<typeof AgentChatMentionSchema>;
export type AgentChatMessage = z.infer<typeof AgentChatMessageSchema>;
export type AgentAssistantChatMessage = z.infer<typeof AgentAssistantChatMessageSchema>;
export type AgentChatInput = z.input<typeof AgentChatRequestSchema>;
export type AgentChatRequest = z.output<typeof AgentChatRequestSchema>;
export type AgentChatError = z.infer<typeof AgentChatErrorSchema>;
export type AgentChatResponse = z.infer<typeof AgentChatResponseSchema>;
export type AgentChatStreamEvent = z.infer<typeof AgentChatStreamEventSchema>;
export type AgentQuotaStatusResponse = z.infer<typeof AgentQuotaStatusResponseSchema>;
export type AgentChatThreadSummary = z.infer<typeof AgentChatThreadSummarySchema>;
export type AgentChatThread = z.infer<typeof AgentChatThreadSchema>;
export type AgentChatThreadUpsertRequest = z.infer<typeof AgentChatThreadUpsertRequestSchema>;
export type AgentChatThreadListResponse = z.infer<typeof AgentChatThreadListResponseSchema>;
export type AgentChatThreadResponse = z.infer<typeof AgentChatThreadResponseSchema>;
export type GraphFrameWrite = z.infer<typeof GraphFrameWriteSchema>;
export type SaleskoGraphVisualizationToolResult = z.infer<typeof SaleskoGraphVisualizationToolResultSchema>;
export type GraphNoteIntakeSourceChannel = z.infer<typeof GraphNoteIntakeSourceChannelSchema>;
export type GraphNoteIntakeProcessingMode = z.infer<typeof GraphNoteIntakeProcessingModeSchema>;
export type GraphNoteIntakeRequest = z.output<typeof GraphNoteIntakeRequestSchema>;
export type GraphNoteIntakeInput = z.input<typeof GraphNoteIntakeRequestSchema>;
export type GraphNoteIntakePreview = z.infer<typeof GraphNoteIntakePreviewSchema>;
export type GraphNoteEvidenceSummary = z.infer<typeof GraphNoteEvidenceSummarySchema>;
export type GraphNoteIntakeJobStatus = z.infer<typeof GraphNoteIntakeJobStatusSchema>;
export type GraphNoteIntakeJobRecord = z.infer<typeof GraphNoteIntakeJobRecordSchema>;
export type GraphNoteIntakeJobMessage = z.infer<typeof GraphNoteIntakeJobMessageSchema>;
export type GraphNoteIntakeCommittedResponse = z.infer<typeof GraphNoteIntakeCommittedResponseSchema>;
export type GraphNoteIntakeQueuedResponse = z.infer<typeof GraphNoteIntakeQueuedResponseSchema>;
export type GraphNoteIntakeResponse = z.infer<typeof GraphNoteIntakeResponseSchema>;
export type GraphFrameExportFormat = z.infer<typeof GraphFrameExportFormatSchema>;
export type GraphFrameJsonExportArtifact = z.infer<typeof GraphFrameJsonExportArtifactSchema>;
export type GraphObsidianExportFile = z.infer<typeof GraphObsidianExportFileSchema>;
export type GraphFrameObsidianExportArtifact = z.infer<typeof GraphFrameObsidianExportArtifactSchema>;
export type GraphFrameExportResponse = z.infer<typeof GraphFrameExportResponseSchema>;
export type GraphFrameJsonImportRequest = z.infer<typeof GraphFrameJsonImportRequestSchema>;
export type GraphFrameObsidianImportRequest = z.infer<typeof GraphFrameObsidianImportRequestSchema>;
export type GraphProjectionStatus = z.infer<typeof GraphProjectionStatusSchema>;
export type GraphChangeSetStatus = z.infer<typeof GraphChangeSetStatusSchema>;
export type GraphChangeSetAuditLogEventType = z.infer<typeof GraphChangeSetAuditLogEventTypeSchema>;
export type GraphChangeSetWrite = z.infer<typeof GraphChangeSetWriteSchema>;
export type GraphV1ChangeSetCreateRequest = z.infer<typeof GraphV1ChangeSetCreateRequestSchema>;
export type GraphChangeSetRecord = z.infer<typeof GraphChangeSetRecordSchema>;
export type GraphChangeSetCreateResponse = z.infer<typeof GraphChangeSetCreateResponseSchema>;
export type GraphChangeSetListResponse = z.infer<typeof GraphChangeSetListResponseSchema>;
export type GraphChangeSetAuditLogRecord = z.infer<typeof GraphChangeSetAuditLogRecordSchema>;
export type GraphChangeSetAuditLogListResponse = z.infer<typeof GraphChangeSetAuditLogListResponseSchema>;
export type GraphChangeSetAuditExportArtifact = z.infer<typeof GraphChangeSetAuditExportArtifactSchema>;
export type GraphChangeSetAuditExportResponse = z.infer<typeof GraphChangeSetAuditExportResponseSchema>;
export type GraphCsvImportRequest = z.infer<typeof GraphCsvImportRequestSchema>;
export type GraphCsvImportResponse = z.infer<typeof GraphCsvImportResponseSchema>;
export type GraphCsvImportSummary = z.infer<typeof GraphCsvImportSummarySchema>;
export type GraphChangeSetApplyResponse = z.infer<typeof GraphChangeSetApplyResponseSchema>;
export type GraphChangeSetRevertResponse = z.infer<typeof GraphChangeSetRevertResponseSchema>;
export type GraphFrameCommitResponse = z.infer<typeof GraphFrameCommitResponseSchema>;
export type GraphFrameImportSummary = z.infer<typeof GraphFrameImportSummarySchema>;
export type GraphFrameImportResponse = z.infer<typeof GraphFrameImportResponseSchema>;
export type GraphFrameDeleteResponse = z.infer<typeof GraphFrameDeleteResponseSchema>;
export type GraphCanonicalEntityEraseResponse = z.infer<typeof GraphCanonicalEntityEraseResponseSchema>;
export type GraphCanonicalEntityUpdateRequest = z.infer<typeof GraphCanonicalEntityUpdateRequestSchema>;
export type GraphCanonicalEntityUpdateResponse = z.infer<typeof GraphCanonicalEntityUpdateResponseSchema>;
export type GraphCanonicalEntityMergeRequest = z.infer<typeof GraphCanonicalEntityMergeRequestSchema>;
export type GraphCanonicalEntityMergeResponse = z.infer<typeof GraphCanonicalEntityMergeResponseSchema>;
export type GraphCanonicalEntityRekeyRequest = z.infer<typeof GraphCanonicalEntityRekeyRequestSchema>;
export type GraphCanonicalEntityRekeyResponse = z.infer<typeof GraphCanonicalEntityRekeyResponseSchema>;
export type GraphCanonicalEntitySplitNodeRef = z.infer<typeof GraphCanonicalEntitySplitNodeRefSchema>;
export type GraphCanonicalEntitySplitRequest = z.infer<typeof GraphCanonicalEntitySplitRequestSchema>;
export type GraphCanonicalEntitySplitResponse = z.infer<typeof GraphCanonicalEntitySplitResponseSchema>;
export type GraphTenantErasureRequest = z.infer<typeof GraphTenantErasureRequestSchema>;
export type GraphTenantErasureResponse = z.infer<typeof GraphTenantErasureResponseSchema>;
export type GraphRetentionPolicy = z.infer<typeof GraphRetentionPolicySchema>;
export type GraphRetentionPlanFrame = z.infer<typeof GraphRetentionPlanFrameSchema>;
export type GraphRetentionPlanResponse = z.infer<typeof GraphRetentionPlanResponseSchema>;
export type GraphRetentionRunRequest = z.infer<typeof GraphRetentionRunRequestSchema>;
export type GraphRetentionRunResponse = z.infer<typeof GraphRetentionRunResponseSchema>;
export type GraphFrameRecord = z.infer<typeof GraphFrameRecordSchema>;
export type GraphFrameRecordV2 = z.infer<typeof GraphFrameRecordV2Schema>;
export type TransactionDetailResponse = z.infer<typeof TransactionDetailResponseSchema>;
export type TransactionGraphResponse = z.infer<typeof TransactionGraphResponseSchema>;
export type ActivitySourceDetailResponse = z.infer<typeof ActivitySourceDetailResponseSchema>;
export type TransactionCommandOperation = z.infer<typeof TransactionCommandOperationSchema>;
export type TransactionCommandOutcome = z.infer<typeof TransactionCommandOutcomeSchema>;
export type TransactionCommandResponse = z.infer<typeof TransactionCommandResponseSchema>;
export type ActivityBackfillResponse = z.infer<typeof ActivityBackfillResponseSchema>;
export type GraphFrameShareReadResponse = z.infer<typeof GraphFrameShareReadResponseSchema>;
export type GraphFrameListItem = z.infer<typeof GraphFrameListItemSchema>;
export type GraphFrameListResponse = z.infer<typeof GraphFrameListResponseSchema>;
export type GraphReadNode = z.infer<typeof GraphReadNodeSchema>;
export type GraphReadEdge = z.infer<typeof GraphReadEdgeSchema>;
export type GraphReadNodeV2 = z.infer<typeof GraphReadNodeV2Schema>;
export type GraphReadEdgeV2 = z.infer<typeof GraphReadEdgeV2Schema>;
export type GraphProjectionResponse = z.infer<typeof GraphProjectionResponseSchema>;
export type GraphSearchResponse = z.infer<typeof GraphSearchResponseSchema>;
export type GraphOpportunityStatus = z.infer<typeof GraphOpportunityStatusSchema>;
export type GraphOpportunityFeedItem = z.infer<typeof GraphOpportunityFeedItemSchema>;
export type GraphOpportunityFeedResponse = z.infer<typeof GraphOpportunityFeedResponseSchema>;
export type GraphOpportunityActionRequest = z.infer<typeof GraphOpportunityActionRequestSchema>;
export type GraphOpportunityActionResponse = z.infer<typeof GraphOpportunityActionResponseSchema>;
export type GraphOpportunityActionProposalRequest = z.infer<typeof GraphOpportunityActionProposalRequestSchema>;
export type GraphOpportunityActionProposalResponse = z.infer<typeof GraphOpportunityActionProposalResponseSchema>;
export type GraphNeighborsResponse = z.infer<typeof GraphNeighborsResponseSchema>;
export type GraphRelationshipPath = z.infer<typeof GraphRelationshipPathSchema>;
export type GraphRelationshipPathKind = z.infer<typeof GraphRelationshipPathKindSchema>;
export type GraphRelationshipPathsResponse = z.infer<typeof GraphRelationshipPathsResponseSchema>;
export type GraphRelationshipReminder = z.infer<typeof GraphRelationshipReminderSchema>;
export type GraphRelationshipRemindersResponse = z.infer<typeof GraphRelationshipRemindersResponseSchema>;
export type GraphCanonicalEntity = z.infer<typeof GraphCanonicalEntitySchema>;
export type GraphCanonicalEntityListResponse = z.infer<typeof GraphCanonicalEntityListResponseSchema>;
export type GraphCanonicalEntityDetailResponse = z.infer<typeof GraphCanonicalEntityDetailResponseSchema>;
export type GraphPrivacyExportCanonicalEntity = z.infer<typeof GraphPrivacyExportCanonicalEntitySchema>;
export type GraphPrivacyExportCanonicalEntityV2 = z.infer<typeof GraphPrivacyExportCanonicalEntityV2Schema>;
export type GraphPrivacyExportChangeSet = z.infer<typeof GraphPrivacyExportChangeSetSchema>;
export type GraphPrivacyExportCanonicalGraphRows = z.infer<typeof GraphPrivacyExportCanonicalGraphRowsSchema>;
export type GraphPrivacyExportAgentResearchOutbox = z.infer<typeof GraphPrivacyExportAgentResearchOutboxSchema>;
export type GraphPrivacyExportAgentResearchEvent = z.infer<typeof GraphPrivacyExportAgentResearchEventSchema>;
export type GraphPrivacyExportAgentResearchErasureRequest = z.infer<
  typeof GraphPrivacyExportAgentResearchErasureRequestSchema
>;
export type GraphPrivacyExportAgentResearch = z.infer<typeof GraphPrivacyExportAgentResearchSchema>;
export type GraphPrivacyExportArtifact = z.infer<typeof GraphPrivacyExportArtifactSchema>;
export type GraphPrivacyExportResponse = z.infer<typeof GraphPrivacyExportResponseSchema>;
export type TransactionActivityPrivacySummary = z.infer<typeof TransactionActivityPrivacySummarySchema>;
export type TransactionActivityPrivacyRows = z.infer<typeof TransactionActivityPrivacyRowsSchema>;
export type GraphPrivacyExportArtifactV2 = z.infer<typeof GraphPrivacyExportArtifactV2Schema>;
export type GraphPrivacyExportResponseV2 = z.infer<typeof GraphPrivacyExportResponseV2Schema>;
export type GraphPrivacyExportFormat = z.infer<typeof GraphPrivacyExportFormatSchema>;
export type GraphTenantErasurePlanFrame = z.infer<typeof GraphTenantErasurePlanFrameSchema>;
export type GraphTenantErasurePlanResponse = z.infer<typeof GraphTenantErasurePlanResponseSchema>;
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/** Internal Summary content only. Host job records own coverage, identity and authorization. */
export const ConversationSummaryContentSchemaVersion = "salesko.conversation_summary_content.v1" as const;
export const ConversationSummaryContentSchema = z.object({
  schemaVersion: z.literal(ConversationSummaryContentSchemaVersion),
  historicalGoals: z.array(z.string().min(1)),
  constraintsAndPreferences: z.array(z.string().min(1)),
  decisionsAndSupersessions: z.array(z.string().min(1)),
  acceptedAnswers: z.array(z.string().min(1)),
  unresolvedMatters: z.array(z.string().min(1)),
  entitiesAndReferences: z.array(z.string().min(1)),
  criticalContext: z.array(z.string().min(1)),
}).strict();
export type ConversationSummaryContent = z.infer<typeof ConversationSummaryContentSchema>;

export const AgentResearchJobSchemaVersion = "salesko.agent_research_job.v1" as const;
export const AgentResearchResultSchemaVersion = "salesko.agent_research_result.v1" as const;
export const AgentResearchUsageEventProducer = "salesko-sidecar" as const;
export const AgentResearchCallbackAudience = "salesko-api-internal" as const;
export const AgentResearchCallbackGrantTokenPrefix = "salesko-agent-research-callback-v1" as const;

export const AgentResearchModeSchema = z.literal("relationship_enrichment");
export const AgentResearchJobKindSchema = z.literal("relationship_research");
// The bare research-preset value. `AgentResearchAgentProfileSchema` layers the
// dispatch default on top for job requests; the Private Agent profile stores
// the value without a default so a missing preset fails closed instead of
// silently becoming `relationship_research`.
export const AgentResearchAgentProfileValueSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9_-]*$/);
export const AgentResearchAgentProfileSchema = AgentResearchAgentProfileValueSchema.default("relationship_research");
export const AgentResearchLocaleSeedSchema = z
  .object({
    locale: LocaleSchema.default(DefaultSaleskoLocale),
    region: z.string().trim().min(1).max(80).optional(),
    timezone: z.string().trim().min(1).max(80).optional()
  })
  .default({ locale: DefaultSaleskoLocale });
export const AgentResearchMessagingChannelSchema = z.enum(["telegram", "wechat", "line"]);
// Generic Agent defaults only. The research preset moved to
// `PrivateAgentProfileSchema.researchPreset` — the Private Agent owns it,
// because it is the agent that runs research on the owner's own computer.
export const SaleskoAgentSettingsSchema = z.object({
  localeSeed: AgentResearchLocaleSeedSchema,
  messagingAddonEnabled: z.boolean().default(false),
  messagingChannels: z.array(AgentResearchMessagingChannelSchema).max(3).default([])
});
export const SaleskoAgentSettingsResponseSchema = z.object({
  tenantId: TenantIdSchema,
  settings: SaleskoAgentSettingsSchema
});
export const AgentResearchEntitlementReasonSchema = z.enum([
  "paid_personal_sidecar_required",
  "paid_plan_required"
]);
export const AgentResearchEntitlementSchema = z.object({
  eligible: z.boolean(),
  reason: AgentResearchEntitlementReasonSchema.optional(),
  tier: SaleskoBillingTierSchema.optional(),
  privateAgentAccessMode: SaleskoPrivateAgentAccessModeSchema.optional()
});
export const AgentResearchEntitlementResponseSchema = z.object({
  tenantId: TenantIdSchema,
  entitlement: AgentResearchEntitlementSchema
});

// --- Billing / paid entitlement (Stripe) ---
// Per-tenant subscription. `messagingAddonEnabled` in agent settings and Sidecar
// access are both derived effects of an active subscription's tier here, not
// independent toggles. `tier` is the single source of truth (see
// SaleskoBillingTierSchema above); the absence of a row means "free".
export const SaleskoBillingSubscriptionStatusSchema = z.enum([
  "active",
  "trialing",
  "pending_cancel",
  "canceled",
  "incomplete"
]);
export const SaleskoBillingSubscriptionSchema = z.object({
  tenantId: TenantIdSchema,
  tier: SaleskoBillingTierSchema,
  status: SaleskoBillingSubscriptionStatusSchema,
  stripeCustomerId: z.string().trim().min(1).max(255).optional(),
  stripeSubscriptionId: z.string().trim().min(1).max(255).optional(),
  currentPeriodEnd: z.string().datetime().optional(),
  cancelAtPeriodEnd: z.boolean().default(false)
});
export const SaleskoBillingEntitlementResponseSchema = z.object({
  tenantId: TenantIdSchema,
  messagingAddon: z.boolean(),
  tier: SaleskoBillingTierSchema,
  subscription: SaleskoBillingSubscriptionSchema.nullable()
});
export const SaleskoBillingCheckoutRequestSchema = z.object({
  // Catalog-driven checkout only; price/credits/interval/tier are looked up
  // server-side from productId and never trusted from the client.
  productId: z.string().trim().min(1).max(80),
  returnPath: z.string().trim().max(512).optional()
});
export const SaleskoBillingCheckoutResponseSchema = z.object({
  checkoutUrl: z.string().url()
});

// --- Pricing catalog (subscription tiers) ---
// Server-side source of truth mirrored to the client read-only for the pricing
// UI. Tier gates the messaging addon; privateAgentAccessMode determines how Deep
// Research is unlocked (own computer vs SalesKo-managed credits).
export const SaleskoBillingProductTypeSchema = z.enum(["subscription", "one_time"]);
export const SaleskoBillingIntervalSchema = z.enum(["month", "year"]);
export const SaleskoBillingProductSchema = z.object({
  productId: z.string().min(1).max(80),
  // Omitted for credit packs (one-time, no tier).
  tier: SaleskoBillingTierSchema.optional(),
  name: z.string().min(1).max(120),
  description: z.string().max(500),
  type: SaleskoBillingProductTypeSchema,
  priceCents: z.number().int().nonnegative(),
  currency: z.string().min(3).max(8),
  credits: z.number().int().nonnegative(),
  interval: SaleskoBillingIntervalSchema.optional(),
  creditsValidDays: z.number().int().positive().optional(),
  // Required for subscription products; omitted for one-time credit packs
  // (which have no tier and do not themselves change Private Agent access).
  privateAgentAccessMode: SaleskoPrivateAgentAccessModeSchema.optional()
});
export const SaleskoBillingCatalogResponseSchema = z.object({
  products: z.array(SaleskoBillingProductSchema)
});

// --- Credit balance + history (Agent usage ledger) ---
export const SaleskoBillingCreditBalanceResponseSchema = z.object({
  tenantId: TenantIdSchema,
  balance: z.number().int().nonnegative()
});
export const SaleskoBillingCreditTransactionTypeSchema = z.enum(["grant", "consume"]);
export const SaleskoBillingCreditTransactionSchema = z.object({
  transactionNo: z.string().min(1),
  transactionType: SaleskoBillingCreditTransactionTypeSchema,
  scene: z.string().nullable(),
  credits: z.number().int(),
  remainingCredits: z.number().int(),
  description: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime()
});
export const SaleskoBillingCreditHistoryResponseSchema = z.object({
  tenantId: TenantIdSchema,
  transactions: z.array(SaleskoBillingCreditTransactionSchema)
});

export type SaleskoBillingSubscriptionStatus = z.infer<typeof SaleskoBillingSubscriptionStatusSchema>;
export type SaleskoBillingSubscription = z.infer<typeof SaleskoBillingSubscriptionSchema>;
export type SaleskoBillingEntitlementResponse = z.infer<typeof SaleskoBillingEntitlementResponseSchema>;
export type SaleskoBillingCheckoutRequest = z.infer<typeof SaleskoBillingCheckoutRequestSchema>;
export type SaleskoBillingCheckoutResponse = z.infer<typeof SaleskoBillingCheckoutResponseSchema>;
export type SaleskoBillingProductType = z.infer<typeof SaleskoBillingProductTypeSchema>;
export type SaleskoBillingTier = z.infer<typeof SaleskoBillingTierSchema>;
export type SaleskoPrivateAgentAccessMode = z.infer<typeof SaleskoPrivateAgentAccessModeSchema>;
export type SaleskoBillingInterval = z.infer<typeof SaleskoBillingIntervalSchema>;
export type SaleskoBillingProduct = z.infer<typeof SaleskoBillingProductSchema>;
export type SaleskoBillingCatalogResponse = z.infer<typeof SaleskoBillingCatalogResponseSchema>;
export type SaleskoBillingCreditBalanceResponse = z.infer<typeof SaleskoBillingCreditBalanceResponseSchema>;
export type SaleskoBillingCreditTransaction = z.infer<typeof SaleskoBillingCreditTransactionSchema>;
export type SaleskoBillingCreditHistoryResponse = z.infer<typeof SaleskoBillingCreditHistoryResponseSchema>;
/**
 * `waiting_device` is terminal: apps/api's admission policy rejects a research
 * job when byok-control reports the tenant has no active paired device. It is
 * an admission rejection, not a durable queue, so the single active-job slot is
 * released immediately and the user re-triggers after pairing a device.
 */
export const AgentResearchJobStatusSchema = z.enum([
  "queued",
  "dispatching",
  "running",
  "cancel_requested",
  "succeeded",
  "failed",
  "canceled",
  "waiting_device",
  "erased"
]);
export const AgentResearchUsageStateSchema = z.enum(["none", "pending", "reconciled", "failed"]);
export const AgentResearchStopReasonSchema = z.enum([
  "completed",
  "step_budget",
  "time_budget",
  "token_budget",
  "provider_limit"
]);
export const AgentResearchUsageOperationSchema = z.enum([
  "llm.generate",
  "search.query",
  "http.fetch",
  "sandbox.running_ms",
  "sandbox.create",
  "workspace.read_bytes",
  "workspace.write_bytes",
  "artifact.storage_bytes"
]);
export const AgentResearchCallbackScopeSchema = z.enum([
  "graph:read-context",
  "job:submit-proposal",
  "job:complete",
  "job:fail",
  "usage:write"
]);
export const AgentResearchBudgetMaxCostMicros = 1_000_000_000_000;

export const AgentResearchBudgetSchema = z
  .object({
    maxSteps: z.number().int().min(1).max(64).optional(),
    maxWallClockSeconds: z.number().int().min(30).max(3_600).optional(),
    maxSources: z.number().int().min(1).max(100).optional(),
    maxInputTokens: z.number().int().min(1).max(2_000_000).optional(),
    maxOutputTokens: z.number().int().min(1).max(500_000).optional(),
    maxCostMicros: z.number().int().min(1).max(AgentResearchBudgetMaxCostMicros).optional(),
    maxResultBytes: z.number().int().min(1_024).max(2_000_000).optional()
  })
  .default({});

export const AgentResearchEffectiveBudgetSchema = AgentResearchBudgetSchema.default({
  maxResultBytes: 524_288,
  maxSources: 20,
  maxSteps: 12,
  maxWallClockSeconds: 900
});

export const AgentResearchJobCreateRequestSchema = z.object({
  agentId: z.lazy(() => PrivateAgentAgentIdSchema),
  clientRequestId: z.string().trim().min(1).max(128),
  frameRecordId: z.string().min(1),
  expectedFrameVersion: z.number().int().positive(),
  targetCanonicalEntityId: z.string().min(1),
  question: z.string().trim().min(1).max(2_000),
  agentProfile: AgentResearchAgentProfileSchema,
  localeSeed: AgentResearchLocaleSeedSchema,
  messagingChannels: z.array(AgentResearchMessagingChannelSchema).max(3).default([]),
  mode: AgentResearchModeSchema.default("relationship_enrichment"),
  requestedBudget: AgentResearchBudgetSchema.optional()
});

export const AgentResearchJobSchema = z.object({
  schemaVersion: z.literal(AgentResearchJobSchemaVersion),
  jobId: z.string().min(1),
  datasetScope: z.string().min(1).max(64).default("salesko"),
  tenantId: TenantIdSchema,
  createdByUserId: z.string().min(1).max(128),
  kind: AgentResearchJobKindSchema.default("relationship_research"),
  status: AgentResearchJobStatusSchema,
  rowVersion: z.number().int().positive().default(1),
  clientRequestId: z.string().min(1).max(128),
  frameRecordId: z.string().min(1),
  baseFrameVersion: z.number().int().positive(),
  targetCanonicalEntityId: z.string().min(1),
  question: z.string().min(1).max(2_000),
  agentProfile: AgentResearchAgentProfileSchema,
  localeSeed: AgentResearchLocaleSeedSchema,
  messagingChannels: z.array(AgentResearchMessagingChannelSchema).max(3).default([]),
  mode: AgentResearchModeSchema,
  effectiveBudget: AgentResearchBudgetSchema,
  externalJobId: z.string().min(1).optional(),
  attemptCount: z.number().int().nonnegative().default(0),
  usageState: AgentResearchUsageStateSchema.default("none"),
  cancelRequestedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const AgentResearchEvidenceSchema = z.object({
  url: z.string().url(),
  title: z.string().trim().min(1).max(300),
  excerpt: z.string().trim().min(1).max(2_000),
  observedAt: z.string().datetime()
});

export const AgentResearchResultSchema = z.object({
  schemaVersion: z.literal(AgentResearchResultSchemaVersion),
  jobId: z.string().min(1),
  baseFrameRecordId: z.string().min(1),
  baseFrameVersion: z.number().int().positive(),
  previewFrame: GraphVisualizationFrameSchema,
  proposedChangeSet: GraphChangeSetWriteSchema,
  evidence: z.array(AgentResearchEvidenceSchema).max(100).default([]),
  warnings: z.array(z.string().min(1).max(500)).max(100).default([]),
  stopReason: AgentResearchStopReasonSchema,
  resultSha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  createdAt: z.string().datetime().optional()
});

export const AgentResearchJobCreateResponseSchema = z.object({
  schemaVersion: z.literal(AgentResearchJobSchemaVersion),
  jobId: z.string().min(1),
  status: z.enum(["queued", "dispatching", "running"]),
  effectiveBudget: AgentResearchBudgetSchema,
  createdAt: z.string().datetime()
});

export const AgentResearchRuntimePhaseSchema = z.enum([
  "queued",
  "dispatching",
  "running",
  "canceling",
  "usage_reconciliation",
  "ready_for_review",
  "terminal"
]);
export const AgentResearchSandboxPhaseSchema = z.enum([
  "not_requested",
  "pending_if_tool_used",
  "used",
  "not_used",
  "terminated"
]);
export const AgentResearchWorkspaceCheckpointPhaseSchema = z.enum([
  "not_started",
  "pending",
  "checkpointed",
  "not_required"
]);
export const AgentResearchRuntimeStatusSchema = z.object({
  phase: AgentResearchRuntimePhaseSchema,
  label: z.string().trim().min(1).max(160),
  sandboxPhase: AgentResearchSandboxPhaseSchema,
  workspaceCheckpointPhase: AgentResearchWorkspaceCheckpointPhaseSchema,
  materialization: z.enum(["not_ready", "pending_change_set_ready", "blocked_by_stale_frame"]).default("not_ready")
});
export const AgentResearchUsageSummarySchema = z.object({
  usageState: AgentResearchUsageStateSchema,
  eventCount: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  toolUnits: z.number().int().nonnegative(),
  llmGenerateCount: z.number().int().nonnegative(),
  searchQueryCount: z.number().int().nonnegative(),
  httpFetchCount: z.number().int().nonnegative(),
  llmSearchCostMicros: z.number().int().nonnegative(),
  totalCostMicros: z.number().int().nonnegative(),
  sandbox: z.object({
    createCount: z.number().int().nonnegative(),
    runningMs: z.number().int().nonnegative(),
    pausedCount: z.number().int().nonnegative(),
    pausedAgeMs: z.number().int().nonnegative()
  }),
  workspace: z.object({
    readBytes: z.number().int().nonnegative(),
    writeBytes: z.number().int().nonnegative(),
    storageBytes: z.number().int().nonnegative()
  }),
  unitEconomics: z.object({
    grossCostMicros: z.number().int().nonnegative(),
    grossMarginEstimateMicros: z.number().int().nullable(),
    grossMarginPercent: z.number().nullable(),
    revenueSource: z.enum(["not_configured", "estimated"])
  })
});

export const AgentResearchJobReadResponseSchema = z.object({
  job: AgentResearchJobSchema,
  result: AgentResearchResultSchema.optional(),
  runtimeStatus: AgentResearchRuntimeStatusSchema.optional(),
  usageSummary: AgentResearchUsageSummarySchema.optional()
});

export const AgentResearchJobListResponseSchema = z.object({
  items: z.array(AgentResearchJobSchema),
  count: z.number().int().nonnegative()
});

export const AgentResearchJobCancelResponseSchema = z.object({
  jobId: z.string().min(1),
  // Cancelling an already-terminal job echoes its stored status; this enum
  // lists the statuses the cancel route can reach (`erased` jobs are not
  // cancellable through this route).
  status: z.enum(["cancel_requested", "canceled", "succeeded", "failed", "waiting_device"]),
  canceledAt: z.string().datetime().optional()
});

export const AgentResearchMaterializeChangeSetResponseSchema = z.object({
  jobId: z.string().min(1),
  frameRecordId: z.string().min(1),
  changeSet: GraphChangeSetCreateResponseSchema.shape.changeSet,
  auditLogId: z.string().min(1),
  applied: z.literal(false),
  nextAction: z.literal("review_change_set")
});

export const AgentResearchToolIdSchema = z.enum([
  "salesko.graph.read_job_context",
  "salesko.graph.submit_proposal",
  "public_search.search",
  "public_http.fetch_allowlisted",
  "workspace.read",
  "workspace.write",
  "sandbox.exec",
  "provider.generate"
]);

export const AgentResearchDefaultAllowedTools = [
  "salesko.graph.read_job_context",
  "salesko.graph.submit_proposal",
  "public_search.search",
  "public_http.fetch_allowlisted",
  "workspace.read",
  "workspace.write",
  "sandbox.exec",
  "provider.generate"
] as const;

export const AgentResearchContextResponseSchema = z.object({
  job: AgentResearchJobSchema,
  baseFrame: GraphVisualizationFrameSchema,
  currentFrameVersion: z.number().int().positive(),
  targetEntity: GraphCanonicalEntitySchema.optional(),
  neighbourhood: z.object({
    nodes: z.array(GraphReadNodeSchema).default([]),
    edges: z.array(GraphReadEdgeSchema).default([])
  }),
  allowedTools: z.array(AgentResearchToolIdSchema).default([...AgentResearchDefaultAllowedTools])
});

export const AgentResearchCallbackGrantSchema = z.object({
  iss: z.literal("salesko-api"),
  aud: z.literal(AgentResearchCallbackAudience),
  sub: z.string().min(1),
  tenantRef: z.string().min(16).max(256),
  scopes: z.array(AgentResearchCallbackScopeSchema).min(1),
  frameRecordId: z.string().min(1),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
  jti: z.string().min(1).max(160),
  kid: z.string().min(1).max(160).optional()
});

export const AgentResearchFastclawToolPolicySchema = z.object({
  required_sequence: z.array(z.string().min(1)).min(1).max(8),
  max_no_tool_retries: z.number().int().nonnegative().max(5).default(2)
});

export const AgentResearchDispatchParamsSchema = z.object({
  salesko_job_id: z.string().min(1),
  dataset_scope: z.string().min(1).default("salesko"),
  tenant_id: z.string().min(1),
  frame_record_id: z.string().min(1),
  expected_frame_version: z.number().int().positive(),
  target_canonical_entity_id: z.string().min(1),
  mode: AgentResearchModeSchema,
  agent_profile: AgentResearchAgentProfileSchema,
  locale_seed: AgentResearchLocaleSeedSchema,
  messaging_channels: z.array(AgentResearchMessagingChannelSchema).max(3).default([]),
  budget: AgentResearchBudgetSchema,
  /**
   * Legacy fallback for MCP runtimes that cannot mint job-scoped callback grants.
   * New Salesko MCP servers should mint the grant server-side from tenant/job
   * params so the model never has to copy a bearer token into tool args.
   */
  capability_grant: z.string().min(1),
  /**
   * FastClaw runtime policy for API-owned long-running jobs. This is opt-in
   * request metadata consumed by FastClaw, not a Salesko App authority surface.
   */
  fastclaw_tool_policy: AgentResearchFastclawToolPolicySchema.optional()
});

export const AgentResearchUsageEventSchema = z.object({
  eventId: z.string().min(1).max(160),
  producer: z.literal(AgentResearchUsageEventProducer),
  jobId: z.string().min(1),
  operation: AgentResearchUsageOperationSchema,
  provider: z.string().min(1).max(120).optional(),
  model: z.string().min(1).max(160).optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  toolUnits: z.number().int().nonnegative().optional(),
  sandboxRunningMs: z.number().int().nonnegative().optional(),
  sandboxCpuMs: z.number().int().nonnegative().optional(),
  readBytes: z.number().int().nonnegative().optional(),
  writeBytes: z.number().int().nonnegative().optional(),
  storageBytes: z.number().int().nonnegative().optional(),
  costMicros: z.number().int().nonnegative().optional(),
  occurredAt: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).default({})
});

export const AgentResearchUsageBatchSchema = z.object({
  events: z.array(AgentResearchUsageEventSchema).min(1).max(500)
});
export const AgentResearchUsageRecordResponseSchema = z.object({
  acceptedCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative()
});

export const PersonalAgentExecutionSchemaVersion = "salesko.personal_agent_execution.v1" as const;
export const PersonalAgentWorkspaceSchemaVersion = "salesko.personal_agent_workspace.v1" as const;
export const PersonalAgentUsageSchemaVersion = "salesko.personal_agent_usage.v1" as const;
export const PersonalAgentUsageEventProducer = "salesko-sidecar" as const;

const PersonalAgentOpaqueIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9][A-Za-z0-9:_./-]*$/);
const PersonalAgentOpaqueRefSchema = z.string().trim().min(8).max(256);
const PersonalAgentSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const PersonalAgentMetadataValueSchema = z.union([z.string().trim().max(256), z.number(), z.boolean(), z.null()]);
const PersonalAgentMetadataSchema = z.record(z.string().trim().min(1).max(64), PersonalAgentMetadataValueSchema).default({});
export const PersonalAgentWorkspacePathSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .refine(
    (path) =>
      !path.startsWith("/") &&
      !path.includes("\\") &&
      path
        .split("/")
        .every((part) => part.length > 0 && part !== "." && part !== ".."),
    {
      message: "Workspace paths must be relative normalized paths without traversal segments."
    }
  );

export const PersonalAgentExecutionStatusSchema = z.enum([
  "queued",
  "retry_wait",
  "claimed",
  "running",
  "waiting_provider",
  "waiting_sandbox",
  "checkpointing",
  "cancel_requested",
  "succeeded",
  "failed",
  "canceled",
  "erased"
]);
export const PersonalAgentExecutionKindSchema = z.enum(["relationship_research"]);
export const PersonalAgentStepTypeSchema = z.enum([
  "llm.generate",
  "search.query",
  "http.fetch",
  "sandbox.exec",
  "workspace.checkpoint",
  "graph.proposal",
  "usage.settle",
  "callback"
]);
export const PersonalAgentStepStatusSchema = z.enum(["pending", "running", "checkpointing", "succeeded", "failed", "skipped", "canceled"]);
export const PersonalAgentRuntimeProviderIdSchema = z.enum(["salesko-sidecar", "fastclaw-runtime-adapter", "fake-runtime"]);
export const PersonalAgentSandboxProviderIdSchema = z.enum(["e2b", "fake-sandbox"]);
export const PersonalAgentWorkspaceProviderIdSchema = z.enum(["r2-manifest", "drive9", "fake-workspace"]);
export const PersonalAgentUsageSinkIdSchema = z.enum(["app-usage-outbox", "fake-usage"]);
export const PersonalAgentErrorCodeSchema = z.enum([
  "budget_exceeded",
  "provider_rate_limited",
  "provider_unavailable",
  "sandbox_unavailable",
  "sandbox_lost",
  "workspace_conflict",
  "workspace_missing_blob",
  "invalid_graph_proposal",
  "stale_frame_version",
  "tenant_forbidden",
  "feature_disabled",
  "erasure_tombstoned",
  "unknown_provider",
  "internal_error"
]);

// --- BYOK provider credential state (S00 contract boundary; S01 `salesko_sidecar.provider_state`) ---
// A principal may bring their own LLM provider key. The raw key is owned by the
// Sidecar runtime (pass-through): it is NEVER stored in the App DB and NEVER
// returned to the Browser. Only this non-secret STATE crosses the App boundary.
// Selecting `byok` shifts model/provider COGS to the principal; it does NOT relax
// App entitlement — paid-feature access stays App-authoritative (S07 boundary).
// Runtime set/delete of the key is deferred (not part of S00/S01).
export const PersonalAgentLlmProviderSchema = z.enum(["anthropic", "openai", "openrouter"]);
export const PersonalAgentProviderCredentialSourceSchema = z.enum(["managed", "byok"]);
// Allows OpenRouter `vendor/model` ids; the runtime splits `${provider}/${model}`
// on the FIRST slash to pick the provider.
export const PersonalAgentProviderModelSchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]*$/);
export const PersonalAgentProviderStateSchema = z
  .object({
    schemaVersion: z.literal("salesko.personal_agent_provider_state.v1"),
    tenantRef: PersonalAgentOpaqueRefSchema,
    userRef: PersonalAgentOpaqueRefSchema,
    source: PersonalAgentProviderCredentialSourceSchema.default("managed"),
    enabled: z.boolean().default(false),
    provider: PersonalAgentLlmProviderSchema.optional(),
    model: PersonalAgentProviderModelSchema.optional(),
    // Non-secret masked display only (e.g. "sk-ant-…abcd"). Capped well below any
    // real provider key length so a full key structurally cannot fit here.
    keyPrefix: z.string().trim().min(1).max(20).optional(),
    // Opaque handle to the Sidecar-owned credential; must be a `cred_` handle,
    // never a provider-key-shaped value (which would start `sk-`/`pk-` etc.).
    credentialRef: z
      .string()
      .trim()
      .regex(/^cred_[A-Za-z0-9._-]{1,120}$/)
      .optional(),
    updatedAt: z.string().datetime()
  })
  .refine((state) => state.source === "managed" || (state.provider != null && state.model != null), {
    message: "byok provider state requires provider and model"
  });

export const PersonalAgentExecutionBudgetSchema = z
  .object({
    maxSteps: z.number().int().min(1).max(256).optional(),
    maxWallClockSeconds: z.number().int().min(30).max(86_400).optional(),
    maxProviderCalls: z.number().int().min(1).max(512).optional(),
    maxInputTokens: z.number().int().min(1).max(2_000_000).optional(),
    maxOutputTokens: z.number().int().min(1).max(500_000).optional(),
    maxSandboxSeconds: z.number().int().min(1).max(86_400).optional(),
    maxWorkspaceReadBytes: z.number().int().min(1).max(10_000_000_000).optional(),
    maxWorkspaceWriteBytes: z.number().int().min(1).max(10_000_000_000).optional(),
    maxArtifactBytes: z.number().int().min(1).max(5_000_000_000).optional(),
    maxResultBytes: z.number().int().min(1_024).max(2_000_000).optional()
  })
  .default({});

export const PersonalAgentFeatureFlagsSchema = z.object({
  personalAgentEnabled: z.boolean().default(false),
  sidecarControlPlaneEnabled: z.boolean().default(false),
  e2bExecutionEnabled: z.boolean().default(false),
  persistentWorkspaceEnabled: z.boolean().default(false),
  drive9WorkspaceEnabled: z.boolean().default(false),
  fastclawRuntimeAdapterEnabled: z.boolean().default(false),
  redisCoordinationEnabled: z.boolean().default(false),
  managedQueueEnabled: z.boolean().default(false)
});

export const PersonalAgentExecutionCreateSchema = z.object({
  schemaVersion: z.literal("salesko.personal_agent_execution_create.v1"),
  appJobId: PersonalAgentOpaqueIdSchema,
  clientRequestId: z.string().trim().min(1).max(128),
  idempotencyKey: z.string().trim().min(1).max(160),
  kind: PersonalAgentExecutionKindSchema.default("relationship_research"),
  tenantRef: PersonalAgentOpaqueRefSchema,
  userRef: PersonalAgentOpaqueRefSchema,
  laneKey: z.string().trim().min(1).max(256),
  conversationKey: z.string().trim().min(1).max(256).optional(),
  priority: z.number().int().min(0).max(100).default(50),
  frameRecordId: z.string().min(1).max(160),
  baseFrameVersion: z.number().int().positive(),
  targetCanonicalEntityId: z.string().trim().min(1).max(256),
  requestedBudget: PersonalAgentExecutionBudgetSchema.optional(),
  workspaceId: PersonalAgentOpaqueIdSchema.optional(),
  sessionId: PersonalAgentOpaqueIdSchema.optional()
});

export const PersonalAgentExecutionRecordSchema = z.object({
  schemaVersion: z.literal(PersonalAgentExecutionSchemaVersion),
  executionId: PersonalAgentOpaqueIdSchema,
  appJobId: PersonalAgentOpaqueIdSchema,
  tenantRef: PersonalAgentOpaqueRefSchema,
  userRef: PersonalAgentOpaqueRefSchema,
  kind: PersonalAgentExecutionKindSchema,
  status: PersonalAgentExecutionStatusSchema,
  laneKey: z.string().trim().min(1).max(256),
  conversationKey: z.string().trim().min(1).max(256).optional(),
  priority: z.number().int().min(0).max(100),
  version: z.number().int().positive(),
  attemptCount: z.number().int().nonnegative().default(0),
  availableAt: z.string().datetime().optional(),
  budget: PersonalAgentExecutionBudgetSchema,
  runtimeProvider: PersonalAgentRuntimeProviderIdSchema,
  workspaceId: PersonalAgentOpaqueIdSchema.optional(),
  sessionId: PersonalAgentOpaqueIdSchema.optional(),
  currentStepId: PersonalAgentOpaqueIdSchema.optional(),
  leaseOwner: z.string().trim().min(1).max(160).optional(),
  leaseExpiresAt: z.string().datetime().optional(),
  cancelRequestedAt: z.string().datetime().optional(),
  terminalError: z
    .object({
      code: PersonalAgentErrorCodeSchema,
      message: z.string().trim().min(1).max(500).optional()
    })
    .optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().optional()
});

export const PersonalAgentExecutionStepSchema = z.object({
  schemaVersion: z.literal("salesko.personal_agent_execution_step.v1"),
  stepId: PersonalAgentOpaqueIdSchema,
  executionId: PersonalAgentOpaqueIdSchema,
  sequence: z.number().int().nonnegative(),
  type: PersonalAgentStepTypeSchema,
  status: PersonalAgentStepStatusSchema,
  idempotencyKey: z.string().trim().min(1).max(160),
  inputDigest: PersonalAgentSha256Schema.optional(),
  outputDigest: PersonalAgentSha256Schema.optional(),
  checkpointRef: PersonalAgentOpaqueIdSchema.optional(),
  usageEventIds: z.array(PersonalAgentOpaqueIdSchema).max(50).default([]),
  errorCode: PersonalAgentErrorCodeSchema.optional(),
  metadata: PersonalAgentMetadataSchema,
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional()
});

export const PersonalAgentSandboxLifecycleStateSchema = z.enum([
  "reserved",
  "creating",
  "running",
  "pausing",
  "paused",
  "resuming",
  "killing",
  "killed",
  "lost",
  "error"
]);
export const PersonalAgentSandboxMetadataSchema = z.object({
  executionRef: PersonalAgentOpaqueRefSchema,
  workspaceRef: PersonalAgentOpaqueRefSchema.optional(),
  environment: z.enum(["local", "staging", "production"]),
  sandboxTemplateVersion: z.string().trim().min(1).max(160),
  createAttemptRef: PersonalAgentOpaqueIdSchema
});
export const PersonalAgentSandboxBindingSchema = z.object({
  schemaVersion: z.literal("salesko.personal_agent_sandbox_binding.v1"),
  bindingId: PersonalAgentOpaqueIdSchema,
  executionId: PersonalAgentOpaqueIdSchema,
  provider: PersonalAgentSandboxProviderIdSchema,
  state: PersonalAgentSandboxLifecycleStateSchema,
  sandboxRef: PersonalAgentOpaqueRefSchema.optional(),
  workspaceId: PersonalAgentOpaqueIdSchema.optional(),
  metadata: PersonalAgentSandboxMetadataSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  expiresAt: z.string().datetime().optional()
});

export const PersonalAgentWorkspaceBindingSchema = z.object({
  schemaVersion: z.literal(PersonalAgentWorkspaceSchemaVersion),
  workspaceId: PersonalAgentOpaqueIdSchema,
  provider: PersonalAgentWorkspaceProviderIdSchema,
  tenantRef: PersonalAgentOpaqueRefSchema,
  userRef: PersonalAgentOpaqueRefSchema,
  headRevisionId: PersonalAgentOpaqueIdSchema.optional(),
  activeWriterExecutionId: PersonalAgentOpaqueIdSchema.optional(),
  tombstonedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const PersonalAgentWorkspaceManifestEntrySchema = z.object({
  path: PersonalAgentWorkspacePathSchema,
  mode: z.enum(["file", "directory", "symlink"]).default("file"),
  sizeBytes: z.number().int().min(0).max(1_000_000_000),
  digestSha256: PersonalAgentSha256Schema.optional(),
  contentType: z.string().trim().min(1).max(160).optional()
});
export const PersonalAgentWorkspaceRevisionSchema = z.object({
  schemaVersion: z.literal("salesko.personal_agent_workspace_revision.v1"),
  workspaceId: PersonalAgentOpaqueIdSchema,
  revisionId: PersonalAgentOpaqueIdSchema,
  baseRevisionId: PersonalAgentOpaqueIdSchema.optional(),
  rootHash: PersonalAgentSha256Schema,
  creatorExecutionId: PersonalAgentOpaqueIdSchema,
  fileCount: z.number().int().min(0).max(2_000),
  totalBytes: z.number().int().min(0).max(10_000_000_000),
  changedPaths: z.array(PersonalAgentWorkspacePathSchema).max(500).default([]),
  files: z.array(PersonalAgentWorkspaceManifestEntrySchema).max(2_000).default([]),
  createdAt: z.string().datetime()
});
export const PersonalAgentWorkspaceArtifactSchema = z.object({
  schemaVersion: z.literal("salesko.personal_agent_workspace_artifact.v1"),
  artifactId: PersonalAgentOpaqueIdSchema,
  workspaceId: PersonalAgentOpaqueIdSchema,
  executionId: PersonalAgentOpaqueIdSchema,
  revisionId: PersonalAgentOpaqueIdSchema.optional(),
  role: z.enum(["evidence", "report", "patch", "source", "log"]),
  path: PersonalAgentWorkspacePathSchema,
  contentType: z.string().trim().min(1).max(160),
  byteSize: z.number().int().min(0).max(250_000_000),
  digestSha256: PersonalAgentSha256Schema,
  createdAt: z.string().datetime()
});

export const PersonalAgentUsageOperationSchema = z.enum([
  "llm.generate",
  "search.query",
  "http.fetch",
  "sandbox.running_ms",
  "sandbox.create",
  "workspace.read_bytes",
  "workspace.write_bytes",
  "artifact.storage_bytes"
]);
export const PersonalAgentUsageEventSchema = z.object({
  schemaVersion: z.literal(PersonalAgentUsageSchemaVersion),
  eventId: z.string().trim().min(1).max(160),
  producer: z.literal(PersonalAgentUsageEventProducer),
  executionId: PersonalAgentOpaqueIdSchema,
  appJobId: PersonalAgentOpaqueIdSchema.optional(),
  idempotencyKey: z.string().trim().min(1).max(160),
  operation: PersonalAgentUsageOperationSchema,
  provider: z.string().trim().min(1).max(120).optional(),
  model: z.string().trim().min(1).max(160).optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  toolUnits: z.number().int().nonnegative().optional(),
  sandboxRunningMs: z.number().int().nonnegative().optional(),
  readBytes: z.number().int().nonnegative().optional(),
  writeBytes: z.number().int().nonnegative().optional(),
  storageBytes: z.number().int().nonnegative().optional(),
  costMicros: z.number().int().nonnegative().optional(),
  metadata: PersonalAgentMetadataSchema,
  occurredAt: z.string().datetime()
});
export const PersonalAgentResearchResultSchema = AgentResearchResultSchema.extend({
  workspaceRevisionId: PersonalAgentOpaqueIdSchema.optional(),
  artifacts: z.array(PersonalAgentWorkspaceArtifactSchema).max(100).default([])
});

// --- Personal Agent device pairing (hosted coordination, no provider credential) ---
// The API projects only the device fields the signed-in owner needs to manage
// pairing. Public keys and admin bearers remain inside the control plane.
export const PersonalAgentDeviceIdSchema = z.string().trim().min(1).max(200);
export const PersonalAgentDeviceSchema = z.object({
  productId: z.string().trim().min(1).max(160),
  deviceId: PersonalAgentDeviceIdSchema,
  deviceName: z.string().trim().min(1).max(256),
  proofKeyId: z.string().trim().min(1).max(160),
  proofKeyEpoch: z.number().int().nonnegative(),
  // Stable per-machine fingerprint the daemon reports at pairing time. It is
  // optional because devices paired before the control plane recorded it have
  // none, and every consumer must keep working without it.
  machineId: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
  // The capability strings the daemon declared at pairing time, projected
  // verbatim from the control plane's device record. It is optional because a
  // control plane that predates the declaration reports none, and every
  // consumer must keep working without it. Absent is not "no capabilities":
  // readiness treats an absent list as an unmet requirement, never as proof.
  capabilities: z.array(z.string().min(1).max(120)).max(64).optional(),
  revoked: z.boolean()
});
export const PersonalAgentPairingCodeResponseSchema = z.object({
  tenantId: TenantIdSchema,
  // A short-lived, single-use enrollment code. This is intentionally distinct
  // from a provider credential, which never traverses the web/API boundary.
  pairingCode: z.string().trim().min(1).max(256),
  expiresAt: z.string().datetime()
});
export const PersonalAgentDeviceListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  devices: z.array(PersonalAgentDeviceSchema)
});
export const PersonalAgentDeviceRevokeRequestSchema = z.object({
  deviceId: PersonalAgentDeviceIdSchema
});
export const PersonalAgentDeviceRevokeResponseSchema = z.object({
  tenantId: TenantIdSchema,
  deviceId: PersonalAgentDeviceIdSchema,
  revoked: z.literal(true)
});
export const PersonalAgentPresenceLevelSchema = z.enum(["online", "thinking", "working", "error", "offline"]);
// Presence is a lossy TTL hint from the paired daemon. It is deliberately not
// a readiness, execution, authorization, or billing state.
// Mirrors the SDK `PresenceRuntimeFact`: a daemon's local probe result. Unknown
// facts are omitted rather than defaulted, and `authPresent` is a masked
// capability bit, never a credential.
export const PersonalAgentPresenceRuntimeFactSchema = z.object({
  id: z.string().trim().min(1).max(160),
  version: z.string().trim().min(1).max(160).optional(),
  authPresent: z.boolean().optional()
});
export const PersonalAgentPresenceHintSchema = z.object({
  deviceId: PersonalAgentDeviceIdSchema,
  level: PersonalAgentPresenceLevelSchema,
  detail: z.string().trim().min(1).max(512).optional(),
  configuredToolsets: z.array(z.string().trim().min(1).max(160)).max(100).optional(),
  runtimes: z.array(PersonalAgentPresenceRuntimeFactSchema).max(50).optional(),
  // The Local Agent release the daemon reported (SDK `clientVersion`).
  localAgentRelease: z.string().trim().min(1).max(160).optional(),
  observedAt: z.string().datetime(),
  expiresAt: z.string().datetime()
});
export const PersonalAgentPresenceHintListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  presenceHints: z.array(PersonalAgentPresenceHintSchema)
});

// --- Private Agent Chat capability requirements ---
// The exact Agent capabilities byok-control admits a chat turn against. This
// is the single source of truth for both the admitting Worker
// (apps/byok-control/src/private-agent-chat.ts) and the server-side readiness
// authority (apps/api/src/private-agent-readiness.ts), so a device that would
// be refused at dispatch is reported "upgrade_required" before a turn exists.
// Every value is the @byok-sdk/protocol constant, except the cloud-owned
// agent-home contract, which lives in @byok-sdk/cloud — a package this
// browser-reachable contract deliberately does not depend on. The byok-control
// test asserts these tuples equal the SDK constants, so drift fails CI.
export const PrivateAgentAgentHomeContractCapability = "agent-home-contract" as const;
export const PrivateAgentChatBootstrapCapabilities = [
  HOST_MCP_TASK_CONTEXT_CAPABILITY,
  AGENT_INPUT_PREPARATION_CAPABILITY,
  PrivateAgentAgentHomeContractCapability,
  AGENT_EGRESS_POLICY_CAPABILITY,
  AGENT_EGRESS_RELIABLE_ACK_CAPABILITY,
  AGENT_EGRESS_FRESH_SESSION_CAPABILITY,
  AGENT_MESSAGE_EGRESS_CAPABILITY,
  TERMINAL_PROJECTION_SELECTION_CAPABILITY
] as const;
export const PrivateAgentChatResumeCapabilities = [
  PrivateAgentAgentHomeContractCapability,
  AGENT_EGRESS_POLICY_CAPABILITY,
  AGENT_EGRESS_RELIABLE_ACK_CAPABILITY,
  AGENT_MESSAGE_EGRESS_CAPABILITY,
  TERMINAL_PROJECTION_SELECTION_CAPABILITY
] as const;

// --- Private Agent chat readiness (server-side authority) ---
// Presence is a lossy TTL hint and was never a right to chat. This projection
// is the one server-computed answer to "can this owner send a turn right now",
// so the web renders a decision it did not make. `chat: "ready"` is the only
// value that admits a turn; every other state carries the single
// `blockedReason` that produced it.
export const PrivateAgentReadinessSchemaVersion = "salesko.private_agent_readiness.v1" as const;
export const PrivateAgentReadinessPresenceSchema = z.enum(["online", "offline", "missing"]);
export const PrivateAgentReadinessCompatibilitySchema = z.enum(["compatible", "upgrade_required"]);
// `unknown` is a control plane that reported no runtime facts at all. It is
// deliberately not a block: the daemon simply did not answer, and refusing on
// silence would deadlock a working computer.
export const PrivateAgentReadinessRuntimeSchema = z.enum(["ready", "missing", "not_authenticated", "unknown"]);
export const PrivateAgentReadinessBindingSchema = z.enum(["valid", "revoked", "missing", "profile_missing"]);
export const PrivateAgentReadinessChatSchema = z.enum(["ready", "blocked"]);
export const PrivateAgentReadinessBlockedReasonSchema = z.enum([
  "profile_missing",
  "device_missing",
  "device_revoked",
  "device_offline",
  "agent_upgrade_required",
  "runtime_missing",
  "runtime_not_authenticated",
  "toolset_not_configured",
  "runtime_unsupported_for_tools",
  "profile_identity_unavailable"
]);
export const PrivateAgentReadinessSchema = z
  .object({
    agentId: z.lazy(() => PrivateAgentAgentIdSchema),
    placementRevision: z.lazy(() => PrivateAgentPlacementRevisionSchema),
    schemaVersion: z.literal(PrivateAgentReadinessSchemaVersion),
    presence: PrivateAgentReadinessPresenceSchema,
    compatibility: PrivateAgentReadinessCompatibilitySchema,
    runtime: PrivateAgentReadinessRuntimeSchema,
    binding: PrivateAgentReadinessBindingSchema,
    chat: PrivateAgentReadinessChatSchema,
    blockedReason: PrivateAgentReadinessBlockedReasonSchema.optional(),
    deviceId: PersonalAgentDeviceIdSchema.optional(),
    installedRelease: z.string().trim().min(1).max(160).optional(),
    missingCapabilities: z.array(z.string().min(1).max(120)).max(64).optional()
  })
  .strict();
export const PrivateAgentReadinessResponseSchema = z
  .object({ readiness: PrivateAgentReadinessSchema })
  .strict();

// The one API error code that means "the server refused this turn because the
// Private Agent is not chat-ready". Its 409 body carries the readiness above,
// so the caller never re-derives the reason.
export const PrivateAgentChatNotReadyErrorCode = "private_agent_chat_not_ready" as const;

// --- Agent-scoped Profile content; Placement independently owns device routing. ---
export const PrivateAgentRuntimePreferenceSchema = z.enum([
  "claude_subscription",
  "codex_subscription",
  "pi_provider_key"
]);
export const PrivateAgentPiModelIdSchema = z.string().trim().min(1).max(160).regex(/^[^\u0000\r\n]+$/u);

// This UUID is generated exactly once by the cloud Profile row. It is opaque
// to every consumer, but the lower-case UUID shape is a portable single
// pathname segment for the SDK-owned Agent home.
export const PrivateAgentAgentIdSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u,
    "agentId must be a lower-case RFC 4122 UUID."
  );

// PostgreSQL BIGINT travels across every JavaScript boundary as its canonical
// decimal text. Keep this lexical: Number would lose exactness above 2^53 - 1.
export const PrivateAgentProfileRevisionMaximum = "9223372036854775807" as const;
export const PrivateAgentProfileRevisionSchema = z
  .string()
  .regex(/^[1-9][0-9]{0,18}$/u, "profileRevision must be a canonical positive decimal string.")
  .refine(
    (value) => value.length < PrivateAgentProfileRevisionMaximum.length || value <= PrivateAgentProfileRevisionMaximum,
    `profileRevision must not exceed PostgreSQL BIGINT maximum ${PrivateAgentProfileRevisionMaximum}.`
  );

function validatePrivateAgentPiSelection(
  profile: { runtimePreference: z.infer<typeof PrivateAgentRuntimePreferenceSchema>;
    providerProfile?: z.infer<typeof ProviderProfileBindingSchema> | undefined;
    subscriptionModelId?: string | undefined }, context: z.RefinementCtx
) {
  const isPi = profile.runtimePreference === "pi_provider_key";
  if (isPi && profile.subscriptionModelId !== undefined) {
    context.addIssue({ code: "custom", path: ["subscriptionModelId"], message: "Pi runtime cannot carry a subscription model." });
  }
  if (isPi && profile.providerProfile === undefined) {
    context.addIssue({ code: "custom", path: ["providerProfile"], message: "Pi runtime requires an exact local provider binding." });
  }
  if (!isPi && profile.providerProfile !== undefined) {
    context.addIssue({ code: "custom", path: ["providerProfile"], message: "Subscription runtimes cannot carry a provider binding." });
  }
}

/**
 * One device-reported preparation model record, kept read-only under the exact
 * profile triple it was reported for.
 *
 * The record itself is the SDK's own model shape (shape validation only — the
 * Host never projects it, fills a field or recomputes the profile hash): it is
 * the model the device will count against, and the Host's job is to carry it
 * verbatim. The triple is what makes it addressable: a request resolves the
 * record by the CURRENT `(profileRef, profileRevision, profileHash)`, so a
 * changed hash simply stops resolving to the old entry.
 */
// Profile content is strict; incomplete legacy rows require operator repair before cutover.
export const PrivateAgentProfileSchema = z
  .object({
    agentId: PrivateAgentAgentIdSchema,
    profileRevision: PrivateAgentProfileRevisionSchema,
    name: z.string().trim().min(1).max(80),
    runtimePreference: PrivateAgentRuntimePreferenceSchema,
    providerProfile: ProviderProfileBindingSchema.optional(),
    subscriptionModelId: PrivateAgentPiModelIdSchema.optional(),
    // The regional research preset this agent dispatches with. It is the only
    // authority for that preset — `SaleskoAgentSettings` no longer carries one —
    // and it is required, so a tenant without a Private Agent cannot start
    // research at all rather than falling back to a guessed default.
    researchPreset: AgentResearchAgentProfileValueSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
  })
  .strict().superRefine(validatePrivateAgentPiSelection);
export const PrivateAgentProfileWriteSchema = z.object({
  name: z.string().trim().min(1).max(80),
  runtimePreference: PrivateAgentRuntimePreferenceSchema,
  providerProfile: ProviderProfileBindingSchema.optional(),
  subscriptionModelId: PrivateAgentPiModelIdSchema.optional(),
  researchPreset: AgentResearchAgentProfileValueSchema
}).strict().superRefine(validatePrivateAgentPiSelection);
export const PrivateAgentPlacementRevisionSchema = PrivateAgentProfileRevisionSchema;
export const PrivateAgentPlacementSchema = z.object({
  agentId: PrivateAgentAgentIdSchema,
  placementRevision: PrivateAgentPlacementRevisionSchema,
  deviceId: PersonalAgentDeviceIdSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
}).strict();
export const PrivateAgentPlacementWriteSchema = z.object({ deviceId: PersonalAgentDeviceIdSchema }).strict();
export const PrivateAgentPlacementUpdateRequestSchema = z.object({
  deviceId: PersonalAgentDeviceIdSchema,
  expectedPlacementRevision: PrivateAgentPlacementRevisionSchema
}).strict();
export const PrivateAgentSchema = z.object({
  profile: PrivateAgentProfileSchema,
  placement: PrivateAgentPlacementSchema
}).strict().refine(value => value.profile.agentId === value.placement.agentId, {
  path: ["placement", "agentId"], message: "Placement must belong to the selected Agent."
});
export const PrivateAgentCreateRequestSchema = z.object({
  profile: PrivateAgentProfileWriteSchema,
  placement: PrivateAgentPlacementWriteSchema
}).strict();
export const PrivateAgentProfileResponseSchema = z.object({
  tenantId: TenantIdSchema,
  profile: PrivateAgentProfileSchema,
  placement: PrivateAgentPlacementSchema
}).strict();

// The SDK projection hook receives only the durable AgentRef. Product Profile
// fields cannot cross this task-time v1 boundary. This marker is retained only
// until the task-free product projection RC replaces the current daemon hook;
// it is not evidence that Profile creation synchronized to the device.
export const PrivateAgentProfileRedactedProjectionSchemaVersion = "salesko.private_agent_profile_projection.v1" as const;
export const PrivateAgentProfileRedactedProjectionSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentProfileRedactedProjectionSchemaVersion),
    agentId: PrivateAgentAgentIdSchema,
    profileRevision: PrivateAgentProfileRevisionSchema
  })
  .strict();

/**
 * Salesko-owned bounded product content for a device-local Profile projection.
 * Routing and delivery metadata are deliberately outside this payload.
 */
export const PrivateAgentProfileProductProjectionSchemaVersion =
  "salesko.private_agent_profile_product_projection.v2" as const;
export const PrivateAgentProfileProductProjectionSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentProfileProductProjectionSchemaVersion),
    agentId: PrivateAgentAgentIdSchema,
    profileRevision: PrivateAgentProfileRevisionSchema,
    name: z.string().trim().min(1).max(80),
    runtimePreference: PrivateAgentRuntimePreferenceSchema,
    providerProfile: ProviderProfileBindingSchema.optional(),
    subscriptionModelId: PrivateAgentPiModelIdSchema.optional(),
    researchPreset: AgentResearchAgentProfileValueSchema
  })
  .strict()
  .superRefine(validatePrivateAgentPiSelection);

export const PrivateAgentProfileProjectionDispatchSchemaVersion =
  "salesko.private_agent_profile_projection_dispatch.v1" as const;
export const PrivateAgentProfileProjectionDispatchSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentProfileProjectionDispatchSchemaVersion),
    tenantRef: z.string().regex(/^[0-9a-f]{32}$/u),
    deviceId: PersonalAgentDeviceIdSchema,
    projection: PrivateAgentProfileProductProjectionSchema,
    projectionHash: SaleskoOpaqueSha256Schema
  })
  .strict();

export const PrivateAgentProfileProjectionSyncStatusSchema = z.enum(["pending", "synced", "failed"]);
export const PrivateAgentProfileProjectionSyncStateSchema = z
  .object({
    status: PrivateAgentProfileProjectionSyncStatusSchema,
    profileRevision: PrivateAgentProfileRevisionSchema,
    projectionHash: SaleskoOpaqueSha256Schema
  })
  .strict();

/** Shared product authority for every Agent execution and device policy gate. */
export const PrivateAgentExecutionSnapshotSchemaVersion = "salesko.private_agent_execution.v4" as const;
export const PrivateAgentCanonicalHomeCwdBinding = "byok-agent-home" as const;
export const PrivateAgentEgressPolicyRevision = "salesko.agent-egress.metadata-status.v1" as const;
export const PrivateAgentEgressPolicy: AgentEgressPolicy = AgentEgressPolicySchema.parse({
  policyRevision: PrivateAgentEgressPolicyRevision,
  activity: { mode: "metadata-status", delivery: "latest-value" },
  reliable: {
    maxPendingEventsPerAgent: 256,
    maxPendingBytesPerAgent: 8_388_608,
    maxPendingBytesPerTenant: 67_108_864
  },
  transfers: {
    workspace: "disabled",
    transcript: "disabled",
    artifact: "disabled"
  }
});

export function isPrivateAgentMetadataStatusPolicy(policy: AgentEgressPolicy): boolean {
  return policy.policyRevision === PrivateAgentEgressPolicyRevision
    && policy.activity.mode === "metadata-status"
    && policy.activity.delivery === "latest-value"
    && policy.reliable.maxPendingEventsPerAgent === PrivateAgentEgressPolicy.reliable.maxPendingEventsPerAgent
    && policy.reliable.maxPendingBytesPerAgent === PrivateAgentEgressPolicy.reliable.maxPendingBytesPerAgent
    && policy.reliable.maxPendingBytesPerTenant === PrivateAgentEgressPolicy.reliable.maxPendingBytesPerTenant
    && policy.transfers.workspace === "disabled"
    && policy.transfers.transcript === "disabled"
    && policy.transfers.artifact === "disabled";
}

const PrivateAgentRefSchema = AgentRefSchema.superRefine((agentRef, context) => {
  if (!PrivateAgentAgentIdSchema.safeParse(agentRef.agentId).success) {
    context.addIssue({ code: "custom", path: ["agentId"], message: "AgentRef agentId must be the Profile's opaque UUID." });
  }
  if (!PrivateAgentProfileRevisionSchema.safeParse(agentRef.profileRevision).success) {
    context.addIssue({
      code: "custom",
      path: ["profileRevision"],
      message: "AgentRef profileRevision must be the Profile's canonical positive BIGINT decimal string."
    });
  }
});

function validatePrivateAgentRuntimeSelection(
  execution: {
    runtime: z.infer<typeof RuntimeIdSchema>;
    dispatchSelection?: z.infer<typeof DispatchSelectionSchema> | undefined;
  },
  context: z.RefinementCtx
) {
  if (execution.runtime === "pi") {
    // These schemas also decode immutable historical offers. The flat SDK lane
    // remains exact historical data; current Profile producers only author byok-profile.
    if ((execution.dispatchSelection?.lane !== "byok" && execution.dispatchSelection?.lane !== "byok-profile") || execution.dispatchSelection.runtimeId !== "pi") {
      context.addIssue({
        code: "custom",
        path: ["dispatchSelection"],
        message: "Pi execution requires a Pi BYOK dispatch selection."
      });
    }
    return;
  }
  if (execution.dispatchSelection !== undefined &&
      (execution.dispatchSelection.lane !== "subscription" || execution.dispatchSelection.runtimeId !== execution.runtime)) {
    context.addIssue({
      code: "custom",
      path: ["dispatchSelection"],
      message: "Subscription execution requires a matching subscription runtime selection."
    });
  }
}

export const PrivateAgentExecutionSnapshotSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentExecutionSnapshotSchemaVersion),
    agentRef: PrivateAgentRefSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema,
    runtime: RuntimeIdSchema,
    cwd: z.literal(PrivateAgentCanonicalHomeCwdBinding),
    researchPreset: AgentResearchAgentProfileValueSchema,
    egressPolicy: AgentEgressPolicySchema,
    dispatchSelection: DispatchSelectionSchema.optional()
  })
  .strict()
  .superRefine((snapshot, context) => {
    if (!isPrivateAgentMetadataStatusPolicy(snapshot.egressPolicy)) {
      context.addIssue({
        code: "custom",
        path: ["egressPolicy"],
        message: "Private Agent execution requires the exact metadata/status egress policy."
      });
    }
    validatePrivateAgentRuntimeSelection(snapshot, context);
  });

/** API -> byok-control strict boundary; deviceId remains routing-only. */
export const ByokAgentResearchOfferSchemaVersion = "salesko.byok_agent_egress_fresh_offer.v1" as const;
export const ByokAgentResearchOfferSchema = z
  .object({
    schemaVersion: z.literal(ByokAgentResearchOfferSchemaVersion),
    taskId: z.string().min(1).max(200),
    tenantRef: z.string().regex(/^[0-9a-f]{32}$/u),
    deviceId: PersonalAgentDeviceIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema,
    agentRef: PrivateAgentRefSchema,
    runtime: RuntimeIdSchema,
    dispatchSelection: DispatchSelectionSchema.optional(),
    cwd: z.literal(PrivateAgentCanonicalHomeCwdBinding),
    researchPreset: AgentResearchAgentProfileValueSchema,
    egressPolicy: AgentEgressPolicySchema,
    instruction: z.string().min(1).max(100_000),
    ttlSeconds: z.number().int().min(60).max(86_400),
    policy: z.object({ mode: z.literal("readonly"), allowTools: z.tuple([]) }).strict()
  })
  .strict()
  .superRefine((offer, context) => {
    if (!isPrivateAgentMetadataStatusPolicy(offer.egressPolicy)) {
      context.addIssue({
        code: "custom",
        path: ["egressPolicy"],
        message: "Private Agent offer requires the exact metadata/status egress policy."
      });
    }
    validatePrivateAgentRuntimeSelection(offer, context);
  });

// --- Private Agent Chat (App-owned conversation/turn protocol) ---
//
// This namespace is deliberately separate from `AgentChat*` and from the
// research-job contracts above.  A chat turn is an asynchronous, exact Agent
// execution: the App owns the conversation and transcript, while the paired
// Local Agent owns the runtime session.  Every control/result shape below is
// strict so a new field cannot be silently stripped at a security boundary.

export const PrivateAgentConversationSchemaVersion = "salesko.private_agent_conversation.v6" as const;
export const PrivateAgentMessageSchemaVersion = "salesko.private_agent_message.v2" as const;
export const PrivateAgentTurnSchemaVersion = "salesko.private_agent_turn.v8" as const;
export const PrivateAgentEventSchemaVersion = "salesko.private_agent_event.v5" as const;
export const PrivateAgentChatMessageContract = "salesko.private_agent_chat_message.v1" as const;
export const PrivateAgentTerminalResultSchemaVersion = "salesko.private_agent_terminal_result.v5" as const;
export const PrivateAgentTerminalErrorSchemaVersion = "salesko.private_agent_terminal_error.v5" as const;
export const PrivateAgentTurnOfferSchemaVersion = "salesko.private_agent_turn_offer.v8" as const;
export const PrivateAgentTerminalReceiptSchemaVersion = "salesko.private_agent_terminal_receipt.v7" as const;
export const PrivateAgentChatMessageConsumeSchemaVersion = "salesko.private_agent_chat_message_consume.v1" as const;
export const PrivateAgentEventReplayResponseSchemaVersion = "salesko.private_agent_event_replay.v2" as const;

export const PrivateAgentConversationTitleMaximum = 200 as const;
export const PrivateAgentMessageContentMaximum = 50_000 as const;
export const PrivateAgentTerminalMessageMaximum = 100_000 as const;
export const PrivateAgentTerminalErrorMessageMaximum = 500 as const;
export const PrivateAgentChatInstructionMaximum = PrivateAgentMessageContentMaximum;
export const PrivateAgentClientRequestIdMaximum = 128 as const;
export const PrivateAgentEventBatchMaximum = 100 as const;
export const PrivateAgentSessionRefMaximum = 512 as const;
export const PrivateAgentTerminalTtlMinimumSeconds = 60 as const;
export const PrivateAgentTerminalTtlMaximumSeconds = 86_400 as const;

/** Opaque IDs are storage/routing identifiers, never user-authored semantics. */
export const PrivateAgentChatOpaqueIdSchema = z
  .string()
  .min(1)
  .max(160)
  .refine((value) => value.trim() === value, "Private Agent IDs must not have surrounding whitespace.")
  .regex(/^[A-Za-z0-9][A-Za-z0-9:_./-]*$/u);



/** Preserve user text byte-for-byte while rejecting empty/control-only payloads. */
const PrivateAgentChatTextSchema = z
  .string()
  .min(1)
  .max(PrivateAgentMessageContentMaximum)
  .refine((value) => value.trim().length > 0, "Private Agent Chat text must not be whitespace-only.")
  .refine((value) => !value.includes("\u0000"), "Private Agent Chat text must not contain NUL.");

const PrivateAgentChatTerminalTextSchema = z
  .string()
  .min(1)
  .max(PrivateAgentTerminalMessageMaximum)
  .refine((value) => value.trim().length > 0, "Private Agent terminal text must not be whitespace-only.")
  .refine((value) => !value.includes("\u0000"), "Private Agent terminal text must not contain NUL.");

export const PrivateAgentChatEventSequenceSchema = z
  .number()
  .int()
  .positive()
  .max(Number.MAX_SAFE_INTEGER);
export const PrivateAgentChatReplayCursorSchema = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);

/**
 * The device's own activity position a progress observation came from. It
 * orders within one turn only and exists so a repeated reconcile poll
 * observing the same position writes no second event.
 */
export const PrivateAgentChatActivitySequenceSchema = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);

/**
 * The only device activity kinds this product surfaces to a reader. Kind is
 * all this product carries: the frozen egress policy
 * (`salesko.agent-egress.metadata-status.v1`) redacts progress text, tool
 * names and approval summaries on the device before egress, so no content
 * field may cross this boundary.
 */
export const PrivateAgentChatActivityKindSchema = z.enum(["progress", "tool_use", "needs_approval"]);

/** Context epoch is exclusively the Host clear-chat generation. */
export const PrivateAgentChatContextEpochSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const PrivateAgentChatSnapshotSchema = z.object({
  contextEpoch: PrivateAgentChatContextEpochSchema,
  transcriptRevision: PrivateAgentChatReplayCursorSchema,
  eventWatermark: PrivateAgentChatReplayCursorSchema
}).strict();
export const PrivateAgentChatExpectedContextSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("absent") }).strict(),
  z.object({ state: z.literal("present"), conversationId: PrivateAgentChatOpaqueIdSchema,
    epoch: PrivateAgentChatContextEpochSchema }).strict()
]);
export const PrivateAgentChatMaximumUnsettledTurns = 8;
export const PrivateAgentChatMessagePageMaximum = 100;

/**
 * Conversation/turn binding is a snapshot, never a request for ambient device
 * selection.  `PrivateAgentRefSchema` is the Profile's opaque durable identity
 * plus canonical revision; the exact egress policy and runtime lane are checked
 * again here instead of accepting a generic SDK offer shape.
 */
export const PrivateAgentChatAgentRefSchema = PrivateAgentRefSchema;
export const PrivateAgentReadToolsetIdSchema = z.literal("salesko.read.v1");
export const PrivateAgentProposalToolsetIdSchema = z.literal("salesko.propose.v1");
export const PrivateAgentToolsetIdSchema = z.union([PrivateAgentReadToolsetIdSchema, PrivateAgentProposalToolsetIdSchema]);
export const PrivateAgentChatToolBindingSchemaVersion = "salesko.private_agent_chat_tools.v2" as const;
/** One current binding for offers, persisted executions and terminal receipts. */
export const PrivateAgentChatToolBindingSchema = z.object({
  schemaVersion: z.literal(PrivateAgentChatToolBindingSchemaVersion),
  requiredToolsets: z.tuple([PrivateAgentReadToolsetIdSchema, PrivateAgentProposalToolsetIdSchema])
}).strict();
export const PrivateAgentChatToolBinding = PrivateAgentChatToolBindingSchema.parse({
  schemaVersion: PrivateAgentChatToolBindingSchemaVersion,
  requiredToolsets: ["salesko.read.v1", "salesko.propose.v1"]
});
export const PrivateAgentChatToolRuntimeSchema = z.enum(["claude", "codex", "pi"]);
export const PrivateAgentReadToolsAudience = "salesko-private-agent-read-tools-v1" as const;
export const PrivateAgentProposalToolsAudience = "salesko-private-agent-proposal-tools-v1" as const;
export const PrivateAgentToolsAudienceByToolset = {
  "salesko.read.v1": PrivateAgentReadToolsAudience,
  "salesko.propose.v1": PrivateAgentProposalToolsAudience
} as const;
export const PrivateAgentChatExecutionBindingSchema = z
  .object({
    agentRef: PrivateAgentRefSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    placementRevision: PrivateAgentPlacementRevisionSchema,
    runtime: PrivateAgentChatToolRuntimeSchema,
    dispatchSelection: DispatchSelectionSchema.optional(),
    cwd: z.literal(PrivateAgentCanonicalHomeCwdBinding),
    egressPolicy: AgentEgressPolicySchema,
    tools: PrivateAgentChatToolBindingSchema
  })
  .strict()
  .superRefine((binding, context) => {
    if (!isPrivateAgentMetadataStatusPolicy(binding.egressPolicy)) {
      context.addIssue({
        code: "custom",
        path: ["egressPolicy"],
        message: "Private Agent Chat requires the exact metadata/status egress policy."
      });
    }
    validatePrivateAgentRuntimeSelection(binding, context);
  });



/** Frozen dispatch snapshot with separately recorded Host cancellation/observation facts. */
export const PrivateAgentChatTurnExecutionSchema = z
  .object({
    taskId: PrivateAgentChatOpaqueIdSchema,
    generation: z.number().int().positive(),
    contextEpoch: PrivateAgentChatContextEpochSchema,
    /** Host arbitration, persisted on the exact Execution record. */
    cancel: z.object({
      decision: z.enum(["cancelled_before_accept", "stop_remaining"]),
      requestedAt: z.string().datetime(),
      /** SDK cancel acknowledgement is not a device terminal or release proof. */
      acknowledgedAt: z.string().datetime().optional()
    }).strict().optional(),
    /** Host cancel/claim transaction withdrew this exact task before any dispatch. */
    withdrawnBeforeDispatchAt: z.string().datetime().optional(),
    /** Only an authenticated task-result observation may populate this. */
    terminalObservedAt: z.string().datetime().optional()
  })
  .strict();

export const PrivateAgentConversationStatusSchema = z.enum(["active", "archived"]);
export const PrivateAgentMessageRoleSchema = z.enum(["user", "assistant"]);
export const PrivateAgentTurnStatusSchema = z.enum([
  "queued",
  "dispatching",
  "running",
  "cancel_requested",
  "blocked",
  "succeeded",
  "failed",
  "canceled",
  "ended"
]);
export const PrivateAgentChatStatusEventValueSchema = z.enum([
  "queued",
  "dispatching",
  "running",
  "cancel_requested"
]);
export const PrivateAgentTerminalOutcomeSchema = z.enum(["succeeded", "failed", "canceled"]);
export const PrivateAgentTerminalErrorCodeSchema = z.enum([
  "bound_device_unavailable",
  "device_revoked",
  "device_offline",
  "capability_missing",
  "binding_mismatch",
  "runtime_unsupported",
  "message_missing",
  "malformed_terminal_result",
  "terminal_conflict",
  "turn_conflict",
  "replay_gap",
  "runtime_failed",
  "offer_rejected",
  "canceled"
]);

export const PrivateAgentConversationTitleSchema = z
  .string()
  .min(1)
  .max(PrivateAgentConversationTitleMaximum)
  .refine((value) => value.trim().length > 0, "Conversation title must not be whitespace-only.");

const PrivateAgentMessageBaseSchema = z.object({
  schemaVersion: z.literal(PrivateAgentMessageSchemaVersion),
  messageId: PrivateAgentChatOpaqueIdSchema,
  conversationId: PrivateAgentChatOpaqueIdSchema,
  contextEpoch: PrivateAgentChatContextEpochSchema,
  sequence: PrivateAgentChatEventSequenceSchema,
  createdAt: z.string().datetime()
}).strict();
export const PrivateAgentMessageSchema = z.discriminatedUnion("role", [
  PrivateAgentMessageBaseSchema.extend({ role: z.literal("user"),
    author: z.object({ kind: z.literal("user") }).strict(),
    clientRequestId: z.uuid(), producingTurnId: z.null(), content: PrivateAgentChatTextSchema }),
  PrivateAgentMessageBaseSchema.extend({ role: z.literal("assistant"),
    author: z.object({ kind: z.literal("agent"), agentId: PrivateAgentAgentIdSchema }).strict(),
    clientRequestId: z.null(), producingTurnId: PrivateAgentChatOpaqueIdSchema, content: PrivateAgentChatTerminalTextSchema })
]);
export const PrivateAgentConversationSummarySchema = PrivateAgentChatSnapshotSchema.extend({
  schemaVersion: z.literal(PrivateAgentConversationSchemaVersion),
  agentId: PrivateAgentAgentIdSchema, conversationId: PrivateAgentChatOpaqueIdSchema,
  title: PrivateAgentConversationTitleSchema, status: PrivateAgentConversationStatusSchema,
  messageCount: PrivateAgentChatReplayCursorSchema, turnCount: PrivateAgentChatReplayCursorSchema,
  createdAt: z.string().datetime(), updatedAt: z.string().datetime()
}).strict();
export const PrivateAgentConversationSchema = PrivateAgentConversationSummarySchema;
/** Selection never creates a canonical row. */
export const PrivateAgentCanonicalConversationResponseSchema = z.object({
  conversation: PrivateAgentConversationSchema.nullable()
}).strict();

/**
 * One canonical entity a Private Agent turn read, resolved against the
 * canonical-entity registry projection at conversation-read time. `resolved`
 * carries the registry's label and its latest node so a click can open the
 * entity in any frame; `unavailable` means the registry answered and holds no
 * such entity, so the browser shows the type and stays inert rather than
 * inventing a name from the loaded frame.
 */
export const PrivateAgentTouchedEntityDirectoryEntrySchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("resolved"),
      entityType: GraphEntityTypeSchema,
      label: z.string().min(1).max(400),
      latestFrameRecordId: z.string().min(1).max(160),
      latestNodeId: z.string().min(1).max(160)
    })
    .strict(),
  z
    .object({
      status: z.literal("unavailable"),
      entityType: GraphEntityTypeSchema
    })
    .strict()
]);

/**
 * The conversation read returns at most 100 turns and each turn stores at most
 * `PrivateAgentTurnTouchedEntitiesMaximum` entities, so a directory built from
 * that window can never hold more distinct ids than the product. The cap states
 * that structural bound; it is not a page size, and a projection that exceeds
 * it is a defect rather than something to silently truncate. The product is
 * asserted in `private-agent-chat.test.ts` because the per-turn maximum is
 * declared after this line.
 */
export const PrivateAgentTouchedEntityDirectoryMaximum = 5_000 as const;

/**
 * Every person, company and deal the loaded turns of one conversation read,
 * deduped by canonical entity id. A registry read that failed is one top-level
 * `error`: it is never flattened into an empty `ready` directory or into
 * per-entry `unavailable`, either of which would claim the registry answered.
 * The turn store stays the authority for the id and the type; the registry
 * projection stays the authority for the label and the latest node.
 */
export const PrivateAgentTouchedEntityDirectorySchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("ready"),
      entries: z
        .record(z.string().min(1).max(160), PrivateAgentTouchedEntityDirectoryEntrySchema)
        .refine(
          (entries) => Object.keys(entries).length <= PrivateAgentTouchedEntityDirectoryMaximum,
          `A conversation read window cannot touch more than ${PrivateAgentTouchedEntityDirectoryMaximum} distinct entities.`
        )
    })
    .strict(),
  z
    .object({
      status: z.literal("error"),
      code: z.string().min(1).max(120)
    })
    .strict()
]);

/** One snapshot; message pagination and active execution recovery are independent. */
export const PrivateAgentConversationResponseSchema = PrivateAgentChatSnapshotSchema.extend({
  conversation: PrivateAgentConversationSchema,
  messages: z.array(PrivateAgentMessageSchema).max(PrivateAgentChatMessagePageMaximum),
  turns: z.array(z.lazy(() => PrivateAgentTurnSchema)).max(PrivateAgentChatMessagePageMaximum),
  touchedEntityDirectory: PrivateAgentTouchedEntityDirectorySchema,
  recovery: z.lazy(() => PrivateAgentChatRecoverySchema),
  beforeSequence: PrivateAgentChatEventSequenceSchema.nullable(), hasMore: z.boolean()
}).strict().superRefine((value, ctx) => {
  for (const key of ["contextEpoch", "transcriptRevision", "eventWatermark"] as const) {
    if (value[key] !== value.conversation[key] || value[key] !== value.recovery[key])
      ctx.addIssue({ code: "custom", path: [key], message: "All projections must come from the same snapshot." });
  }
  if (value.recovery.conversationId !== value.conversation.conversationId)
    ctx.addIssue({ code: "custom", path: ["recovery"], message: "Recovery belongs to another canonical." });
  const turns = new Map(value.turns.map(t => [t.turnId, t]));
  if (turns.size !== value.turns.length || new Set(value.messages.map(m => m.messageId)).size !== value.messages.length)
    ctx.addIssue({ code: "custom", message: "Duplicate message or Turn identity." });
  value.turns.forEach(t => {
    if (t.contextEpoch !== value.contextEpoch || t.conversationId !== value.conversation.conversationId
      || t.responderAgentId !== value.conversation.agentId || t.revision > value.transcriptRevision)
      ctx.addIssue({ code: "custom", path: ["turns"], message: "Turn is outside this canonical snapshot." });
  });
  value.messages.forEach((m, i) => {
    const t = m.role === "assistant" ? turns.get(m.producingTurnId) : value.turns.find(t => t.inputMessageId === m.messageId);
    if (m.contextEpoch !== value.contextEpoch || m.conversationId !== value.conversation.conversationId
      || (i > 0 && value.messages[i - 1]!.sequence >= m.sequence) || !t
      || (m.role === "assistant" && m.author.agentId !== t.responderAgentId))
      ctx.addIssue({ code: "custom", path: ["messages", i], message: "Page must contain ordered current-epoch messages and their exact associated Turns." });
  });
  if (value.beforeSequence !== (value.messages[0]?.sequence ?? null) || (value.hasMore && value.messages.length === 0))
    ctx.addIssue({ code: "custom", path: ["beforeSequence"], message: "Page cursor must identify the oldest returned message." });
});
export const PrivateAgentChatMessagesQuerySchema = z.object({
  epoch: PrivateAgentChatContextEpochSchema,
  beforeSequence: PrivateAgentChatEventSequenceSchema.optional(),
  limit: z.number().int().positive().max(PrivateAgentChatMessagePageMaximum)
}).strict();

export const PrivateAgentTerminalAssistantMessageSchema = z
  .object({
    content: PrivateAgentChatTerminalTextSchema
  })
  .strict();

/** Exact SDK-authored receipt retained with the product terminal; no body is duplicated here. */
export const PrivateAgentChatMessageReceiptSchema = z
  .object({
    contract: z.literal(PrivateAgentChatMessageContract),
    messageId: z.uuid(),
    cursor: z.number().int().positive(),
    contentType: z.literal("text/markdown"),
    contentHash: SaleskoOpaqueSha256Schema,
    byteCount: z.number().int().positive().max(100_000)
  })
  .strict();

export const PrivateAgentTerminalResultSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentTerminalResultSchemaVersion),
    outcome: z.literal("succeeded"),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    binding: PrivateAgentChatExecutionBindingSchema,
    /** Native message provenance, never a selector for another execution. */
    sessionRef: z.string().min(1).max(PrivateAgentSessionRefMaximum).refine(v => v.trim() === v && !/[\u0000-\u001f\u007f]/u.test(v)),
    messageReceipt: PrivateAgentChatMessageReceiptSchema,
    assistantMessage: PrivateAgentTerminalAssistantMessageSchema,
    recordedAt: z.string().datetime()
  })
  .strict();

export const PrivateAgentTerminalErrorSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentTerminalErrorSchemaVersion),
    outcome: z.enum(["failed", "canceled"]),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    binding: PrivateAgentChatExecutionBindingSchema,
    code: PrivateAgentTerminalErrorCodeSchema,
    message: z
      .string()
      .min(1)
      .max(PrivateAgentTerminalErrorMessageMaximum)
      .refine((value) => value.trim().length > 0, "Terminal error message must not be whitespace-only."),
    retryable: z.boolean(),
    executionTerminalObservedAt: z.string().datetime().optional(),
    recordedAt: z.string().datetime()
  })
  .strict();

export const PrivateAgentTerminalSchema = z.discriminatedUnion("outcome", [
  PrivateAgentTerminalResultSchema,
  PrivateAgentTerminalErrorSchema
]);

export const PrivateAgentTurnTouchedEntitiesMaximum = 50 as const;

/**
 * One graph entity a Private Agent turn actually read through a Salesko read
 * tool. Identity is the canonical entity id so it stays stable across frames;
 * the human label is resolved from the current frame at read time and is
 * deliberately not stored here.
 */
export const PrivateAgentTurnTouchedEntitySchema = z
  .object({
    canonicalEntityId: z.string().min(1).max(160),
    entityType: GraphEntityTypeSchema
  })
  .strict();

export type PrivateAgentTurnTouchedEntity = z.infer<typeof PrivateAgentTurnTouchedEntitySchema>;

/** Explicit model selection frozen with a submitted input; SDK remains runtime authority. */
export const PrivateAgentChatModelSelectionSchema = z.discriminatedUnion("lane", [
  z.object({ lane: z.literal("subscription"), runtimeId: z.enum(["claude", "codex"]), providerId: z.null(), modelId: PrivateAgentPiModelIdSchema }).strict(),
  z.object({ lane: z.literal("byok-profile"), runtimeId: z.literal("pi"), providerProfile: ProviderProfileBindingSchema }).strict()
]);
export type PrivateAgentChatModelSelection = z.infer<typeof PrivateAgentChatModelSelectionSchema>;

export const PrivateAgentTurnSettlementSchema = z.object({
  reason: z.enum(["answered", "ended", "cancelled_before_accept"]), recordedAt: z.string().datetime()
}).strict();

const PrivateAgentTurnBaseSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentTurnSchemaVersion),
    turnId: PrivateAgentChatOpaqueIdSchema,
    conversationId: PrivateAgentChatOpaqueIdSchema,
    clientRequestId: z.string().min(1).max(PrivateAgentClientRequestIdMaximum),
    inputMessageId: PrivateAgentChatOpaqueIdSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    turnSequence: PrivateAgentChatEventSequenceSchema,
    responderAgentId: PrivateAgentAgentIdSchema,
    revision: PrivateAgentChatReplayCursorSchema,
    settlement: PrivateAgentTurnSettlementSchema.nullable(),
    modelSelection: PrivateAgentChatModelSelectionSchema,
    status: PrivateAgentTurnStatusSchema,
    binding: PrivateAgentChatExecutionBindingSchema,
    execution: PrivateAgentChatTurnExecutionSchema,
    terminal: PrivateAgentTerminalSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    touchedEntities: z
      .array(PrivateAgentTurnTouchedEntitySchema)
      .max(PrivateAgentTurnTouchedEntitiesMaximum)
      .default([])
  })
  .strict();

export const PrivateAgentPreparedTurnSchema = PrivateAgentTurnBaseSchema
  .superRefine((turn, context) => {
    if (turn.execution.contextEpoch !== turn.contextEpoch || turn.binding.agentRef.agentId !== turn.responderAgentId
      || (turn.terminal && (turn.terminal.contextEpoch !== turn.contextEpoch || turn.terminal.turnId !== turn.turnId
        || turn.terminal.conversationId !== turn.conversationId || JSON.stringify(turn.terminal.binding) !== JSON.stringify(turn.binding)))
      || ((turn.settlement?.reason === "answered") !== (turn.terminal?.outcome === "succeeded")))
      context.addIssue({ code: "custom", message: "Turn, execution and accepted fact must share exact identity." });
    if ((turn.execution.cancel?.decision === "stop_remaining" && turn.terminal?.outcome !== "succeeded")
      || (turn.execution.cancel?.decision === "cancelled_before_accept" && turn.terminal?.outcome === "succeeded")) {
      context.addIssue({ code: "custom", path: ["execution", "cancel"], message: "Cancel arbitration must preserve the accepted message fact." });
    }
    if (turn.execution.withdrawnBeforeDispatchAt !== undefined && (turn.execution.cancel?.decision !== "cancelled_before_accept"
      || turn.execution.terminalObservedAt !== undefined)) {
      context.addIssue({ code: "custom", path: ["execution", "withdrawnBeforeDispatchAt"], message: "Local withdrawal is a pre-dispatch cancellation, not a device terminal." });
    }
    if ((turn.status === "ended") !== (turn.settlement?.reason === "ended")
      || (turn.status === "ended" && turn.completedAt === undefined))
      context.addIssue({ code: "custom", message: "Ended turns require an immutable End settlement and completion time." });
    const settledCancellation = turn.status === "canceled" && turn.settlement?.reason === "cancelled_before_accept"
      && turn.execution.cancel?.decision === "cancelled_before_accept" && turn.completedAt === turn.settlement.recordedAt;
    const isTerminalStatus = !settledCancellation && ["succeeded", "failed", "canceled"].includes(turn.status);
    if (isTerminalStatus && turn.terminal === undefined) {
      context.addIssue({ code: "custom", path: ["terminal"], message: "Terminal turns require one terminal receipt." });
    }
    if (!isTerminalStatus && turn.terminal !== undefined) {
      context.addIssue({ code: "custom", path: ["terminal"], message: "Non-terminal turns cannot carry a terminal receipt." });
    }
    if (turn.status === "succeeded" && turn.terminal?.outcome !== "succeeded") {
      context.addIssue({ code: "custom", path: ["terminal", "outcome"], message: "Succeeded turns require a successful terminal result." });
    }
    if (turn.status === "failed" && turn.terminal?.outcome !== "failed") {
      context.addIssue({ code: "custom", path: ["terminal", "outcome"], message: "Failed turns require a failed terminal error." });
    }
    if (turn.status === "canceled" && !settledCancellation && turn.terminal?.outcome !== "canceled") {
      context.addIssue({ code: "custom", path: ["terminal", "outcome"], message: "Canceled turns require a canceled terminal error." });
    }
  });

/** An input can exist before any device/task/session has been selected. */
export const PrivateAgentUnpreparedTurnSchema = PrivateAgentTurnBaseSchema.omit({
  status: true, binding: true, execution: true, terminal: true, startedAt: true
}).extend({
  status: z.enum(["queued", "canceled", "ended"]),
  binding: z.null(),
  execution: z.null(),
  terminal: z.never().optional(),
  startedAt: z.never().optional()
}).superRefine((turn, context) => {
  if ((turn.status !== "queued") !== (turn.completedAt !== undefined)
    || (turn.status === "queued" ? turn.settlement !== null : turn.settlement?.reason !== (turn.status === "ended" ? "ended" : "cancelled_before_accept"))) {
    context.addIssue({ code: "custom", path: ["completedAt"], message: "Only a canceled unprepared input has a completion time." });
  }
});

export const PrivateAgentTurnSchema = z.union([PrivateAgentPreparedTurnSchema, PrivateAgentUnpreparedTurnSchema]);
export type PrivateAgentPreparedTurn = z.infer<typeof PrivateAgentPreparedTurnSchema>;
export type PrivateAgentUnpreparedTurn = z.infer<typeof PrivateAgentUnpreparedTurnSchema>;
export function privateAgentTurnIsPrepared(turn: PrivateAgentTurn): turn is PrivateAgentPreparedTurn {
  return turn.execution !== null;
}

/** Bounded current logical queue and independently bounded cross-epoch executions. */
export const PrivateAgentChatRecoveryTurnSchema = z.object({
  turnId: PrivateAgentChatOpaqueIdSchema, contextEpoch: PrivateAgentChatContextEpochSchema,
  turnSequence: PrivateAgentChatEventSequenceSchema, revision: PrivateAgentChatReplayCursorSchema,
  execution: z.object({ taskId: PrivateAgentChatOpaqueIdSchema, generation: PrivateAgentChatEventSequenceSchema }).strict().nullable(),
  settlement: PrivateAgentTurnSettlementSchema.nullable(),
  executionObservation: z.enum(["unprepared", "pending", "submitting", "admitted", "unknown", "withdrawn_before_dispatch", "terminal"]),
  messageDisposition: z.enum(["unknown", "pending", "accepted", "held", "refused", "none"]),
  resourceObservation: z.enum(["unknown", "busy", "quiescent-observed"]),
  actions: z.object({ stop: z.boolean(), end: z.boolean(), retry: z.boolean(), stopAndSend: z.boolean() }).strict()
}).strict().superRefine((row, ctx) => {
  if ((row.execution === null) !== (row.executionObservation === "unprepared")
    || (row.actions.stop && row.execution === null)
    || ((row.actions.end || row.actions.retry) && row.settlement !== null)
    || (row.actions.retry && row.executionObservation !== "terminal")
    || (row.executionObservation === "withdrawn_before_dispatch" && row.settlement?.reason !== "cancelled_before_accept"))
    ctx.addIssue({ code: "custom", message: "Recovery actions contradict exact execution/settlement facts." });
});
export const PrivateAgentChatRecoverySchema = PrivateAgentChatSnapshotSchema.extend({
  schemaVersion: z.literal("salesko.private_agent_chat_recovery.v2"),
  conversationId: PrivateAgentChatOpaqueIdSchema,
  unsettledTurns: z.number().int().nonnegative().max(PrivateAgentChatMaximumUnsettledTurns),
  headTurnId: PrivateAgentChatOpaqueIdSchema.nullable(),
  turns: z.array(PrivateAgentChatRecoveryTurnSchema).max(PrivateAgentChatMaximumUnsettledTurns),
  toolCalls: z.array(z.lazy(() => PrivateAgentToolCallObservationSchema)).max(PrivateAgentChatMessagePageMaximum),
  toolCallsNextCursor: z.lazy(() => PrivateAgentToolCallRecoveryCursorSchema).nullable(),
  executions: z.array(PrivateAgentChatRecoveryTurnSchema).max(PrivateAgentChatMaximumUnsettledTurns)
}).strict().superRefine((r, ctx) => {
  const ids = new Set(r.turns.map(t => t.turnId));
  if (ids.size !== r.turns.length || r.turns.length !== r.unsettledTurns
    || new Set(r.turns.map(t => t.turnSequence)).size !== r.turns.length
    || ((r.unsettledTurns === 0) !== (r.headTurnId === null))
    || (r.headTurnId !== null && !ids.has(r.headTurnId))
    || r.turns.some(t => t.contextEpoch !== r.contextEpoch || t.settlement !== null
      || (t.actions.end || t.actions.retry) && t.turnId !== r.headTurnId)
    || r.executions.some(t => t.execution === null || t.executionObservation === "terminal" || t.executionObservation === "withdrawn_before_dispatch")
    || r.toolCalls.some(call => call.revision > r.transcriptRevision)
    || (r.toolCallsNextCursor !== null && (r.toolCalls.length !== PrivateAgentChatMessagePageMaximum
      || r.toolCallsNextCursor.conversationId !== r.conversationId || r.toolCallsNextCursor.transcriptRevision !== r.transcriptRevision
      || JSON.stringify(r.toolCallsNextCursor.after) !== JSON.stringify({taskId:r.toolCalls.at(-1)?.taskId,toolset:r.toolCalls.at(-1)?.toolset,requestId:r.toolCalls.at(-1)?.requestId})))
    || new Set(r.executions.map(t => t.execution?.taskId)).size !== r.executions.length
    || [...r.turns, ...r.executions].some(t => t.revision > r.transcriptRevision || t.contextEpoch > r.contextEpoch
      || t.contextEpoch !== r.contextEpoch && (t.actions.retry || t.actions.end || t.actions.stopAndSend)))
    ctx.addIssue({ code: "custom", message: "Recovery must preserve bounded current queue and exact cross-epoch execution recovery." });
});
export type PrivateAgentChatRecovery = z.infer<typeof PrivateAgentChatRecoverySchema>;
export type PrivateAgentChatRecoveryTurn = z.infer<typeof PrivateAgentChatRecoveryTurnSchema>;

/** Four independent Host facts; status/terminal retain their existing product meaning. */
export const PrivateAgentChatTurnFactsSchema = z.object({
  messageDecision: z.enum(["open", "answered", "ended", "cancelled_before_accept"]),
  cancelArbitration: z.enum(["not_requested", "cancelled_before_accept", "stop_remaining"]),
  executionObservation: z.enum(["not_started", "started", "cancel_requested", "withdrawn_before_dispatch", "terminal"]),
  resourceObservation: z.enum(["unknown", "busy", "quiescent-observed"])
}).strict();

export function privateAgentChatTurnFacts(turn: PrivateAgentTurn): z.infer<typeof PrivateAgentChatTurnFactsSchema> {
  if (!privateAgentTurnIsPrepared(turn)) return {
    messageDecision: turn.settlement?.reason ?? "open",
    cancelArbitration: turn.status === "canceled" ? "cancelled_before_accept" : "not_requested",
    executionObservation: "not_started",
    resourceObservation: "unknown"
  };
  const answered = turn.terminal?.outcome === "succeeded";
  const canceled = turn.execution.cancel?.decision === "cancelled_before_accept" || turn.status === "cancel_requested" || turn.status === "canceled";
  return {
    messageDecision: turn.settlement?.reason ?? (canceled ? "cancelled_before_accept" : "open"),
    cancelArbitration: turn.execution.cancel?.decision ?? (canceled ? "cancelled_before_accept" : "not_requested"),
    // not_started means no start observation, not proof that enqueue/execution never happened.
    executionObservation: turn.execution.withdrawnBeforeDispatchAt !== undefined ? "withdrawn_before_dispatch" : turn.execution.terminalObservedAt !== undefined || (turn.terminal?.outcome !== "succeeded" && turn.terminal?.executionTerminalObservedAt !== undefined)
      ? "terminal" : turn.execution.cancel !== undefined || turn.status === "cancel_requested" || turn.status === "canceled"
        ? "cancel_requested" : answered ? "started" : "not_started",
    // Neither a product receipt nor SDK cancellation proves home quiescence.
    resourceObservation: "unknown"
  };
}



/** Explicit closure of one frozen Execution; never reselect the current task on replay. */
export const PrivateAgentTurnEndRequestSchema = z.object({
  actionId: PrivateAgentChatOpaqueIdSchema, expectedEpoch: PrivateAgentChatContextEpochSchema,
  taskId: PrivateAgentChatOpaqueIdSchema.nullable(), generation: PrivateAgentChatEventSequenceSchema.nullable()
}).strict().refine(r => (r.taskId === null) === (r.generation === null), "End requires an exact execution pair or an explicit unprepared target.");
export const PrivateAgentTurnRetryRequestSchema = z.object({
  expectedEpoch: PrivateAgentChatContextEpochSchema,
  actionId: PrivateAgentChatOpaqueIdSchema,
  taskId: PrivateAgentChatOpaqueIdSchema,
  generation: z.number().int().positive()
}).strict();
export const PrivateAgentTurnRetryResponseSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema,
  turnId: PrivateAgentChatOpaqueIdSchema,
  source: PrivateAgentTurnRetryRequestSchema,
  taskId: PrivateAgentChatOpaqueIdSchema,
  generation: z.number().int().positive(),
  recordedAt: z.string().datetime()
}).strict().superRefine((receipt, context) => {
  if (receipt.generation !== receipt.source.generation + 1 || receipt.taskId === receipt.source.taskId) {
    context.addIssue({ code: "custom", message: "A retry creates exactly the next distinct execution identity." });
  }
});
export type PrivateAgentTurnRetryResponse = z.infer<typeof PrivateAgentTurnRetryResponseSchema>;
export const PrivateAgentTurnEndSettlementSchema = PrivateAgentTurnEndRequestSchema.safeExtend({
  reason: z.literal("ended"),
  recordedAt: z.string().datetime()
}).strict();
export type PrivateAgentTurnEndSettlement = z.infer<typeof PrivateAgentTurnEndSettlementSchema>;
export const PrivateAgentTurnEndResponseSchema = z.object({
  turn: PrivateAgentTurnSchema,
  settlement: PrivateAgentTurnEndSettlementSchema
}).strict().superRefine((value, context) => {
  if ((value.turn.execution?.taskId ?? null) !== value.settlement.taskId || (value.turn.execution?.generation ?? null) !== value.settlement.generation
    || value.turn.contextEpoch !== value.settlement.expectedEpoch
    || value.turn.status !== "ended" || value.turn.settlement?.reason !== "ended"
    || value.turn.settlement.recordedAt !== value.settlement.recordedAt
    || value.turn.terminal?.outcome === "succeeded") {
    context.addIssue({ code: "custom", path: ["settlement"], message: "End must retain its exact execution and cannot erase an accepted reply." });
  }
});

export const PrivateAgentInputSubmitRequestSchema = z
  .object({
    modelSelection: PrivateAgentChatModelSelectionSchema,
    clientRequestId: z.uuid(),
    expectedContext: PrivateAgentChatExpectedContextSchema,
    message: PrivateAgentChatTextSchema
  })
  .strict();

export const PrivateAgentInputAdmissionReceiptSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema, clientRequestId: z.uuid(), contextEpoch: PrivateAgentChatContextEpochSchema,
  inputMessageId: PrivateAgentChatOpaqueIdSchema, turnId: PrivateAgentChatOpaqueIdSchema,
  messageSequence: PrivateAgentChatEventSequenceSchema, turnSequence: PrivateAgentChatEventSequenceSchema,
  eventSequence: PrivateAgentChatEventSequenceSchema, transcriptRevision: PrivateAgentChatReplayCursorSchema,
  recordedAt: z.string().datetime()
}).strict();
export const PrivateAgentClearChatRequestSchema = z.object({ kind: z.literal("clear_chat"),
  expectedEpoch: PrivateAgentChatContextEpochSchema, clientRequestId: z.uuid() }).strict();
export const PrivateAgentClearChatReceiptSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema, clientRequestId: z.uuid(),
  previousEpoch: PrivateAgentChatContextEpochSchema, contextEpoch: PrivateAgentChatContextEpochSchema,
  eventSequence: PrivateAgentChatEventSequenceSchema, transcriptRevision: PrivateAgentChatReplayCursorSchema,
  recordedAt: z.string().datetime()
}).strict().refine(r => r.contextEpoch === r.previousEpoch + 1, "Clear advances exactly one epoch.");
/** User-context label for Summary projection; never a system instruction. */
export const PrivateAgentChatHistorySummaryMarker = "【历史摘要】\n" as const;

/** Immutable metadata; only an independently authorized fixture may seed this store in C04. */
export const PrivateAgentChatSummarySchema = z.object({
  schemaVersion: z.literal("salesko.private_agent_chat_summary.v1"), datasetScope: z.string().min(1),
  tenantId: z.string().min(1), userId: z.string().min(1), conversationId: PrivateAgentChatOpaqueIdSchema,
  producerAgentId: PrivateAgentAgentIdSchema, contextEpoch: PrivateAgentChatContextEpochSchema,
  coverageToTurnSequence: PrivateAgentChatEventSequenceSchema, sourceRevision: PrivateAgentChatReplayCursorSchema,
  prefixFingerprint: SaleskoOpaqueSha256Schema, body: z.string().min(1), contentHash: SaleskoOpaqueSha256Schema,
  byteCount: PrivateAgentChatEventSequenceSchema, recordedAt: z.string().datetime()
}).strict().refine(r => new TextEncoder().encode(r.body).byteLength === r.byteCount, "Summary byteCount must match UTF-8 body bytes.");
export type PrivateAgentInputAdmissionReceipt = z.infer<typeof PrivateAgentInputAdmissionReceiptSchema>;
export type PrivateAgentClearChatRequest = z.infer<typeof PrivateAgentClearChatRequestSchema>;
export type PrivateAgentClearChatReceipt = z.infer<typeof PrivateAgentClearChatReceiptSchema>;
export type PrivateAgentChatSummary = z.infer<typeof PrivateAgentChatSummarySchema>;

export const PrivateAgentStopAndSendTargetSchema = z.object({
  turnId: PrivateAgentChatOpaqueIdSchema,
  execution: z.object({ taskId: PrivateAgentChatOpaqueIdSchema, generation: z.number().int().positive() }).strict().nullable()
}).strict();
export const PrivateAgentTurnCancelRequestSchema = PrivateAgentStopAndSendTargetSchema.pick({ execution: true }).extend({ expectedEpoch: PrivateAgentChatContextEpochSchema });
export type PrivateAgentTurnCancelRequest = z.infer<typeof PrivateAgentTurnCancelRequestSchema>;
export const PrivateAgentStopAndSendRequestSchema = z.object({
  actionId: PrivateAgentChatOpaqueIdSchema,
  expectedEpoch: PrivateAgentChatContextEpochSchema,
  target: PrivateAgentStopAndSendTargetSchema,
  input: PrivateAgentInputSubmitRequestSchema
}).strict();
export type PrivateAgentStopAndSendRequest = z.infer<typeof PrivateAgentStopAndSendRequestSchema>;
/** Temporary browser intent only; never a transcript or execution authority. */
const PrivateAgentChatIntentBase = z.object({
  schemaVersion: z.literal("salesko.private_agent_chat_intent.v2"),
  owner: z.object({ tenantId: z.string().min(1), userId: z.string().min(1) }).strict(),
  agentId: PrivateAgentAgentIdSchema,
  conversationId: PrivateAgentChatOpaqueIdSchema
}).strict();
export const PrivateAgentChatClientIntentSchema = z.discriminatedUnion("kind", [
  PrivateAgentChatIntentBase.extend({ kind: z.literal("send"), conversationId: PrivateAgentChatOpaqueIdSchema.nullable(), request: PrivateAgentInputSubmitRequestSchema }),
  PrivateAgentChatIntentBase.extend({ kind: z.literal("clear"), request: PrivateAgentClearChatRequestSchema }),
  PrivateAgentChatIntentBase.extend({ kind: z.literal("stop"), turnId: PrivateAgentChatOpaqueIdSchema, request: PrivateAgentTurnCancelRequestSchema }),
  PrivateAgentChatIntentBase.extend({ kind: z.literal("end"), turnId: PrivateAgentChatOpaqueIdSchema, request: PrivateAgentTurnEndRequestSchema }),
  PrivateAgentChatIntentBase.extend({ kind: z.literal("retry"), turnId: PrivateAgentChatOpaqueIdSchema, request: PrivateAgentTurnRetryRequestSchema }),
  PrivateAgentChatIntentBase.extend({ kind: z.literal("stopAndSend"), request: PrivateAgentStopAndSendRequestSchema })
]);
export type PrivateAgentChatClientIntent = z.infer<typeof PrivateAgentChatClientIntentSchema>;
export const PrivateAgentStopAndSendReceiptSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema,
  actionId: PrivateAgentChatOpaqueIdSchema,
  expectedEpoch: PrivateAgentChatContextEpochSchema,
  target: PrivateAgentStopAndSendTargetSchema,
  input: z.object({ turnId: PrivateAgentChatOpaqueIdSchema, clientRequestId: PrivateAgentInputSubmitRequestSchema.shape.clientRequestId,
    inputMessageId: PrivateAgentChatOpaqueIdSchema }).strict(),
  stopDecision: z.enum(["cancelled_before_accept", "reply_preserved", "already_settled"]),
  recordedAt: z.string().datetime()
}).strict().superRefine((receipt, context) => {
  if (receipt.target.turnId === receipt.input.turnId) context.addIssue({ code: "custom", message: "Stop-and-send must append a distinct Turn." });
});
export type PrivateAgentStopAndSendReceipt = z.infer<typeof PrivateAgentStopAndSendReceiptSchema>;

export const PrivateAgentTurnResponseSchema = z
  .object({
    turn: PrivateAgentTurnSchema,
    facts: PrivateAgentChatTurnFactsSchema.optional()
  })
  .strict()
  .transform((response, context) => {
    const facts = privateAgentChatTurnFacts(response.turn);
    if (response.facts !== undefined && Object.keys(facts).some((key) => response.facts![key as keyof typeof facts] !== facts[key as keyof typeof facts])) {
      context.addIssue({ code: "custom", path: ["facts"], message: "Turn fact projection does not match its authoritative records." });
      return z.NEVER;
    }
    return { turn: response.turn, facts };
  });

export const PrivateAgentTurnListResponseSchema = z
  .object({
    turns: z.array(PrivateAgentTurnSchema).max(100),
    nextCursor: PrivateAgentChatReplayCursorSchema.optional()
  })
  .strict();

/** API -> BYOK control: one strict offer for one durable Private Agent turn. */
export const PrivateAgentChatAuditOfferSchema = z
  .object({
    schemaVersion: z.literal("salesko.private_agent_turn_offer.v7"),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    deviceId: PersonalAgentDeviceIdSchema,
    binding: PrivateAgentChatExecutionBindingSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    /** Product message semantics; the runtime cannot author a target from it. */
    messageContract: z.literal(PrivateAgentChatMessageContract),
    instruction: z
      .string()
      .min(1)
      .max(PrivateAgentChatInstructionMaximum)
      .refine((value) => value.trim().length > 0, "Private Agent turn instruction must not be whitespace-only.")
      .refine((value) => !value.includes("\u0000"), "Private Agent turn instruction must not contain NUL."),
    ttlSeconds: z.number().int().min(PrivateAgentTerminalTtlMinimumSeconds).max(PrivateAgentTerminalTtlMaximumSeconds),
    policy: z.object({ mode: z.literal("auto") }).strict()
  })
  .strict()
  .superRefine((offer, context) => {
    if (offer.deviceId !== offer.binding.deviceId) {
      context.addIssue({ code: "custom", path: ["deviceId"], message: "Offer deviceId must equal the frozen binding deviceId." });
    }
  });

/** Ready receipt projection retained by the Host; values originate in SDK readback. */
export const PrivateAgentChatReadyReceiptSchema = z.object({
  reference: InputPreparationOfferBindingSchema.shape.reference,
  binding: InputPreparationBindingSchema,
  rulingRevision: z.string().min(1),
  bound: z.number().int().positive().safe(),
  artifact: z.object({ requestDigest: z.string().regex(/^[a-f0-9]{64}$/), envelopeDigest: z.string().regex(/^[a-f0-9]{64}$/),
    requestBytes: z.number().int().nonnegative().safe() }).strict()
}).strict();
export type PrivateAgentChatReadyReceipt = z.infer<typeof PrivateAgentChatReadyReceiptSchema>;

/** The only executable offer has no instruction: the referenced artifact owns D. */
export const PrivateAgentTurnOfferSchema = z.object({
  ...PrivateAgentChatAuditOfferSchema.shape,
  schemaVersion: z.literal(PrivateAgentTurnOfferSchemaVersion),
  preparation: InputPreparationOfferBindingSchema.extend({ artifactDigest: z.string().regex(/^[a-f0-9]{64}$/) }),
  policy: z.object({ mode: z.literal("auto"), allowTools: z.tuple([]) }).strict()
}).omit({ instruction: true }).strict().superRefine((offer, context) => {
  if (offer.deviceId !== offer.binding.deviceId || offer.binding.runtime !== "pi"
    || offer.binding.dispatchSelection?.lane !== "byok-profile")
    context.addIssue({code:"custom",message:"Prepared chat requires the exact Pi BYOK binding."});
});
export const PrivateAgentChatPreparedSnapshotSchema = z.object({
  origin: z.literal("frozen_dispatch_v3"), sdkOfferType: z.literal("task.offer_prepared"),
  offer: PrivateAgentTurnOfferSchema,
  budget: z.object({ rulingRevision: z.string().min(1), bound: z.number().int().positive().safe(),
    /** Provider request domain, independent of offer.preparation.requestDigest. */
    artifactRequestDigest: z.string().regex(/^[a-f0-9]{64}$/) }).strict()
}).strict();
/** Migrated fresh records are audit data only; no executable target accepts them. */
export const PrivateAgentChatExecutionSnapshotSchema = z.discriminatedUnion("sdkOfferType", [
  PrivateAgentChatPreparedSnapshotSchema,
  z.object({ origin: z.literal("frozen_dispatch_v3"), sdkOfferType: z.literal("task.offer_for_agent_with_egress_fresh"),
    offer: PrivateAgentChatAuditOfferSchema }).strict()
]);

/** Exact canonical SDK terminal bytes, authenticated by the control read path. */
export const PrivateAgentChatSdkTerminalReceiptSchema = z.object({
  body: z.string().min(1), recordedAt: z.string().datetime()
}).strict();

/** Authenticated public TaskAttempt readback, not a claim inferred from an error string. */
export const PrivateAgentChatSdkUnclaimedAttemptSchema = z.object({
  taskId: PrivateAgentChatOpaqueIdSchema,
  deviceId: PersonalAgentDeviceIdSchema,
  agentRef: AgentRefSchema,
  status: z.literal("failed"),
  ownerDeviceId: z.null(),
  claimedRuntime: z.null(),
  claimedHarnessId: z.null(),
  updatedAt: z.string().datetime()
}).strict();
export type PrivateAgentChatSdkUnclaimedAttempt = z.infer<typeof PrivateAgentChatSdkUnclaimedAttemptSchema>;

/** Host stores the exact message alongside the SDK public disposition. */
export const PrivateAgentChatSdkMessageReceiptSchema = z.object({
  schemaVersion: z.literal("salesko.sdk_message_receipt.v1"),
  payload: AgentMessagePublishPayloadSchema,
  disposition: AgentMessageDispositionPayloadSchema
}).strict().superRefine(({ payload, disposition }, context) => {
  if (disposition.messageId !== payload.messageId
    || disposition.agentRef.agentId !== payload.agentRef.agentId || disposition.agentRef.profileRevision !== payload.agentRef.profileRevision
    || disposition.sessionRef !== payload.sessionRef || disposition.contract !== payload.contract
    || disposition.cursor !== payload.cursor || disposition.contentHash !== payload.contentHash) {
    context.addIssue({ code: "custom", message: "SDK disposition does not match the exact message." });
  }
});
export type PrivateAgentChatSdkMessageReceipt = z.infer<typeof PrivateAgentChatSdkMessageReceiptSchema>;
export function privateAgentChatSdkMessageDisposition(receipt: PrivateAgentChatSdkMessageReceipt) {
  return PrivateAgentChatSdkMessageReceiptSchema.parse(receipt).disposition;
}

/** Internal repository record; Turn/UI projections are not a second snapshot writer. */
export const PrivateAgentChatExecutionRecordSchema = z.object({
  datasetScope: z.string().min(1),
  tenantId: z.string().min(1),
  userId: z.string().min(1),
  conversationId: PrivateAgentChatOpaqueIdSchema,
  turnId: PrivateAgentChatOpaqueIdSchema,
  contextEpoch: PrivateAgentChatContextEpochSchema,
  taskId: PrivateAgentChatOpaqueIdSchema,
  generation: z.number().int().positive(),
  snapshot: PrivateAgentChatExecutionSnapshotSchema,
  retryOf: PrivateAgentTurnRetryRequestSchema.optional(),
  messageContext: z.object({
    destinationBinding: PrivateAgentChatOpaqueIdSchema,
    freshnessCursor: PrivateAgentChatOpaqueIdSchema
  }).strict(),
  cancel: PrivateAgentChatTurnExecutionSchema.shape.cancel,
  observations: z.object({
    dispatch: z.enum(["pending", "submitting", "admitted", "unknown"]),
    reason: z.string().min(1).optional(),
    resource: z.enum(["unknown", "busy", "quiescent-observed"]),
    messageDisposition: z.enum(["pending", "accepted", "held", "refused", "none"]).optional(),
    messageReceipt: PrivateAgentChatSdkMessageReceiptSchema.optional(),
    terminalObservedAt: z.string().datetime().optional(),
    /** Encoded authenticated SDK receipt; product cancellation is not this field. */
    terminalReceipt: PrivateAgentChatSdkTerminalReceiptSchema.optional(),
    /** Host cancel/claim arbitration proves this frozen task was never dispatched. */
    withdrawnBeforeDispatchAt: z.string().datetime().optional(),
    unclaimedAttempt: PrivateAgentChatSdkUnclaimedAttemptSchema.optional()
  }).strict(),
  productFailure: PrivateAgentTerminalErrorSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
}).strict().superRefine((execution, context) => {
  if (execution.observations.withdrawnBeforeDispatchAt !== undefined && (execution.snapshot.origin !== "frozen_dispatch_v3"
    || execution.observations.dispatch !== "pending" || execution.cancel?.decision !== "cancelled_before_accept")) {
    context.addIssue({ code: "custom", path: ["observations", "withdrawnBeforeDispatchAt"], message: "Local withdrawal requires a never-dispatched frozen task and committed cancellation." });
  }
  if (execution.retryOf && (execution.retryOf.expectedEpoch !== execution.contextEpoch || execution.retryOf.generation + 1 !== execution.generation || execution.retryOf.taskId === execution.taskId)) {
    context.addIssue({ code: "custom", path: ["retryOf"], message: "Retry source must be the distinct predecessor generation." });
  }
  const unclaimed = execution.observations.unclaimedAttempt;
  const executionBinding = execution.snapshot.offer.binding;
  if (unclaimed && (unclaimed.taskId !== execution.taskId || unclaimed.deviceId !== executionBinding.deviceId
    || unclaimed.agentRef.agentId !== executionBinding.agentRef.agentId || unclaimed.agentRef.profileRevision !== executionBinding.agentRef.profileRevision)) {
    context.addIssue({ code: "custom", path: ["observations", "unclaimedAttempt"], message: "Unclaimed observation must name the exact execution binding." });
  }
  const messageReceipt = execution.observations.messageReceipt;
  const disposition = execution.observations.messageDisposition;
  const parsedMessageReceipt = messageReceipt && PrivateAgentChatSdkMessageReceiptSchema.safeParse(messageReceipt);
  if ((parsedMessageReceipt?.success && privateAgentChatSdkMessageDisposition(parsedMessageReceipt.data).outcome !== disposition)
    || (!messageReceipt && (disposition === "accepted" || disposition === "held" || disposition === "refused"))) {
    context.addIssue({ code: "custom", path: ["observations", "messageDisposition"], message: "SDK disposition must be the exact receipt projection." });
  }
  if (execution.messageContext.destinationBinding !== execution.conversationId || execution.messageContext.freshnessCursor !== execution.turnId) {
    context.addIssue({ code: "custom", path: ["messageContext"], message: "Execution message context must name its exact Conversation and Turn." });
  }
  if (execution.snapshot.origin === "frozen_dispatch_v3") {
    if (execution.snapshot.offer.conversationId !== execution.conversationId || execution.snapshot.offer.turnId !== execution.turnId || execution.snapshot.offer.contextEpoch !== execution.contextEpoch) {
      context.addIssue({ code: "custom", path: ["snapshot", "offer"], message: "Frozen offer must name this Execution's product identity." });
    }
  }
  if (execution.productFailure && (execution.productFailure.turnId !== execution.turnId || execution.productFailure.conversationId !== execution.conversationId)) {
    context.addIssue({ code: "custom", path: ["productFailure"], message: "Product failure must belong to this exact Turn." });
  }

});
export type PrivateAgentChatExecutionSnapshot = z.infer<typeof PrivateAgentChatExecutionSnapshotSchema>;
export type PrivateAgentChatExecutionRecord = z.infer<typeof PrivateAgentChatExecutionRecordSchema>;

/** Internal routing identity, derived from one persisted Execution (never from the UI Turn). */
export const PrivateAgentChatExecutionTargetSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema,
  turnId: PrivateAgentChatOpaqueIdSchema,
  contextEpoch: PrivateAgentChatContextEpochSchema,
  taskId: PrivateAgentChatOpaqueIdSchema,
  generation: z.number().int().positive(),
  snapshot: PrivateAgentChatExecutionSnapshotSchema,
  messageContext: z.object({ destinationBinding: PrivateAgentChatOpaqueIdSchema, freshnessCursor: PrivateAgentChatOpaqueIdSchema }).strict()
}).strict().superRefine((target, context) => {
  if (target.snapshot.sdkOfferType !== "task.offer_prepared")
    context.addIssue({code:"custom",message:"Historical fresh execution is audit-only."});
  if (target.messageContext.destinationBinding !== target.conversationId || target.messageContext.freshnessCursor !== target.turnId
    || (target.snapshot.origin === "frozen_dispatch_v3" && (target.snapshot.offer.turnId !== target.turnId || target.snapshot.offer.conversationId !== target.conversationId || target.snapshot.offer.contextEpoch !== target.contextEpoch))) {
    context.addIssue({ code: "custom", message: "Execution target must retain its exact product binding." });
  }
});
export type PrivateAgentChatExecutionTarget = z.infer<typeof PrivateAgentChatExecutionTargetSchema>;

/** Validate transport evidence against the persisted execution, without authoring a product reply. */
export function privateAgentChatSdkMessageMatchesExecution(receipt: PrivateAgentChatSdkMessageReceipt, execution: PrivateAgentChatExecutionTarget): boolean {
  const { payload } = PrivateAgentChatSdkMessageReceiptSchema.parse(receipt);
  const frozen = execution.snapshot.offer;
  return payload.agentRef.agentId === frozen.binding.agentRef.agentId
    && payload.agentRef.profileRevision === frozen.binding.agentRef.profileRevision
    && payload.contract === PrivateAgentChatMessageContract;
}


/** Control -> API: identities come only from verified task claims; assertion stays outside this payload. */
export const PrivateAgentToolInvokeRequestSchema = z
  .object({
    tenantRef: z.string().regex(/^[0-9a-f]{32}$/u),
    deviceId: PersonalAgentDeviceIdSchema,
    taskId: PrivateAgentChatOpaqueIdSchema,
    agentRef: AgentRefSchema,
    requestId: z.uuid(),
    toolset: PrivateAgentToolsetIdSchema,
    tool: z.string().min(1).max(120).regex(/^[a-z][a-z0-9_]*$/u),
    input: z.unknown()
  })
  .strict();
/** Domain key and canonical semantic fingerprint; assertion/jti are deliberately excluded. */
/** Owner-frozen S0 duplicate wait bound; not an effect execution deadline. */
export const PrivateAgentProposalDuplicateWaitMs = 5000;

export const PrivateAgentToolCallKeySchema = z.object({
  taskId: PrivateAgentChatOpaqueIdSchema,
  toolset: PrivateAgentToolsetIdSchema,
  requestId: z.uuid(),
}).strict();
export type PrivateAgentToolCallKey = z.infer<typeof PrivateAgentToolCallKeySchema>;
export const PrivateAgentToolCallRecoveryCursorSchema = z.object({
  conversationId: PrivateAgentChatOpaqueIdSchema,
  transcriptRevision: z.number().int().nonnegative().safe(),
  after: PrivateAgentToolCallKeySchema,
}).strict();
export type PrivateAgentToolCallRecoveryCursor = z.infer<typeof PrivateAgentToolCallRecoveryCursorSchema>;

function canonicalToolJson(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalToolJson).join(",")}]`;
  if (typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype) {
    return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonicalToolJson((value as Record<string, unknown>)[key])}`).join(",")}}`;
  }
  throw new Error("Tool semantic input must be JSON.");
}
async function toolSha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}
/** v1 = pct1_ + lowercase SHA256 of JSON [taskId,toolset,requestId]; always 69 characters. */
export async function privateAgentToolClientMutationId(input: PrivateAgentToolCallKey): Promise<string> {
  const key = PrivateAgentToolCallKeySchema.parse(input);
  return `pct1_${await toolSha256(JSON.stringify([key.taskId, key.toolset, key.requestId]))}`;
}
export async function privateAgentToolRequestFingerprint(tool: string, input: unknown): Promise<string> {
  return toolSha256(canonicalToolJson({ tool, input }));
}

export const PrivateAgentToolInvokeResponseSchema = z
  .object({
    toolset: PrivateAgentToolsetIdSchema,
    tool: z.string().min(1).max(120).regex(/^[a-z][a-z0-9_]*$/u),
    result: z.unknown()
  })
  .strict();

/** Derived call recovery observations; these do not settle or replace an Execution. */
export const PrivateAgentToolCallObservationSchema = PrivateAgentToolCallKeySchema.extend({
  revision: PrivateAgentChatReplayCursorSchema,
  turnId: PrivateAgentChatOpaqueIdSchema,
  state: z.enum(["claimed", "settled", "unresolved"]),
  response: PrivateAgentToolInvokeResponseSchema.nullable(),
}).strict().superRefine((call, ctx) => {
  if ((call.state === "settled") !== (call.response !== null)
    || (call.response !== null && call.response.toolset !== call.toolset)) ctx.addIssue({ code: "custom", message: "Call result must match its settled logical key." });
});
export type PrivateAgentToolCallObservation = z.infer<typeof PrivateAgentToolCallObservationSchema>;

/** BYOK control -> API: one authenticated Agent-authored message publication. */
export const PrivateAgentChatMessageConsumeRequestSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentChatMessageConsumeSchemaVersion),
    tenantRef: z.string().regex(/^[0-9a-f]{32}$/u),
    deviceId: PersonalAgentDeviceIdSchema,
    taskId: PrivateAgentChatOpaqueIdSchema,
    context: AgentMessageServerContextSchema,
    payload: AgentMessagePublishPayloadSchema
  })
  .strict();

export const PrivateAgentChatMessageConsumeResponseSchema = z
  .object({
    outcome: z.enum(["accepted", "held", "refused"]),
    reasonCode: z.string().min(1).max(160).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u).optional()
  })
  .strict();

/**
 * BYOK control -> API: the one device activity observation a pending
 * reconciliation may carry. It is a display fact; liveness and terminal
 * authority stay with the reconcile status itself.
 */
export const PrivateAgentChatActivityObservationSchema = z
  .object({
    seq: PrivateAgentChatActivitySequenceSchema,
    kind: PrivateAgentChatActivityKindSchema,
    occurredAt: z.string().datetime()
  })
  .strict();

/** BYOK control -> API callback; the terminal document is the sole assistant-output seam. */
export const PrivateAgentTerminalReceiptSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentTerminalReceiptSchemaVersion),
    taskId: PrivateAgentChatOpaqueIdSchema,
    generation: z.number().int().positive(),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    terminal: PrivateAgentTerminalSchema,
    sdkTerminalReceipt: PrivateAgentChatSdkTerminalReceiptSchema,
    unclaimedAttempt: PrivateAgentChatSdkUnclaimedAttemptSchema.optional()
  })
  .strict();

export const PrivateAgentEventSchema = z.discriminatedUnion("type", [
  z.object({ schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
    type: z.literal("turn.budget_integrity_mismatch_observed"), eventId: PrivateAgentChatOpaqueIdSchema,
    conversationId: PrivateAgentChatOpaqueIdSchema, turnId: PrivateAgentChatOpaqueIdSchema,
    sequence: PrivateAgentChatEventSequenceSchema, contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema, rulingRevision: z.string().min(1).optional(),
    reason: z.enum(["stored_binding_missing", "request_digest_mismatch"]),
    occurredAt: z.string().datetime() }).strict(),
  z.object({ schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
    type: z.literal("turn.budget_bound_falsified"), eventId: PrivateAgentChatOpaqueIdSchema,
    conversationId: PrivateAgentChatOpaqueIdSchema, turnId: PrivateAgentChatOpaqueIdSchema,
    sequence: PrivateAgentChatEventSequenceSchema, contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema, rulingRevision: z.string().min(1),
    requestDigest: z.string().regex(/^[0-9a-f]{64}$/u),
    initialPromptTokens: z.number().int().positive(), bound: z.number().int().positive(),
    occurredAt: z.string().datetime() }).strict(),
  z.object({ schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
    type: z.enum(["turn.context_overflow_observed", "turn.usage_unavailable_observed"]),
    eventId: PrivateAgentChatOpaqueIdSchema, conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema, sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema, revision: PrivateAgentChatReplayCursorSchema,
    rulingRevision: z.string().min(1).optional(), occurredAt: z.string().datetime() }).strict(),
  z.object({ schemaVersion: z.literal(PrivateAgentEventSchemaVersion), type: z.literal("conversation.cleared"),
    eventId: PrivateAgentChatOpaqueIdSchema, conversationId: PrivateAgentChatOpaqueIdSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema, previousEpoch: PrivateAgentChatContextEpochSchema,
    sequence: PrivateAgentChatEventSequenceSchema, revision: PrivateAgentChatReplayCursorSchema, occurredAt: z.string().datetime() }).strict()
    .refine(event => event.contextEpoch === event.previousEpoch + 1, "Clear event advances exactly one epoch."),
  z.object({
    schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
    type: z.literal("turn.settled"),
    eventId: PrivateAgentChatOpaqueIdSchema,
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema,
    settlement: PrivateAgentTurnEndSettlementSchema,
    occurredAt: z.string().datetime()
  }).strict(),
  z.object({
    schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
    type: z.literal("turn.input"),
    eventId: PrivateAgentChatOpaqueIdSchema,
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema,
    status: z.enum(["queued", "canceled"]),
    occurredAt: z.string().datetime()
  }).strict(),
  z
    .object({
      schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
      type: z.literal("turn.status"),
      eventId: PrivateAgentChatOpaqueIdSchema,
      conversationId: PrivateAgentChatOpaqueIdSchema,
      turnId: PrivateAgentChatOpaqueIdSchema,
      sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema,
      status: PrivateAgentChatStatusEventValueSchema,
      binding: PrivateAgentChatExecutionBindingSchema,
        occurredAt: z.string().datetime()
    })
    .strict(),
  z
    .object({
      schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
      type: z.literal("turn.terminal"),
      eventId: PrivateAgentChatOpaqueIdSchema,
      conversationId: PrivateAgentChatOpaqueIdSchema,
      turnId: PrivateAgentChatOpaqueIdSchema,
      sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema,
      terminal: PrivateAgentTerminalSchema,
      occurredAt: z.string().datetime()
    })
    .strict(),
  /**
   * One coalesced observation of what the device is doing right now. It is a
   * display fact only: liveness and replay invalidation stay owned by
   * `turn.status` / `turn.terminal`. `kind` is the whole payload — the frozen
   * egress policy redacts every content field on the device — and
   * `activitySeq` is the device activity position it came from, so a repeated
   * poll writes no second event.
   */
  z
    .object({
      schemaVersion: z.literal(PrivateAgentEventSchemaVersion),
      type: z.literal("turn.progress"),
      eventId: PrivateAgentChatOpaqueIdSchema,
      conversationId: PrivateAgentChatOpaqueIdSchema,
      turnId: PrivateAgentChatOpaqueIdSchema,
      sequence: PrivateAgentChatEventSequenceSchema,
    contextEpoch: PrivateAgentChatContextEpochSchema,
    revision: PrivateAgentChatReplayCursorSchema,
      activitySeq: PrivateAgentChatActivitySequenceSchema,
      kind: PrivateAgentChatActivityKindSchema,
      occurredAt: z.string().datetime()
    })
    .strict()
]);

export const PrivateAgentEventReplayQuerySchema = z
  .object({
    conversationId: PrivateAgentChatOpaqueIdSchema,
    epoch: PrivateAgentChatContextEpochSchema,
    afterSequence: PrivateAgentChatReplayCursorSchema,
    limit: z.number().int().positive().max(PrivateAgentEventBatchMaximum).default(PrivateAgentEventBatchMaximum)
  })
  .strict();

export const PrivateAgentEventReplayResponseSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentEventReplayResponseSchemaVersion),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    epoch: PrivateAgentChatContextEpochSchema,
    afterSequence: PrivateAgentChatReplayCursorSchema,
    events: z.array(PrivateAgentEventSchema).max(PrivateAgentEventBatchMaximum),
    nextSequence: PrivateAgentChatReplayCursorSchema,
    hasMore: z.boolean()
  })
  .strict().superRefine((batch, context) => {
    let sequence = batch.afterSequence;
    for (const event of batch.events) {
      if (event.conversationId !== batch.conversationId || event.contextEpoch !== batch.epoch || event.sequence <= sequence)
        context.addIssue({ code: "custom", message: "Replay must preserve owned epoch and strictly increasing event sequence." });
      sequence = event.sequence;
    }
    if (batch.nextSequence < sequence || (batch.hasMore && batch.nextSequence <= batch.afterSequence))
      context.addIssue({ code: "custom", message: "Replay cursor must cover delivered and filtered events and make progress." });
  });

export const PrivateAgentReplayGapErrorSchema = z
  .object({
    schemaVersion: z.literal(PrivateAgentTerminalErrorSchemaVersion),
    outcome: z.literal("failed"),
    conversationId: PrivateAgentChatOpaqueIdSchema,
    turnId: PrivateAgentChatOpaqueIdSchema,
    code: z.literal("replay_gap"),
    message: z.string().min(1).max(PrivateAgentTerminalErrorMessageMaximum),
    retryable: z.literal(false),
    recordedAt: z.string().datetime()
  })
  .strict();

export type AgentResearchMode = z.infer<typeof AgentResearchModeSchema>;
export type AgentResearchAgentProfile = z.infer<typeof AgentResearchAgentProfileSchema>;
export type AgentResearchLocaleSeed = z.infer<typeof AgentResearchLocaleSeedSchema>;
export type AgentResearchMessagingChannel = z.infer<typeof AgentResearchMessagingChannelSchema>;
export type SaleskoAgentSettings = z.infer<typeof SaleskoAgentSettingsSchema>;
export type SaleskoAgentSettingsResponse = z.infer<typeof SaleskoAgentSettingsResponseSchema>;
export type AgentResearchEntitlementReason = z.infer<typeof AgentResearchEntitlementReasonSchema>;
export type AgentResearchEntitlement = z.infer<typeof AgentResearchEntitlementSchema>;
export type AgentResearchEntitlementResponse = z.infer<typeof AgentResearchEntitlementResponseSchema>;
export type AgentResearchJobStatus = z.infer<typeof AgentResearchJobStatusSchema>;
export type AgentResearchStopReason = z.infer<typeof AgentResearchStopReasonSchema>;
export type AgentResearchBudget = z.infer<typeof AgentResearchBudgetSchema>;
export type AgentResearchJobCreateRequest = z.input<typeof AgentResearchJobCreateRequestSchema>;
export type AgentResearchJob = z.infer<typeof AgentResearchJobSchema>;
export type AgentResearchEvidence = z.infer<typeof AgentResearchEvidenceSchema>;
export type AgentResearchResult = z.infer<typeof AgentResearchResultSchema>;
export type AgentResearchJobCreateResponse = z.infer<typeof AgentResearchJobCreateResponseSchema>;
export type AgentResearchJobReadResponse = z.infer<typeof AgentResearchJobReadResponseSchema>;
export type AgentResearchJobListResponse = z.infer<typeof AgentResearchJobListResponseSchema>;
export type AgentResearchJobCancelResponse = z.infer<typeof AgentResearchJobCancelResponseSchema>;
export type AgentResearchMaterializeChangeSetResponse = z.infer<typeof AgentResearchMaterializeChangeSetResponseSchema>;
export type AgentResearchToolId = z.infer<typeof AgentResearchToolIdSchema>;
export type AgentResearchContextResponse = z.infer<typeof AgentResearchContextResponseSchema>;
export type AgentResearchCallbackGrant = z.infer<typeof AgentResearchCallbackGrantSchema>;
export type AgentResearchFastclawToolPolicy = z.infer<typeof AgentResearchFastclawToolPolicySchema>;
export type AgentResearchDispatchParams = z.infer<typeof AgentResearchDispatchParamsSchema>;
export type AgentResearchUsageEvent = z.infer<typeof AgentResearchUsageEventSchema>;
export type AgentResearchUsageBatch = z.infer<typeof AgentResearchUsageBatchSchema>;
export type AgentResearchUsageRecordResponse = z.infer<typeof AgentResearchUsageRecordResponseSchema>;
export type AgentResearchRuntimePhase = z.infer<typeof AgentResearchRuntimePhaseSchema>;
export type AgentResearchRuntimeStatus = z.infer<typeof AgentResearchRuntimeStatusSchema>;
export type AgentResearchUsageSummary = z.infer<typeof AgentResearchUsageSummarySchema>;
export type PersonalAgentExecutionStatus = z.infer<typeof PersonalAgentExecutionStatusSchema>;
export type PersonalAgentExecutionKind = z.infer<typeof PersonalAgentExecutionKindSchema>;
export type PersonalAgentExecutionBudget = z.infer<typeof PersonalAgentExecutionBudgetSchema>;
export type PersonalAgentFeatureFlags = z.infer<typeof PersonalAgentFeatureFlagsSchema>;
export type PersonalAgentExecutionCreate = z.infer<typeof PersonalAgentExecutionCreateSchema>;
export type PersonalAgentExecutionRecord = z.infer<typeof PersonalAgentExecutionRecordSchema>;
export type PersonalAgentExecutionStep = z.infer<typeof PersonalAgentExecutionStepSchema>;
export type PersonalAgentRuntimeProviderId = z.infer<typeof PersonalAgentRuntimeProviderIdSchema>;
export type PersonalAgentSandboxProviderId = z.infer<typeof PersonalAgentSandboxProviderIdSchema>;
export type PersonalAgentWorkspaceProviderId = z.infer<typeof PersonalAgentWorkspaceProviderIdSchema>;
export type PersonalAgentUsageSinkId = z.infer<typeof PersonalAgentUsageSinkIdSchema>;
export type PersonalAgentErrorCode = z.infer<typeof PersonalAgentErrorCodeSchema>;
export type PersonalAgentLlmProvider = z.infer<typeof PersonalAgentLlmProviderSchema>;
export type PersonalAgentProviderCredentialSource = z.infer<typeof PersonalAgentProviderCredentialSourceSchema>;
export type PersonalAgentProviderState = z.infer<typeof PersonalAgentProviderStateSchema>;
export type PersonalAgentSandboxLifecycleState = z.infer<typeof PersonalAgentSandboxLifecycleStateSchema>;
export type PersonalAgentSandboxBinding = z.infer<typeof PersonalAgentSandboxBindingSchema>;
export type PersonalAgentWorkspaceBinding = z.infer<typeof PersonalAgentWorkspaceBindingSchema>;
export type PersonalAgentWorkspaceManifestEntry = z.infer<typeof PersonalAgentWorkspaceManifestEntrySchema>;
export type PersonalAgentWorkspaceRevision = z.infer<typeof PersonalAgentWorkspaceRevisionSchema>;
export type PersonalAgentWorkspaceArtifact = z.infer<typeof PersonalAgentWorkspaceArtifactSchema>;
export type PersonalAgentUsageOperation = z.infer<typeof PersonalAgentUsageOperationSchema>;
export type PersonalAgentUsageEvent = z.infer<typeof PersonalAgentUsageEventSchema>;
export type PersonalAgentResearchResult = z.infer<typeof PersonalAgentResearchResultSchema>;
export type PersonalAgentDeviceId = z.infer<typeof PersonalAgentDeviceIdSchema>;
export type PersonalAgentDevice = z.infer<typeof PersonalAgentDeviceSchema>;
export type PersonalAgentPairingCodeResponse = z.infer<typeof PersonalAgentPairingCodeResponseSchema>;
export type PersonalAgentDeviceListResponse = z.infer<typeof PersonalAgentDeviceListResponseSchema>;
export type PersonalAgentDeviceRevokeRequest = z.infer<typeof PersonalAgentDeviceRevokeRequestSchema>;
export type PersonalAgentDeviceRevokeResponse = z.infer<typeof PersonalAgentDeviceRevokeResponseSchema>;
export type PersonalAgentPresenceLevel = z.infer<typeof PersonalAgentPresenceLevelSchema>;
export type PersonalAgentPresenceRuntimeFact = z.infer<typeof PersonalAgentPresenceRuntimeFactSchema>;
export type PersonalAgentPresenceHint = z.infer<typeof PersonalAgentPresenceHintSchema>;
export type PersonalAgentPresenceHintListResponse = z.infer<typeof PersonalAgentPresenceHintListResponseSchema>;
export type PrivateAgentReadinessPresence = z.infer<typeof PrivateAgentReadinessPresenceSchema>;
export type PrivateAgentReadinessCompatibility = z.infer<typeof PrivateAgentReadinessCompatibilitySchema>;
export type PrivateAgentReadinessRuntime = z.infer<typeof PrivateAgentReadinessRuntimeSchema>;
export type PrivateAgentReadinessBinding = z.infer<typeof PrivateAgentReadinessBindingSchema>;
export type PrivateAgentReadinessChat = z.infer<typeof PrivateAgentReadinessChatSchema>;
export type PrivateAgentReadinessBlockedReason = z.infer<typeof PrivateAgentReadinessBlockedReasonSchema>;
export type PrivateAgentReadiness = z.infer<typeof PrivateAgentReadinessSchema>;
export type PrivateAgentReadinessResponse = z.infer<typeof PrivateAgentReadinessResponseSchema>;
export type PrivateAgentAgentId = z.infer<typeof PrivateAgentAgentIdSchema>;
export type PrivateAgentRuntimePreference = z.infer<typeof PrivateAgentRuntimePreferenceSchema>;
export type PrivateAgentPiModelId = z.infer<typeof PrivateAgentPiModelIdSchema>;
export type PrivateAgentProfile = z.infer<typeof PrivateAgentProfileSchema>;
export type PrivateAgentProfileWrite = z.infer<typeof PrivateAgentProfileWriteSchema>;
export type PrivateAgentProfileResponse = z.infer<typeof PrivateAgentProfileResponseSchema>;
export type PrivateAgentProfileRevision = z.infer<typeof PrivateAgentProfileRevisionSchema>;
export type PrivateAgentProfileRedactedProjection = z.infer<typeof PrivateAgentProfileRedactedProjectionSchema>;
export type PrivateAgentProfileProductProjection = z.infer<typeof PrivateAgentProfileProductProjectionSchema>;
export type PrivateAgentProfileProjectionDispatch = z.infer<typeof PrivateAgentProfileProjectionDispatchSchema>;
export type PrivateAgentProfileProjectionSyncStatus = z.infer<typeof PrivateAgentProfileProjectionSyncStatusSchema>;
export type PrivateAgentProfileProjectionSyncState = z.infer<typeof PrivateAgentProfileProjectionSyncStateSchema>;
export type PrivateAgentExecutionSnapshot = z.infer<typeof PrivateAgentExecutionSnapshotSchema>;
export type ByokAgentResearchOffer = z.infer<typeof ByokAgentResearchOfferSchema>;
export type PrivateAgentChatOpaqueId = z.infer<typeof PrivateAgentChatOpaqueIdSchema>;
export type PrivateAgentChatAgentRef = z.infer<typeof PrivateAgentChatAgentRefSchema>;
export type PrivateAgentChatExecutionBinding = z.infer<typeof PrivateAgentChatExecutionBindingSchema>;
export type PrivateAgentChatTurnExecution = z.infer<typeof PrivateAgentChatTurnExecutionSchema>;
export type PrivateAgentChatToolBinding = z.infer<typeof PrivateAgentChatToolBindingSchema>;
export type PrivateAgentToolInvokeRequest = z.infer<typeof PrivateAgentToolInvokeRequestSchema>;
export type PrivateAgentToolInvokeResponse = z.infer<typeof PrivateAgentToolInvokeResponseSchema>;
export type PrivateAgentConversationStatus = z.infer<typeof PrivateAgentConversationStatusSchema>;
export type PrivateAgentMessageRole = z.infer<typeof PrivateAgentMessageRoleSchema>;
export type PrivateAgentTurnStatus = z.infer<typeof PrivateAgentTurnStatusSchema>;
export type PrivateAgentTerminalOutcome = z.infer<typeof PrivateAgentTerminalOutcomeSchema>;
export type PrivateAgentTerminalErrorCode = z.infer<typeof PrivateAgentTerminalErrorCodeSchema>;
export type PrivateAgentMessage = z.infer<typeof PrivateAgentMessageSchema>;
export type PrivateAgentConversationSummary = z.infer<typeof PrivateAgentConversationSummarySchema>;
export type PrivateAgentConversation = z.infer<typeof PrivateAgentConversationSchema>;
export type PrivateAgentConversationResponse = z.infer<typeof PrivateAgentConversationResponseSchema>;
export type PrivateAgentTouchedEntityDirectory = z.infer<typeof PrivateAgentTouchedEntityDirectorySchema>;
export type PrivateAgentTouchedEntityDirectoryEntry = z.infer<typeof PrivateAgentTouchedEntityDirectoryEntrySchema>;
export type PrivateAgentTerminalAssistantMessage = z.infer<typeof PrivateAgentTerminalAssistantMessageSchema>;
export type PrivateAgentChatMessageReceipt = z.infer<typeof PrivateAgentChatMessageReceiptSchema>;
export type PrivateAgentTerminalResult = z.infer<typeof PrivateAgentTerminalResultSchema>;
export type PrivateAgentTerminalError = z.infer<typeof PrivateAgentTerminalErrorSchema>;
export type PrivateAgentTerminal = z.infer<typeof PrivateAgentTerminalSchema>;
export type PrivateAgentTurn = z.infer<typeof PrivateAgentTurnSchema>;
export type PrivateAgentInputSubmitRequest = z.infer<typeof PrivateAgentInputSubmitRequestSchema>;
export type PrivateAgentTurnResponse = z.infer<typeof PrivateAgentTurnResponseSchema>;
export type PrivateAgentTurnListResponse = z.infer<typeof PrivateAgentTurnListResponseSchema>;
export type PrivateAgentTurnOffer = z.infer<typeof PrivateAgentTurnOfferSchema>;
export type PrivateAgentChatMessageConsumeRequest = z.infer<typeof PrivateAgentChatMessageConsumeRequestSchema>;
export type PrivateAgentChatMessageConsumeResponse = z.infer<typeof PrivateAgentChatMessageConsumeResponseSchema>;
export type PrivateAgentTerminalReceipt = z.infer<typeof PrivateAgentTerminalReceiptSchema>;
export type PrivateAgentEvent = z.infer<typeof PrivateAgentEventSchema>;
export type PrivateAgentChatActivityKind = z.infer<typeof PrivateAgentChatActivityKindSchema>;
export type PrivateAgentChatActivityObservation = z.infer<typeof PrivateAgentChatActivityObservationSchema>;
export type PrivateAgentProgressEvent = Extract<PrivateAgentEvent, { type: "turn.progress" }>;
export type PrivateAgentEventReplayQuery = z.input<typeof PrivateAgentEventReplayQuerySchema>;
export type PrivateAgentEventReplayResponse = z.infer<typeof PrivateAgentEventReplayResponseSchema>;
export type PrivateAgentReplayGapError = z.infer<typeof PrivateAgentReplayGapErrorSchema>;

// --- Email connector (Phase 1: contracts + Gmail OAuth connect. Sync, LLM
// classification, and lead candidates land in later phases; their schemas
// are defined now so the wire contract is stable across phases.) ---
export const EmailProviderSchema = z.enum(["gmail", "microsoft"]);
export const EmailConnectionStatusSchema = z.enum(["connected", "revoked", "error"]);
export const EmailSyncStatusSchema = z.enum(["ok", "failed", "running"]);

// Public connection view — never includes the refresh token or any other
// credential material. The repository's internal row type carries those; see
// apps/api/src/email-connection-repository.ts.
export const EmailConnectionSchema = z.object({
  id: z.string().min(1),
  provider: EmailProviderSchema,
  providerAccountEmail: z.string().email().max(320),
  status: EmailConnectionStatusSchema,
  grantedScopes: z.string().min(1),
  lastSyncedAt: z.string().datetime().optional(),
  lastSyncStatus: EmailSyncStatusSchema.optional(),
  lastSyncError: z.string().max(2_000).optional(),
  connectedAt: z.string().datetime()
});

export const EmailConnectionStatusResponseSchema = z.object({
  tenantId: TenantIdSchema,
  connections: z.array(EmailConnectionSchema)
});

// Response for authorize-url (apps/api/src/connectors-email-routes.ts): the
// Google consent URL the web client top-level-redirects the browser to.
export const EmailConnectorAuthorizeUrlResponseSchema = z.object({
  url: z.string().url()
});

export const EmailLeadCandidateStatusSchema = z.enum(["pending", "confirmed", "dismissed"]);

// Shared field-length caps for a raw email message's untrusted metadata —
// reused by both the classify-request shape (EmailLeadClassifyMessageInputSchema)
// and the persisted candidate row (EmailLeadCandidateSchema) so the two never
// drift, and exported so apps/api/src/email-sync.ts can truncate Gmail's raw
// header/snippet values to the same limit before they ever reach either schema.
export const EmailLeadSenderEmailMaxLength = 320;
export const EmailLeadSenderNameMaxLength = 300;
export const EmailLeadSubjectMaxLength = 500;
export const EmailLeadSnippetMaxLength = 2_000;

// Shared by the persisted candidate row and the classify-endpoint result —
// both describe the same "what did we pull out of this message" shape.
export const EmailLeadExtractedSchema = z
  .object({
    personName: z.string().min(1).max(300).optional(),
    orgName: z.string().min(1).max(300).optional()
  })
  .partial()
  .default({});

export const EmailLeadCandidateSchema = z.object({
  id: z.string().min(1),
  connectionId: z.string().min(1),
  provider: EmailProviderSchema,
  providerMessageId: z.string().min(1),
  providerThreadId: z.string().min(1).optional(),
  senderEmail: z.string().email().max(EmailLeadSenderEmailMaxLength),
  senderName: z.string().min(1).max(EmailLeadSenderNameMaxLength).optional(),
  subject: z.string().max(EmailLeadSubjectMaxLength).optional(),
  snippet: z.string().max(EmailLeadSnippetMaxLength).optional(),
  internalDate: z.number().int().nonnegative().optional(),
  classificationConfidence: z.number().min(0).max(1),
  classificationReason: z.string().min(1).max(500),
  extracted: EmailLeadExtractedSchema,
  status: EmailLeadCandidateStatusSchema,
  confirmedChangeSetId: z.string().min(1).optional(),
  confirmedNodeId: z.string().min(1).optional(),
  createdAt: z.string().datetime()
});

export const EmailLeadCandidateListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  items: z.array(EmailLeadCandidateSchema),
  count: z.number().int().nonnegative()
});

export const EmailLeadClassifyMessageInputSchema = z.object({
  messageId: z.string().min(1),
  senderEmail: z.string().email().max(EmailLeadSenderEmailMaxLength),
  senderName: z.string().min(1).max(EmailLeadSenderNameMaxLength).optional(),
  subject: z.string().max(EmailLeadSubjectMaxLength).optional(),
  snippet: z.string().max(EmailLeadSnippetMaxLength).optional()
});

export const EmailLeadClassifyRequestSchema = z.object({
  messages: z.array(EmailLeadClassifyMessageInputSchema).min(1).max(25)
});

export const EmailLeadClassifyResultSchema = z.object({
  messageId: z.string().min(1),
  isLead: z.boolean(),
  confidence: z.number().min(0).max(1),
  reason: z.string().min(1).max(500),
  extracted: EmailLeadExtractedSchema
});

export const EmailLeadClassifyResponseSchema = z.object({
  results: z.array(EmailLeadClassifyResultSchema)
});

export const EmailLeadCandidateConfirmRequestSchema = z.object({
  frameRecordId: z.string().min(1),
  clientMutationId: z.string().min(1).max(128).optional()
});

// Response for confirming a candidate: the change set that materialized it is
// always created and applied in the same request (apps/api/src/connectors-email-routes.ts),
// so appliedFrameVersion mirrors GraphChangeSetRecordSchema's own optionality
// rather than being guaranteed — it is present once the apply actually lands.
export const EmailLeadCandidateConfirmResponseSchema = z.object({
  candidate: EmailLeadCandidateSchema,
  changeSetId: z.string().min(1),
  nodeId: z.string().min(1),
  appliedFrameVersion: z.number().int().positive().optional()
});

// Response for dismiss (apps/api/src/connectors-email-routes.ts): the
// candidate row after it flips to status="dismissed".
export const EmailLeadCandidateDismissResponseSchema = z.object({
  candidate: EmailLeadCandidateSchema
});

export const EmailReplyTemplateProviderSchema = z.literal("gmail");
export const EmailReplyTemplateNameMaxLength = 120;
export const EmailReplyTemplateDescriptionMaxLength = 600;
export const EmailReplyTemplateSubjectMaxLength = 300;
export const EmailReplyTemplateBodyMaxLength = 4_000;

export const EmailReplyTemplateSchema = z.object({
  id: z.string().min(1),
  provider: EmailReplyTemplateProviderSchema,
  name: z.string().min(1).max(EmailReplyTemplateNameMaxLength),
  description: z.string().max(EmailReplyTemplateDescriptionMaxLength).optional(),
  enabled: z.boolean(),
  requiresReview: z.boolean(),
  subject: z.string().min(1).max(EmailReplyTemplateSubjectMaxLength),
  body: z.string().min(1).max(EmailReplyTemplateBodyMaxLength),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const EmailReplyTemplateResponseSchema = z.object({
  tenantId: TenantIdSchema,
  template: EmailReplyTemplateSchema
});

export const EmailReplyTemplateListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  templates: z.array(EmailReplyTemplateSchema),
  count: z.number().int().nonnegative()
});

export const EmailReplyTemplateCreateRequestSchema = z.object({
  name: z.string().trim().min(1).max(EmailReplyTemplateNameMaxLength),
  description: z.string().trim().max(EmailReplyTemplateDescriptionMaxLength).optional(),
  enabled: z.boolean(),
  requiresReview: z.boolean(),
  subject: z.string().trim().min(1).max(EmailReplyTemplateSubjectMaxLength),
  body: z.string().trim().min(1).max(EmailReplyTemplateBodyMaxLength)
});

export const EmailReplyTemplateUpdateRequestSchema = z.object({
  name: z.string().trim().min(1).max(EmailReplyTemplateNameMaxLength),
  description: z.string().trim().max(EmailReplyTemplateDescriptionMaxLength).optional(),
  enabled: z.boolean(),
  requiresReview: z.boolean(),
  subject: z.string().trim().min(1).max(EmailReplyTemplateSubjectMaxLength),
  body: z.string().trim().min(1).max(EmailReplyTemplateBodyMaxLength)
});

export const EmailReplyTemplateSelectionMessageSchema = z.object({
  senderEmail: z.string().email().max(EmailLeadSenderEmailMaxLength),
  senderName: z.string().min(1).max(EmailLeadSenderNameMaxLength).optional(),
  subject: z.string().max(EmailLeadSubjectMaxLength).optional(),
  snippet: z.string().max(EmailLeadSnippetMaxLength).optional()
});

export const EmailReplyTemplateSelectionRequestSchema = z.object({
  message: EmailReplyTemplateSelectionMessageSchema,
  templates: z.array(EmailReplyTemplateSchema).min(1).max(25),
  userContext: z.string().max(2_000).optional()
});

export const EmailReplyTemplateSelectionResponseSchema = z.object({
  templateId: z.string().min(1),
  subject: z.string().min(1).max(EmailReplyTemplateSubjectMaxLength),
  body: z.string().min(1).max(EmailReplyTemplateBodyMaxLength),
  reason: z.string().min(1).max(500),
  confidence: z.number().min(0).max(1),
  requiresReview: z.boolean()
});

export const EmailReplyDraftCreateRequestSchema = z.object({
  userContext: z.string().trim().max(2_000).optional()
});

export const EmailReplyDraftSchema = z.object({
  id: z.string().min(1),
  messageId: z.string().min(1).optional(),
  threadId: z.string().min(1).optional()
});

export const EmailReplyDraftCreateResponseSchema = z.object({
  candidate: EmailLeadCandidateSchema,
  templateSelection: EmailReplyTemplateSelectionResponseSchema,
  draft: EmailReplyDraftSchema
});

export const EmailSyncTriggerResponseSchema = z.object({
  status: z.enum(["queued", "completed"]),
  connectionId: z.string().min(1),
  transport: z.enum(["cloudflare_queue", "inline"])
});

export const EmailSyncJobMessageSchema = z.object({
  kind: z.literal("email_sync"),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  connectionId: z.string().min(1),
  trigger: z.enum(["manual", "cron"]),
  requestedAt: z.string().datetime()
});

// Node-graph interop constants: the Current-view stage a confirmed email lead
// lands on, and the frame-node `data` key that stores it. Shared so apps/api
// (writes it when materializing a confirmed candidate) and apps/web (reads it
// to render the Current lane) never drift on the literal string.
export const LEAD_STAGE_CURRENT = "current" as const;
export const NODE_DATA_LEAD_STAGE_KEY = "leadStage" as const;

// Internal OAuth CSRF-state grant for the Gmail connect flow (apps/api/src/
// connectors-email-routes.ts + auth-tokens.ts). Never leaves apps/api — the
// browser only ever sees the opaque signed token string, never the payload.
export const EmailOAuthStateTokenPrefix = "salesko-email-oauth-state-v1" as const;
export const EmailOAuthStateAudience = "salesko-email-oauth-state" as const;
export const EmailOAuthStateGrantSchema = z.object({
  iss: z.string().min(1).default("salesko-api"),
  aud: z.literal(EmailOAuthStateAudience),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  provider: EmailProviderSchema,
  nonce: z.string().min(1).max(64),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export type EmailProvider = z.infer<typeof EmailProviderSchema>;
export type EmailConnectionStatus = z.infer<typeof EmailConnectionStatusSchema>;
export type EmailSyncStatus = z.infer<typeof EmailSyncStatusSchema>;
export type EmailConnection = z.infer<typeof EmailConnectionSchema>;
export type EmailConnectionStatusResponse = z.infer<typeof EmailConnectionStatusResponseSchema>;
export type EmailConnectorAuthorizeUrlResponse = z.infer<typeof EmailConnectorAuthorizeUrlResponseSchema>;
export type EmailLeadCandidateStatus = z.infer<typeof EmailLeadCandidateStatusSchema>;
export type EmailLeadExtracted = z.infer<typeof EmailLeadExtractedSchema>;
export type EmailLeadCandidate = z.infer<typeof EmailLeadCandidateSchema>;
export type EmailLeadCandidateListResponse = z.infer<typeof EmailLeadCandidateListResponseSchema>;
export type EmailLeadClassifyMessageInput = z.infer<typeof EmailLeadClassifyMessageInputSchema>;
export type EmailLeadClassifyRequest = z.infer<typeof EmailLeadClassifyRequestSchema>;
export type EmailLeadClassifyResult = z.infer<typeof EmailLeadClassifyResultSchema>;
export type EmailLeadClassifyResponse = z.infer<typeof EmailLeadClassifyResponseSchema>;
export type EmailLeadCandidateConfirmRequest = z.infer<typeof EmailLeadCandidateConfirmRequestSchema>;
export type EmailLeadCandidateConfirmResponse = z.infer<typeof EmailLeadCandidateConfirmResponseSchema>;
export type EmailLeadCandidateDismissResponse = z.infer<typeof EmailLeadCandidateDismissResponseSchema>;
export type EmailReplyTemplateProvider = z.infer<typeof EmailReplyTemplateProviderSchema>;
export type EmailReplyTemplate = z.infer<typeof EmailReplyTemplateSchema>;
export type EmailReplyTemplateResponse = z.infer<typeof EmailReplyTemplateResponseSchema>;
export type EmailReplyTemplateListResponse = z.infer<typeof EmailReplyTemplateListResponseSchema>;
export type EmailReplyTemplateCreateRequest = z.infer<typeof EmailReplyTemplateCreateRequestSchema>;
export type EmailReplyTemplateUpdateRequest = z.infer<typeof EmailReplyTemplateUpdateRequestSchema>;
export type EmailReplyTemplateSelectionMessage = z.infer<typeof EmailReplyTemplateSelectionMessageSchema>;
export type EmailReplyTemplateSelectionRequest = z.infer<typeof EmailReplyTemplateSelectionRequestSchema>;
export type EmailReplyTemplateSelectionResponse = z.infer<typeof EmailReplyTemplateSelectionResponseSchema>;
export type EmailReplyDraftCreateRequest = z.infer<typeof EmailReplyDraftCreateRequestSchema>;
export type EmailReplyDraft = z.infer<typeof EmailReplyDraftSchema>;
export type EmailReplyDraftCreateResponse = z.infer<typeof EmailReplyDraftCreateResponseSchema>;
export type EmailSyncTriggerResponse = z.infer<typeof EmailSyncTriggerResponseSchema>;
export type EmailSyncJobMessage = z.infer<typeof EmailSyncJobMessageSchema>;
export type EmailOAuthStateGrant = z.infer<typeof EmailOAuthStateGrantSchema>;

// --- Calendar connector (Phase 1: Google + Microsoft OAuth. Apple CalDAV is
// Phase 2; when it lands, add "apple" to CalendarProviderSchema plus the
// matching provider CHECK in 0053_calendar_connections.sql and a dedicated
// app-specific-password storage flow — see docs in the calendar plan.) ---
// Mirrors the email connector's contract shape (status / sync status / OAuth
// state grant / public-vs-internal boundary) so the two connectors share one
// mental model, but every type is distinct: calendar's provider set, sync
// cursor semantics (provider-native syncToken / delta link, not date-based),
// and event payload differ from email's.
export const CalendarProviderSchema = z.enum(["google", "microsoft"]);
export const CalendarConnectionStatusSchema = z.enum(["connected", "revoked", "error"]);
export const CalendarSyncStatusSchema = z.enum(["ok", "failed", "running"]);

// Internal connection row — the full at-rest shape written into
// salesko_calendar_connections (apps/api/src/calendar-connection-repository.ts,
// later slice). Carries the AES-GCM-encrypted OAuth refresh token material,
// so it must never be returned over the wire: the public schema below is the
// API boundary, and any field added here that is credential-like stays out of
// that public shape by construction (mirrors how email keeps its ciphertext
// columns out of EmailConnectionSchema).
export const CalendarConnectionInternalSchema = z.object({
  id: z.string().min(1),
  datasetScope: z.string().min(1),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  provider: CalendarProviderSchema,
  providerAccountEmail: z.string().email().max(320),
  status: CalendarConnectionStatusSchema,
  grantedScopes: z.string().min(1),
  // AES-GCM ciphertext + IV + key-version for the Google/Microsoft OAuth
  // refresh token (apps/api/src/calendar-token-crypto.ts). Bound to the row
  // via additionalData = `${tenantId}|${provider}|${providerAccountEmail}`,
  // using a *calendar-only* env keyring (SALESKO_CALENDAR_TOKEN_ENC_KEY) —
  // independent from email's keyring so the overlapping microsoft provider
  // AAD recipe can never make a ciphertext decryptable under the wrong
  // connector's key.
  refreshTokenCiphertext: z.string().min(1),
  refreshTokenIv: z.string().min(1),
  encKeyVersion: z.number().int().nonnegative(),
  // Provider-native incremental sync cursor. Google stores a syncToken
  // (https://developers.google.com/calendar/v3/sync); Microsoft Graph stores a
  // deltaLink (https://graph.microsoft.com/v1.0/me/calendarView/delta). Either
  // may be null on a brand-new connection before the first full sync lands;
  // sync advances them atomically with its batch commit (fail-closed: a
  // failed sync never moves the cursor).
  lastSyncToken: z.string().nullable().optional(),
  lastDeltaLink: z.string().nullable().optional(),
  lastSyncedAt: z.string().datetime().nullable().optional(),
  lastSyncStatus: CalendarSyncStatusSchema.nullable().optional(),
  lastSyncError: z.string().max(2_000).nullable().optional(),
  connectedAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Public connection view — never includes refresh token ciphertext/IV or the
// enc_key_version. Mirrors emailConnectionFromInternal's public boundary.
// Apps/web's connector registry consumes this shape directly.
export const CalendarConnectionPublicSchema = z.object({
  id: z.string().min(1),
  provider: CalendarProviderSchema,
  providerAccountEmail: z.string().email().max(320),
  status: CalendarConnectionStatusSchema,
  grantedScopes: z.string().min(1),
  lastSyncedAt: z.string().datetime().optional(),
  lastSyncStatus: CalendarSyncStatusSchema.optional(),
  lastSyncError: z.string().max(2_000).optional(),
  connectedAt: z.string().datetime()
});

export const CalendarConnectionStatusResponseSchema = z.object({
  tenantId: TenantIdSchema,
  connections: z.array(CalendarConnectionPublicSchema)
});

// Response for authorize-url (apps/api/src/connectors-calendar-routes.ts,
// later slice): the provider consent URL the web client top-level-redirects
// the browser to.
export const CalendarConnectorAuthorizeUrlResponseSchema = z.object({
  url: z.string().url()
});

export const CalendarEventStatusSchema = z.enum(["confirmed", "tentative", "cancelled"]);
export const CalendarEventDismissStatusSchema = z.enum(["active", "dismissed"]);

// Attendee response status values are the union of Google Calendar
// ("accepted" | "tentative" | "declined" | "needsAction") and Microsoft Graph
// ("accepted" | "tentativelyAccepted" | "declined" | "notResponded" |
// "organizer") normalized to Google's vocabulary — Microsoft's
// "tentativelyAccepted" maps to "tentative", and "notResponded"/"organizer"
// map to "needsAction". Provider implementations do the normalization before
// persisting so the wire contract has one vocabulary.
export const CalendarEventAttendeeResponseStatusSchema = z.enum([
  "needsAction",
  "declined",
  "tentative",
  "accepted"
]);

export const CalendarEventAttendeeSchema = z.object({
  email: z.string().email().max(320),
  name: z.string().min(1).max(300).optional(),
  responseStatus: CalendarEventAttendeeResponseStatusSchema.optional(),
  optional: z.boolean().optional()
});

export const CalendarEventOrganizerSchema = z.object({
  email: z.string().email().max(320),
  name: z.string().min(1).max(300).optional()
});

// Internal calendar event row — the full shape persisted into
// salesko_calendar_events (the provider source-of-truth table, including solo
// events and dismissed events). This table is the MCP read path's source; the
// product graph view is a downstream filtered projection that lives only in
// the graph frame, not in this table.
export const CalendarEventInternalSchema = z.object({
  id: z.string().min(1),
  datasetScope: z.string().min(1),
  tenantId: TenantIdSchema,
  connectionId: z.string().min(1),
  provider: CalendarProviderSchema,
  providerEventId: z.string().min(1),
  // iCal UID is the cross-provider stable identity for a recurring series
  // (Google's iCalUID; Microsoft's iCalUId property on Graph event). Persisted
  // separately from providerEventId because the same series yields many
  // instance event ids but one iCalUid — used for recurring-event grouping.
  iCalUid: z.string().min(1),
  calendarId: z.string().min(1),
  summary: z.string().max(500).nullable().optional(),
  location: z.string().max(1_000).nullable().optional(),
  startAt: z.string().datetime().nullable().optional(),
  endAt: z.string().datetime().nullable().optional(),
  isRecurring: z.boolean(),
  status: CalendarEventStatusSchema,
  attendees: z.array(CalendarEventAttendeeSchema).default([]),
  organizer: CalendarEventOrganizerSchema.nullable().optional(),
  // Provider-native event payload kept verbatim for debugging and for future
  // field extraction without a re-sync. Not surfaced to the public event view
  // (size + untrusted shape); the MCP/web read paths project cleaned fields.
  rawPayload: z.record(z.string(), z.unknown()),
  // Linkage back to the graph node materialized from this event (null until
  // the sync batch lands the upsert_node for it). dismiss flips
  // data.calendarDismissed on this node rather than deleting it — the graph
  // mutation surface has no delete_node.
  graphNodeId: z.string().min(1).nullable().optional(),
  graphSyncedAt: z.string().datetime().nullable().optional(),
  dismissStatus: CalendarEventDismissStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Public calendar event view — cleaned projection of the internal row for
// API/MCP/web reads. Drops the internal DB metadata (datasetScope, tenantId,
// connectionId, rawPayload, createdAt, updatedAt). Kept as a distinct schema
// (not CalendarEventInternalSchema.pick) so additions to the internal row's
// DB metadata don't silently leak into the wire contract.
export const CalendarEventPublicSchema = z.object({
  id: z.string().min(1),
  provider: CalendarProviderSchema,
  providerEventId: z.string().min(1),
  iCalUid: z.string().min(1),
  calendarId: z.string().min(1),
  summary: z.string().max(500).nullable().optional(),
  location: z.string().max(1_000).nullable().optional(),
  startAt: z.string().datetime().nullable().optional(),
  endAt: z.string().datetime().nullable().optional(),
  isRecurring: z.boolean(),
  status: CalendarEventStatusSchema,
  attendees: z.array(CalendarEventAttendeeSchema).default([]),
  organizer: CalendarEventOrganizerSchema.nullable().optional(),
  graphNodeId: z.string().min(1).nullable().optional(),
  graphSyncedAt: z.string().datetime().nullable().optional(),
  dismissStatus: CalendarEventDismissStatusSchema
});

export const CalendarEventListResponseSchema = z.object({
  tenantId: TenantIdSchema,
  items: z.array(CalendarEventPublicSchema),
  count: z.number().int().nonnegative()
});

export const CalendarEventDismissRequestSchema = z.object({
  clientMutationId: z.string().min(1).max(128).optional()
});

// Response for dismiss: the event row after it flips to dismissStatus=
// "dismissed" and the linked graph node (if any) has data.calendarDismissed
// set to true. graphNodeMarked is false if there was no graph node yet
// (e.g. solo event that never projected, or dismissed mid-first-sync).
export const CalendarEventDismissResponseSchema = z.object({
  event: CalendarEventPublicSchema,
  graphNodeMarked: z.boolean()
});

// Node-graph interop constants: the lead-source tag a calendar-synced event
// node carries on `data.leadSource`, and the frame-node `data` key the dismiss
// path flips to hide a calendar-sourced event from the product graph view.
// Shared with the same convention as LEAD_STAGE_CURRENT /
// NODE_DATA_LEAD_STAGE_KEY so apps/api (writes) and apps/web (reads) never
// drift on the literal string.
export const LEAD_SOURCE_CALENDAR = "calendar_connector" as const;
export const NODE_DATA_CALENDAR_DISMISSED_KEY = "calendarDismissed" as const;

// Internal OAuth CSRF-state grant for the Google/Microsoft Calendar connect
// flow (apps/api/src/connectors-calendar-routes.ts + auth-tokens.ts, later
// slice). Never leaves apps/api — the browser only ever sees the opaque
// signed token string, never the payload. Same shape as the email state grant
// but a distinct audience + prefix so a calendar grant can't be replayed
// against the email callback or vice versa.
export const CalendarOAuthStateTokenPrefix = "salesko-calendar-oauth-state-v1" as const;
export const CalendarOAuthStateAudience = "salesko-calendar-oauth-state" as const;
export const CalendarOAuthStateGrantSchema = z.object({
  iss: z.string().min(1).default("salesko-api"),
  aud: z.literal(CalendarOAuthStateAudience),
  tenantId: TenantIdSchema,
  userId: z.string().min(1).max(128),
  provider: CalendarProviderSchema,
  nonce: z.string().min(1).max(64),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive()
});

export type CalendarProvider = z.infer<typeof CalendarProviderSchema>;
export type CalendarConnectionStatus = z.infer<typeof CalendarConnectionStatusSchema>;
export type CalendarSyncStatus = z.infer<typeof CalendarSyncStatusSchema>;
export type CalendarConnectionInternal = z.infer<typeof CalendarConnectionInternalSchema>;
export type CalendarConnectionPublic = z.infer<typeof CalendarConnectionPublicSchema>;
export type CalendarConnectionStatusResponse = z.infer<typeof CalendarConnectionStatusResponseSchema>;
export type CalendarConnectorAuthorizeUrlResponse = z.infer<typeof CalendarConnectorAuthorizeUrlResponseSchema>;
export type CalendarEventStatus = z.infer<typeof CalendarEventStatusSchema>;
export type CalendarEventDismissStatus = z.infer<typeof CalendarEventDismissStatusSchema>;
export type CalendarEventAttendeeResponseStatus = z.infer<typeof CalendarEventAttendeeResponseStatusSchema>;
export type CalendarEventAttendee = z.infer<typeof CalendarEventAttendeeSchema>;
export type CalendarEventOrganizer = z.infer<typeof CalendarEventOrganizerSchema>;
export type CalendarEventInternal = z.infer<typeof CalendarEventInternalSchema>;
export type CalendarEventPublic = z.infer<typeof CalendarEventPublicSchema>;
export type CalendarEventListResponse = z.infer<typeof CalendarEventListResponseSchema>;
export type CalendarEventDismissRequest = z.infer<typeof CalendarEventDismissRequestSchema>;
export type CalendarEventDismissResponse = z.infer<typeof CalendarEventDismissResponseSchema>;
export type CalendarOAuthStateGrant = z.infer<typeof CalendarOAuthStateGrantSchema>;

// --- Schedule read agenda (salesko.schedule.v1) ---
// Slice 1 read-only grouped agenda: Calendar events keep provider time
// authority; opportunity follow-ups and relationship reminders are explicit
// unscheduled/stale groups, never merged into one fabricated timeline (see
// docs/researches/20260710-schedule-use-side-calendar-deps-decision.md and
// plans/plan-20260710-0350-schedule-read-agenda.md). No default window,
// legacy alias, or lenient date parsing: "from"/"to" are required ISO
// datetimes that must carry a timezone offset or "Z".

// Source caps shared by the API route and typed client so truncation and
// pagination stay in lockstep with the contract, not re-declared per caller.
export const ScheduleCalendarEventCap = 100 as const;
export const ScheduleOpportunityFollowUpCap = 50 as const;
export const ScheduleRelationshipReminderCap = 50 as const;
export const ScheduleMaxWindowDays = 92 as const;

const ScheduleRangeBoundsSchema = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true })
});

function scheduleRangeIsOrdered(range: { from: string; to: string }): boolean {
  return Date.parse(range.from) < Date.parse(range.to);
}

function scheduleRangeWithinMaxWindow(range: { from: string; to: string }): boolean {
  const spanMs = Date.parse(range.to) - Date.parse(range.from);
  return spanMs <= ScheduleMaxWindowDays * 24 * 60 * 60 * 1000;
}

// "from"/"to" share identical constraint semantics in the query and in the
// response's echoed range, so both are built from this one factory instead
// of duplicating the ordering/window refinements.
function scheduleRangeSchema(context: "query" | "response range") {
  return ScheduleRangeBoundsSchema.refine(scheduleRangeIsOrdered, {
    message: `Schedule ${context} "from" must be strictly before "to".`
  }).refine(scheduleRangeWithinMaxWindow, {
    message: `Schedule ${context} window must not exceed ${ScheduleMaxWindowDays} days.`
  });
}

export const ScheduleQuerySchema = scheduleRangeSchema("query");

// `count` is the number of items in this response, never a database total —
// enforced here so a caller cannot silently drift the two apart.
function scheduleGroupCountMatchesItems(group: { items: unknown[]; count: number }): boolean {
  return group.count === group.items.length;
}

export const ScheduleCalendarEventGroupSchema = z
  .object({
    items: z.array(CalendarEventPublicSchema).max(ScheduleCalendarEventCap),
    count: z.number().int().nonnegative(),
    truncated: z.boolean()
  })
  .refine(scheduleGroupCountMatchesItems, {
    message: "Schedule calendar event group count must equal items.length."
  });

export const ScheduleOpportunityFollowUpItemSchema = z.object({
  opportunity: GraphOpportunityFeedItemSchema,
  // The time the tenant requested a follow-up, not a due date (plan P3
  // Decision Rationale). Consumers render this as "Unscheduled follow-ups"
  // and must not infer a due time from it.
  followUpRequestedAt: z.string().datetime()
});

export const ScheduleOpportunityFollowUpGroupSchema = z
  .object({
    items: z.array(ScheduleOpportunityFollowUpItemSchema).max(ScheduleOpportunityFollowUpCap),
    count: z.number().int().nonnegative(),
    truncated: z.boolean()
  })
  .refine(scheduleGroupCountMatchesItems, {
    message: "Schedule opportunity follow-up group count must equal items.length."
  });

export const ScheduleRelationshipReminderGroupSchema = z
  .object({
    asOf: z.string().datetime(),
    staleAfterDays: z.number().int().positive(),
    items: z.array(GraphRelationshipReminderSchema).max(ScheduleRelationshipReminderCap),
    count: z.number().int().nonnegative(),
    truncated: z.boolean()
  })
  .refine(scheduleGroupCountMatchesItems, {
    message: "Schedule relationship reminder group count must equal items.length."
  });

// Fixed grouped-agenda envelope: Calendar keeps provider time authority,
// follow-ups and reminders are explicit unscheduled/stale groups. `range`
// only bounds `calendarEvents` — follow-ups sort by followUpRequestedAt DESC
// and reminders keep their existing score/staleness order (plan Public
// contract). No partial-success shape exists; a failing source fails the
// whole response at the route layer, not here.
export const SaleskoScheduleResponseSchema = z.object({
  schemaVersion: z.literal("salesko.schedule.v1"),
  generatedAt: z.string().datetime(),
  range: scheduleRangeSchema("response range"),
  calendarEvents: ScheduleCalendarEventGroupSchema,
  opportunityFollowUps: ScheduleOpportunityFollowUpGroupSchema,
  relationshipReminders: ScheduleRelationshipReminderGroupSchema
});

export type ScheduleQuery = z.infer<typeof ScheduleQuerySchema>;
export type ScheduleCalendarEventGroup = z.infer<typeof ScheduleCalendarEventGroupSchema>;
export type ScheduleOpportunityFollowUpItem = z.infer<typeof ScheduleOpportunityFollowUpItemSchema>;
export type ScheduleOpportunityFollowUpGroup = z.infer<typeof ScheduleOpportunityFollowUpGroupSchema>;
export type ScheduleRelationshipReminderGroup = z.infer<typeof ScheduleRelationshipReminderGroupSchema>;
export type SaleskoScheduleResponse = z.infer<typeof SaleskoScheduleResponseSchema>;

export * from "./meeting-preparation";
export * from "./local-agent-release";

export type PrivateAgentPlacementRevision = z.infer<typeof PrivateAgentPlacementRevisionSchema>;
export type PrivateAgentPlacement = z.infer<typeof PrivateAgentPlacementSchema>;
export type PrivateAgentPlacementWrite = z.infer<typeof PrivateAgentPlacementWriteSchema>;
export type PrivateAgentPlacementUpdateRequest = z.infer<typeof PrivateAgentPlacementUpdateRequestSchema>;
export type PrivateAgent = z.infer<typeof PrivateAgentSchema>;
export type PrivateAgentCreateRequest = z.infer<typeof PrivateAgentCreateRequestSchema>;

/** Deterministic read of the one accepted message; no new body or identity is authored. */
export function privateAgentChatAcceptedMessagePayload(turn: PrivateAgentTurn) {
  const terminal = turn.terminal;
  if (terminal?.outcome !== "succeeded") return undefined;
  return AgentMessagePublishPayloadSchema.parse({
    agentRef: terminal.binding.agentRef, sessionRef: terminal.sessionRef,
    ...terminal.messageReceipt, body: terminal.assistantMessage.content
  });
}
export * from "./pi-accounting-ruling";
// Pure zod + protocol imports only: no `node:` reachability, so the barrel is safe here
// (contrast ./private-agent-chat-verify-source, which reaches node:crypto and has its own subpath).
export * from "./private-agent-preparation-facts";
export * from "./private-agent-preparation-limits";
// Two numbers + their doc comments; no `node:` reachability, so the barrel is safe.
export * from "./private-agent-preparation-wire";
export * from "./private-agent-chat-preparation-transport";
